import { WEATHER_API_KEY, USER_API_BASE_URL, NEWS_API_KEY, COMPANIESHOUSE_API_KEY, SESSION_API, getApiBaseUrl, updateApiBaseUrls, AI_API_URL } from './constant';
import { LINKEDIN_OAUTH_SCOPE, LINKEDIN_PRODUCTION_WEB_REDIRECT } from '@utils/linkedInOAuth';
import { AVATAR_UPDATED, GET_ERRORS, USER_LOADING, NEW_USER_ADDED, DATA_QUERY, LOGGED_OUT, SUCCESS, HISTORY_RETRIEVED, WEATHER_RETRIEVED, DOWNLOAD_AVATAR, UPDATE_GOOGLE_MAIL_TOKEN, SHOW_AUTH_MODAL, FAILED, REQUEST, REGISTER_SUCCESS, DEFAULT_AVATAR, NEWS_UPDATED, HIDE_LOCK_MODAL, SHOW_REGISTER_MODAL, GOOGLE_AUTH_FAILED, MAIL_RETRIEVED, UPDATE_MAIL_COUNT, UPDATE_ACHIEVEMENTS, BACK_TO_LOGIN, SWITCH_COMPANY_FAILED, SWITCH_COMPANY_SUCCESSFUL, HIDE_PASSWORD_RESET_MODAL, UPDATE_PRODUCTS, HIDE_LOADING_MODAL, SHOW_LOADING_MODAL, PENDING_REFRESH, SET_STICKY_NOTES, SHOW_OTP, UPDATED_INVITATIONS, UPDATED_MESSAGES, UPDATED_CALENDAR_ENTRIES, HIDE_ALL_MODALS, HANDLE_FAVOURITE, REFRESH, UPDATE_MAIL_ACCOUNT, CLEAR_MAIL_AUTH, UPDATE_GOOGLE_ACCOUNT, REMOVE_INTEGRATION, PAYMENT_REQUIRED, UPDATE_USER, UPDATE_ACTIVE_COMPANY_MAILBOX_TOKEN } from "@reduxLocal/actions/types";
import { store } from '@reduxLocal/persistState';
import axios from 'axios';
import Constants from 'expo-constants';
import { GoogleCalTypes } from '../src/components/Global/types';
import { Alert, Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import { File as ExpoFile, Paths } from 'expo-file-system';
import * as Linking from 'expo-linking';
import { getStripeConnectAccountLinkReturnUrl } from '@navigation/screens/MidLevel/IntegrationsScreen/utils/integrationOAuthRedirect';
import { saveLog } from '@reduxLocal/persistState';
import { addToQueue, getQueue, clearQueue, removeProcessedRequest } from './utils/requestQueue';
import { monitorNetworkStatus, refreshNetworkStatus, getNetworkStatus } from './utils/networkManager';
import { replayOfflineQueue } from './utils/syncCoordinator';
import { buildSessionScopeId } from './utils/sessionScope';
import { recordOnlineContextAccess } from './utils/recentOnlineContextAccess';
import { addAbortController, removeAbortController } from '@utils/abortController';
import { serializeForReduxDetails } from '@utils/serializeForRedux';
import paymentDispatcher from '@utils/paymentDispatcher';
import { reportApiError, reportApiSuccess } from '@utils/apiHealth';
import { userDataIndicatesPaidSubscription } from '@utils/subscriptionBillingGuard';
import { apiInterceptor } from './utils/optimizedInterceptor';
import { normalizeGoogleAccessToken, resolveGoogleMailAuth } from '@utils/googleMailAuth';
import { resolveCompanyId } from '@utils/companyMailboxSession';
//TODO create decentralised redux dispatches for all types
//TODO remove named exports or remove default exports!

let isOnline = true;
let cancelTokenSource = axios.CancelToken.source();
let modalTimeout: NodeJS.Timeout | null = null;

// Centralized error handler to prevent modal conflicts
const dispatchErrorModal = (errorMessage: string, details?: any) => {
	console.log('🚨 dispatchErrorModal: Dispatching SHOW_AUTH_MODAL', {
		errorMessage,
		timestamp: Date.now(),
	});
	const safeDetails = serializeForReduxDetails(details);
	dispatch({
		type: SHOW_AUTH_MODAL,
		data: {
			content: errorMessage,
			type: 'error',
			...(safeDetails !== undefined ? { details: safeDetails } : {}),
		},
	});
};

// Centralized success handler
const dispatchSuccessModal = (message: string) => {
	dispatch({ 
		type: SHOW_AUTH_MODAL, 
		data: { 
			content: message, 
			type: 'success' 
		} 
	});
};

// Clear any pending modal operations
const clearModalTimeout = () => {
	if (modalTimeout) {
		clearTimeout(modalTimeout);
		modalTimeout = null;
	}
};


// Replay queued requests via sync coordinator (preflight conflict checks + held queue)
const replayQueue = () => replayOfflineQueue();


export const cancelAllRequests = async () => {
	// Clear any pending modal timeouts when canceling requests
	clearModalTimeout();
	return new Promise((resolve) => {
		cancelTokenSource.cancel('All requests cancelled');
		cancelTokenSource = axios.CancelToken.source();
		resolve('All requests cancelled');
	});
}

// Monitor network status
monitorNetworkStatus((status) => {
	isOnline = status;
	if (isOnline) {
		replayQueue();
	}
});

// Prime network state once at startup so interceptors can rely on it across web/native.
refreshNetworkStatus().then((status) => {
	isOnline = status;
	if (status) {
		replayQueue();
	}
});

export const isApiOnline = () => getNetworkStatus();

function dispatch(action) {
	store.dispatch(action)
}

// Initialize API instances with dynamic URLs
const initializeApiInstances = async () => {
	const baseUrl = await getApiBaseUrl();
	const sessionUrl = baseUrl.replace('/api', '/api/session');

	const sessionApi = axios.create({
		withCredentials: true,
		baseURL: sessionUrl,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	const api = axios.create({
		withCredentials: true,
		baseURL: baseUrl,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	const noCreds = axios.create({
		baseURL: baseUrl,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	const publicApi = axios.create({
		baseURL: baseUrl,
		withCredentials: false,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	// Register instances globally for dynamic updates
	if (typeof window !== 'undefined') {
		(window as any).apiInstances = {
			sessionApi,
			api,
			noCreds,
			publicApi
		};
	}

	return { sessionApi, api, noCreds, publicApi };
};

// Initialize instances
let apiInstances: any = null;
const getApiInstances = async () => {
	if (!apiInstances) {
		apiInstances = await initializeApiInstances();
	}
	return apiInstances;
};

// Export instances (will be initialized on first use)
export const sessionApi = axios.create({
	withCredentials: true,
	baseURL: SESSION_API,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const api = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const noCreds = axios.create({
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const publicApi = axios.create({
	baseURL: USER_API_BASE_URL,
	withCredentials: false,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

// Universal response interceptor for handling nested data and billing warnings
const createUniversalResponseInterceptor = () => {
	return (response: any) => {
		const requestKey = `${response.config.method}-${response.config.url}`;
		removeAbortController(requestKey);

		// Handle billing warnings globally (skip if Redux shows an active paid plan — avoids false positives, e.g. target-user context)
		if (response.data?.billingWarning) {
			const state = store.getState();
			const userData = state.promiseData?.data?.userData;
			const companyId = state.app?.selectedProfile?.id;
			// Company workspaces are always paid — don't suppress on personal paid status
			if (companyId || !userDataIndicatesPaidSubscription(userData)) {
				const routeName = response.config.url.split('/')[1];
				paymentDispatcher(routeName, '', response.data.message);
			}
		}

		// Handle nested data structure globally (unwrap { data: { ...fields } }, not raw arrays).
		// Arrays must be skipped: `typeof [] === 'object'` and spreading an array onto the payload
		// adds numeric keys and can break consumers expecting `res.data.data` to stay an array
		// (e.g. GET /api/get-products-list → { success, data: Product[] }).
		const nested = response.data?.data;
		if (
			nested != null &&
			typeof nested === 'object' &&
			!Array.isArray(nested)
		) {
			response.data = {
				...response.data,
				...nested,
			};
		}

		// Show success modal for monitored requests (if configured)
		// Check if this was a monitored request that should show modals
		const isMonitoredRequest = response.config?._monitorType === 'monitored' ||
			response.config?._monitorType === 'form';

		if (isMonitoredRequest) {
			// Show success message if configured
			if (response.config?._shouldShowSuccessModal) {
				const successMessage = response.data?.message ||
					response.data?.success ||
					'Operation completed successfully';

				dispatchSuccessModal(successMessage);
			} else {
				// Hide loading modal if no success modal should be shown
				dispatch({ type: HIDE_LOADING_MODAL });
			}
		}

		return response;
	};
};

// Universal error interceptor
const createUniversalErrorInterceptor = () => {
	return (error: any) => {
		try {
			const { maybeHandleSessionExpiredFromAxiosError } = require('@utils/handleSessionExpired');
			if (maybeHandleSessionExpiredFromAxiosError(error)) {
				return Promise.reject(error);
			}
		} catch {
			/* session handler optional at bootstrap */
		}

		const requestKey = `${error.config?.method}-${error.config?.url}`;
		removeAbortController(requestKey);

		// Handle modal transitions for monitored requests
		const isMonitoredRequest = error.config?._monitorType === 'monitored' ||
			error.config?._monitorType === 'form';

		if (isMonitoredRequest) {
			// Show error modal for monitored requests
			if (error.config?._shouldShowModal) {
				console.log('🚨 Universal interceptor: Showing error modal', {
					hasConfig: !!error.config,
					shouldShowModal: error.config?._shouldShowModal,
					monitorType: error.config?._monitorType,
					errorMessage: error.response?.data?.message || error.response?.data?.error || error.message
				});

				// Use centralized error handler
				const errorMessage =
					error.response?.data?.message ||
					error.response?.data?.error ||
					error.message ||
					'An error occurred while processing your request';

				dispatchErrorModal(errorMessage, error.response?.data);
			} else {
				// Hide loading modal if no error modal should be shown
				dispatch({ type: HIDE_LOADING_MODAL });

				console.log('🚨 Universal interceptor: NOT showing error modal', {
					hasConfig: !!error.config,
					shouldShowModal: error.config?._shouldShowModal,
					monitorType: error.config?._monitorType
				});
			}
		}

		return Promise.reject(error);
	};
};

// Function to update all instances with new base URL
export const updateApiInstances = async (newHost?: string) => {
	const baseUrl = await updateApiBaseUrls(newHost);
	const sessionUrl = baseUrl.replace('/api', '/api/session');

	// Update all instances
	api.defaults.baseURL = baseUrl;
	noCreds.defaults.baseURL = baseUrl;
	publicApi.defaults.baseURL = baseUrl;
	sessionApi.defaults.baseURL = sessionUrl; 

	console.log('Updated all API instances to use:', baseUrl);
};

export const monitoredRequest = axios.create({
	withCredentials: true,
	timeout: 600000, // 10 minutes default (increased for resume processing and other long-running AI operations, especially large documents)
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
})

export const postMonitored = async (url: string, data?: any, config?: any) => {
	return await monitoredRequest.post(url, data, config).then(res => res.data);
}

export const putMonitored = async (url: string, data?: any, config?: any) => {
	return await monitoredRequest.put(url, data, config).then(res => res.data);
}

export const formRequest = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		'Content-Type': 'multipart/form-data'
	}
})

const logOut = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	},
	timeout: 5000
})

export const monitorFail = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
})

export const monitorPass = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
})

export const noMonitor = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	},
	// Allow long-running AI operations (image gen, large reports) to complete.
	// On local, default to 10 minutes; override via REACT_APP_API_TIMEOUT_MS if needed.
	timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS || 600000)
});

// Universal interceptors setup for all API instances
const universalResponseInterceptor = createUniversalResponseInterceptor();
const universalErrorInterceptor = createUniversalErrorInterceptor();

const proactiveApiResponseTap = (response: any) => {
	if (response?.config && !response.config._demoSandboxLocal) {
		try {
			const { cacheDemoGetResponse } = require('./utils/demoCompanySandbox');
			cacheDemoGetResponse(response.config, response.data, response.status);
		} catch {
			/* optional */
		}
	}
	if (Platform.OS === 'web' && response?.config) {
		try {
			const { recordProactiveApiResponse } = require('@services/proactiveActionIngester');
			recordProactiveApiResponse(response.config, response.data);
		} catch {
			/* optional */
		}
	}
	return response;
};

// Apply universal interceptors to all API instances
[api, noCreds, publicApi, sessionApi, monitoredRequest, formRequest, monitorFail, noMonitor].forEach(instance => {
	instance.interceptors.response.use(
		(response) => {
			reportApiSuccess();
			return proactiveApiResponseTap(response);
		},
		(err) => {
			reportApiError(err);
			try {
				const { maybeHandleSessionExpiredFromAxiosError } = require('@utils/handleSessionExpired');
				maybeHandleSessionExpiredFromAxiosError(err);
			} catch {
				/* session handler optional at bootstrap */
			}
			return Promise.reject(err);
		}
	);

	// Local-only: inject X-Dev-Auth-Bypass so AI routes work without a live session cookie.
	// Opt-in via EXPO_PUBLIC_DEV_AUTH_BYPASS=1 (never enabled in production builds).
	if (
		typeof __DEV__ !== 'undefined' &&
		__DEV__ &&
		(process.env.EXPO_PUBLIC_DEV_AUTH_BYPASS === '1' ||
			process.env.EXPO_PUBLIC_DEV_AUTH_BYPASS === 'true')
	) {
		instance.interceptors.request.use((config) => {
			config.headers = config.headers || ({} as typeof config.headers);
			config.headers['X-Dev-Auth-Bypass'] = 'true';
			const uid = process.env.EXPO_PUBLIC_DEV_AUTH_USER_ID;
			if (uid) {
				config.headers['X-Dev-Auth-UserId'] = uid;
			}
			const cid = process.env.EXPO_PUBLIC_DEV_AUTH_COMPANY_ID;
			if (cid) {
				config.headers['X-Dev-Auth-CompanyId'] = cid;
			}
			return config;
		});
	}
	// Request interceptors (keep existing optimized ones)
	if (instance === api) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: false,
				monitorType: 'api'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'api' })
		);
	} else if (instance === monitoredRequest) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: true,
				monitorType: 'monitored'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'monitored' })
		);
		// Add response error interceptor for monitored requests to show error modals
		instance.interceptors.response.use(
			universalResponseInterceptor,
			apiInterceptor.createErrorInterceptor({
				monitorType: 'monitored',
				shouldShowErrorModal: true
			})
		);
	} else if (instance === formRequest) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: true,
				monitorType: 'form'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'form' })
		);
		// Add response error interceptor for form requests to show error modals
		instance.interceptors.response.use(
			universalResponseInterceptor,
			apiInterceptor.createErrorInterceptor({
				monitorType: 'form',
				shouldShowErrorModal: true
			})
		);
	} else if (instance === noMonitor) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: false,
				shouldShowModal: false,
				monitorType: 'noMonitor'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'noMonitor' })
		);
		// Apply universal response interceptor to noMonitor
		instance.interceptors.response.use(universalResponseInterceptor, universalErrorInterceptor);
	} else if (instance === monitorFail) {
		// monitorFail needs error modal support but NOT loading modal
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: false, // No loading modal for monitorFail
				monitorType: 'monitorFail'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'monitorFail' })
		);
		// Add response error interceptor for monitorFail requests to show error modals
		instance.interceptors.response.use(
			universalResponseInterceptor,
			apiInterceptor.createErrorInterceptor({
				monitorType: 'monitorFail',
				shouldShowErrorModal: true
			})
		);
	} else {
		// Apply universal response interceptor to all other instances
		instance.interceptors.response.use(universalResponseInterceptor, universalErrorInterceptor);
	}
});

// Old interceptor removed - handled by universal interceptor

// Old interceptor removed - handled by universal interceptor

// Old interceptor removed - handled by universal interceptor

// Old interceptor removed - handled by universal interceptor


//External APIs______________________________________________________________________________________________________________________________________________
const weatherApi = axios.create({
	baseURL: 'https://api.openweathermap.org/data/2.5',
	withCredentials: false
})


const options = {
	method: 'GET',
	url: 'https://newsapi.org/v2/top-headlines?country=gb&apiKey=97b1c15122cc4974baa5b31c69a08490'
};

const companiesHouseAPI = axios.create({
	baseURL: 'https://api.company-information.service.gov.uk',
	auth: {
		username: COMPANIESHOUSE_API_KEY,
		password: ''
	}
})

const linkedInAPI = axios.create({
	baseURL: 'https://www.linkedin.com/oauth/v2',
	params: {
		response_type: 'code',
		client_id: process.env.EXPO_PUBLIC_LINKEDIN_CLIENTID,
		redirect_uri: LINKEDIN_PRODUCTION_WEB_REDIRECT,
		scope: LINKEDIN_OAUTH_SCOPE,
	}
})

// const quoteAPI = axios.create({
//     baseURL: 'https://zenquotes.io/api'
// })

export const authoriseLinkedIn = async () => {
	await linkedInAPI
		.get('/authorization').then(res => console.log(res))
};

export const getRemainingProfile = async () => {
	try {
		const invitations = await noMonitor.get('/login/get-new-invitations');
		console.log(invitations.data);

		dispatch({ type: UPDATED_INVITATIONS, payload: invitations.data.count });

		const messages = await noMonitor.get('/login/get-new-messages');

		dispatch({ type: UPDATED_MESSAGES, payload: messages.data.count });

		const calendarEntries = await noMonitor.get('/login/get-new-calendar-entries');

		dispatch({ type: UPDATED_CALENDAR_ENTRIES, payload: calendarEntries.data.count });

		return true;
	} catch (err) {
		throw err; // Re-throw the error to be caught by the caller if needed.
	}
};

function delay(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}


// export const quoteToday = async () => {
//     await quoteAPI
//         .get('/today')
// }

export const getCompanyInformation = async (companyNumber) => {
	await companiesHouseAPI
		.get(`/company/${companyNumber}`)
}


export const getCompanyInformationName = async (company) => {
	await companiesHouseAPI
		.get(`/search/companies`, { params: { q: company } })
		.then(res => console.log(res))
}

//Obtain weather information
export const getLocalWeather = async (userLocation) => {
	if (!userLocation) {
		return
	}
	await weatherApi
		.get(`/weather?q=${userLocation}&appId=${WEATHER_API_KEY}&units=metric`)
		.then(res => dispatch({ type: WEATHER_RETRIEVED, payload: res.data }))
		.catch(err => console.log(err))
}

export const refreshUser = async () => {
	await noMonitor.get('/user/refresh-user').then(res => {
		if (__DEV__) {
			console.log('✅ User data refreshed');
		}
		dispatch({ type: REFRESH, payload: res.data.data })
	})
}

/** Full profile for the session user, without dispatching (REFRESH only merges a few fields). */
export const fetchCurrentUser = async () => {
	const res = await noMonitor.get('/user/refresh-user');
	return res.data?.data ?? null;
}

export const getLocalNews = async (language, country) => {
	await axios
		.request(options)
		.then(res => { dispatch({ type: NEWS_UPDATED, payload: res.data }) })
		.catch(err => console.log(err))
}

function newAbortSignal(timeoutMs) {
	const abortController = new AbortController();
	setTimeout(() => abortController.abort(), timeoutMs || 0);

	return abortController.signal;
}

export const getSession = async () => {
	console.log('Requesting session');
	// Avoid false "session expired" logouts from slow networks (guard + loading screen).
	const sessionProbeMs = Number(process.env.REACT_APP_SESSION_PROBE_TIMEOUT_MS || 15000);
	// POST `/` against SESSION_API base (`…/api/session`) so we hit `/api/session/` (backend route).
	// AbortSignal must be axios config, not the request body.
	return await sessionApi.post(
		'/',
		{},
		{
			signal: newAbortSignal(sessionProbeMs),
			withCredentials: true,
		}
	);
}

export const getUserCompanies = async () => {
	console.log('📋 Fetching user companies');
	try {
		const response = await api.get('/user/companies');
		return response.data;
	} catch (error) {
		console.error('❌ Error fetching user companies:', error);
		throw error;
	}
}

//Google APIs______________________________________________________________________________________________________________________________________________
const googleAnalyticsAPI = axios.create({
	baseURL: 'https://analyticsdata.googleapis.com/v1beta/properties',
})

const googleMailApi = axios.create({
	baseURL: 'https://gmail.googleapis.com'
})

const getActiveCompanyMailbox = () => {
	const state = store.getState();
	const companyId = resolveCompanyId(state.app?.selectedProfile);
	const preferPersonal = Boolean(state.app?.mail?.preferPersonalMailbox);
	if (preferPersonal) {
		return null;
	}
	const active = state.app?.mail?.activeCompanyMailbox;
	if (!companyId || !active || String(active.companyId) !== String(companyId)) {
		return null;
	}
	return active;
};

const getStoredGmailAuth = () => {
	const companyMailbox = getActiveCompanyMailbox();
	if (companyMailbox?.email) {
		return {
			email: companyMailbox.email,
			googleId: companyMailbox.googleId,
			accessToken: companyMailbox.accessToken,
			scopes: companyMailbox.scopes || ['https://www.googleapis.com/auth/gmail.modify'],
		};
	}
	// Personal inbox is allowed when no company mailbox is active (incl. prefer-personal mode).
	const google = store.getState().promiseData.data.userData.authentications?.google;
	return resolveGoogleMailAuth(google);
};

const getStoredGmailAccessToken = (email?: string) => {
	const companyMailbox = getActiveCompanyMailbox();
	if (companyMailbox) {
		if (email && String(companyMailbox.email || '').toLowerCase() !== String(email).toLowerCase()) {
			return '';
		}
		return normalizeGoogleAccessToken(companyMailbox.accessToken);
	}
	const google = store.getState().promiseData.data.userData.authentications?.google;
	if (email) {
		const list = Array.isArray(google) ? google : (google ? [google] : []);
		const match = list.find((auth) => auth?.email === email);
		const token = normalizeGoogleAccessToken(match?.accessToken);
		if (token) return token;
	}
	return normalizeGoogleAccessToken(getStoredGmailAuth()?.accessToken);
};

const applyGoogleMailTokenToAxiosConfig = (config, token) => {
	if (!config || !token) return config;
	config.headers = config.headers || {};
	config.headers.Authorization = `Bearer ${token}`;
	if (typeof config.url === 'string' && config.url.includes('access_token=')) {
		config.url = config.url.replace(/access_token=[^&]*/, `access_token=${token}`);
	}
	if (config.params?.access_token) {
		config.params.access_token = token;
	}
	if (typeof config.data === 'string' && config.data.includes('access_token=')) {
		config.data = config.data.replace(/access_token=[^&\s"']+/g, `access_token=${token}`);
	}
	return config;
};

const createGoogleMailRefreshInterceptor = (client) => async (error) => {
	const originalRequest = error.config;
	if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
		return Promise.reject(error);
	}
	originalRequest._retry = true;
	try {
		const googleObj = getStoredGmailAuth();
		if (!googleObj?.email) {
			return Promise.reject(error);
		}
		const refreshed = await renewToken(googleObj.email);
		if (!refreshed) {
			return Promise.reject(error);
		}
		const newToken = getStoredGmailAccessToken(googleObj.email);
		if (!newToken) {
			return Promise.reject(error);
		}
		applyGoogleMailTokenToAxiosConfig(originalRequest, newToken);
		return client.request(originalRequest);
	} catch {
		return Promise.reject(error);
	}
};

googleMailApi.interceptors.response.use(
	(response) => response,
	createGoogleMailRefreshInterceptor(googleMailApi)
);

googleAnalyticsAPI.interceptors.response.use(function (response) {
	return response;
}, async function (error) {
	const originalRequest = error.config;

	// Check if error is 401 and we haven't retried yet
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;

		try {
			const { google } = store.getState().promiseData.data.userData.authentications;
			const googleAuthsArray = Array.isArray(google) ? google : (google ? [google] : []);
			const googleObj = googleAuthsArray.find(auth => auth?.scopes?.includes('https://www.googleapis.com/auth/analytics.readonly') && auth?.email) || googleAuthsArray[0];
			
			await renewToken(googleObj?.email);
			
			// Retry the original request with updated token from Redux state
			const { google: updatedGoogle } = store.getState().promiseData.data.userData.authentications;
			const updatedGoogleAuthsArray = Array.isArray(updatedGoogle) ? updatedGoogle : (updatedGoogle ? [updatedGoogle] : []);
			const updatedGoogleObj = updatedGoogleAuthsArray.find(auth => auth?.email === googleObj?.email) || updatedGoogleAuthsArray[0];

			if (updatedGoogleObj?.accessToken) {
				// Update the authorization header with new token
				originalRequest.headers.Authorization = `Bearer ${updatedGoogleObj.accessToken}`;
				return googleAnalyticsAPI.request(originalRequest);
			}
		} catch (refreshError) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n Try signing in again', type: 'error' } });
			return Promise.reject(refreshError);
		}
	}
	return Promise.reject(error);
}
);


const googleMailCount = axios.create({
	baseURL: 'https://gmail.googleapis.com',
})

googleMailCount.interceptors.response.use(
	(response) => response,
	createGoogleMailRefreshInterceptor(googleMailCount)
);

const googleAPI = axios.create({
	baseURL: 'https://www.googleapis.com/'
})

const googleMailBatch = axios.create({
	baseURL: 'https://www.googleapis.com/batch',
	headers: {
		'Content-Type': 'multipart/mixed; boundary="foo_bar"'
	}
})

googleMailBatch.interceptors.response.use(
	(response) => response,
	createGoogleMailRefreshInterceptor(googleMailBatch)
);

const googleOAuthEndpoint = axios.create({
	baseURL: 'https://oauth2.googleapis.com/token'
})

const googleDriveAPi = axios.create({
	baseURL: 'https://www.googleapis.com/drive/v3'
})

googleDriveAPi.interceptors.response.use(function (response) {
	return response;
}, async function (error) {
	const originalRequest = error.config;

	// Check if error is 401 and we haven't retried yet
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;

		try {
			const { google } = store.getState().promiseData.data.userData.authentications;
			const googleAuthsArray = Array.isArray(google) ? google : (google ? [google] : []);
			const googleObj = googleAuthsArray.find(auth => auth?.scopes?.includes('https://www.googleapis.com/auth/drive') && auth?.email) || googleAuthsArray[0];
			
			await renewToken(googleObj?.email);
			
			// Retry the original request with updated token from Redux state
			const { google: updatedGoogle } = store.getState().promiseData.data.userData.authentications;
			const updatedGoogleAuthsArray = Array.isArray(updatedGoogle) ? updatedGoogle : (updatedGoogle ? [updatedGoogle] : []);
			const updatedGoogleObj = updatedGoogleAuthsArray.find(auth => auth?.email === googleObj?.email) || updatedGoogleAuthsArray[0];

			if (updatedGoogleObj?.accessToken) {
				// Update the authorization header with new token
				originalRequest.headers.Authorization = `Bearer ${updatedGoogleObj.accessToken}`;
				return googleDriveAPi.request(originalRequest);
			}
		} catch (refreshError) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n Try signing in again', type: 'error' } });
			return Promise.reject(refreshError);
		}
	}
	return Promise.reject(error);
}
);

const googleSendApi = axios.create({ baseURL: 'https://www.googleapis.com/upload/gmail/v1/users/me/messages/send' })

const applyRefreshedGoogleMailToken = (data) => {
	if (!data?.newToken) {
		return false;
	}
	const companyMailbox = getActiveCompanyMailbox();
	const refreshedEmail = data.email ? String(data.email).toLowerCase() : '';
	const activeEmail = companyMailbox?.email ? String(companyMailbox.email).toLowerCase() : '';
	const shouldUpdateCompanyMailbox =
		Boolean(companyMailbox?.email) &&
		(!refreshedEmail || !activeEmail || refreshedEmail === activeEmail);

	if (shouldUpdateCompanyMailbox) {
		dispatch({
			type: UPDATE_ACTIVE_COMPANY_MAILBOX_TOKEN,
			payload: {
				companyId: String(companyMailbox.companyId),
				email: data.email || companyMailbox.email,
				googleId: data.googleId || companyMailbox.googleId,
				accessToken: data.newToken,
				newToken: data.newToken,
				scopes: data.newScopes || companyMailbox.scopes,
				newScopes: data.newScopes || companyMailbox.scopes,
				mailType: companyMailbox.mailType || 'google',
			},
		});
		return true;
	}

	dispatch({ type: UPDATE_GOOGLE_MAIL_TOKEN, payload: data });
	if (data.email || data.googleId) {
		dispatch({
			type: UPDATE_GOOGLE_ACCOUNT,
			payload: {
				email: data.email,
				googleId: data.googleId,
				accessToken: data.newToken,
				scopes: data.newScopes,
			},
		});
	}
	return true;
};

const renewToken = async (email?: string) => {
	try {
		const res = await noMonitor.get(email ? `/google/refresh-token?email=${encodeURIComponent(email)}` : '/google/refresh-token');
		if (res.status === 200 && res.data?.newToken) {
			return applyRefreshedGoogleMailToken(res.data);
		}
		return false;
	} catch {
		return false;
	}
}

/**
 * Attempts to refresh the Google mail access token via the backend (same flow as 401 interceptors).
 * Use this for explicit "Re-authenticate" from the UI so re-auth follows the normal auth flow:
 * try refresh first, then only prompt for full re-connect if refresh fails.
 * @returns true if a new token was obtained and stored, false otherwise
 */
export const refreshMailToken = async (email?: string): Promise<boolean> => {
	try {
		const res = await noMonitor.get(email ? `/google/refresh-token?email=${encodeURIComponent(email)}` : '/google/refresh-token');
		if (res.status === 200 && res.data?.newToken) {
			return applyRefreshedGoogleMailToken(res.data);
		}
		return false;
	} catch {
		return false;
	}
}

export const getDefaultWebStats = async (accessToken, payload, propertyId, reportType) => {
	const headers = {
		"Authorization": `Bearer ${accessToken}`,
		"Content-Type": "application/json"
	}
	try {
		const googleWebAnalytics = await googleAnalyticsAPI.post(`/${propertyId}:${reportType}`, payload, { headers });
		if (googleWebAnalytics.status !== 200) {
			dispatch({ type: GOOGLE_AUTH_FAILED });
		}
		return googleWebAnalytics?.data?.rows || [];
	} catch (error) {
		console.warn('[api.getDefaultWebStats] Google Analytics request failed:', {
			propertyId,
			reportType,
			error,
		});
		// Return empty array instead of throwing to prevent Promise.all from failing
		// This allows other successful chart requests to render even if one fails
		return [];
	}
}

//GMAIL parse function
async function parseBatchResponse(response) {
	// Not the same delimiter in the response as we specify ourselves in the request,
	// so we have to extract it.
	const delimiter = response.substr(0, response.indexOf('\n'));
	const parts = response.split(delimiter);
	// The first part will always be an empty string. Just remove it.
	parts.shift();
	// The last part will be the "--". Just remove it.
	parts.pop();
	const result = [];
	for (let i = 0; i < parts.length; i++) {
		let part = parts[i];
		let p = part.substring(part.indexOf("{"), part.lastIndexOf("}") + 1);
		try {
			result.push(JSON.parse(p));
		} catch (e) {
			continue;
		}
	}
	return result;
}

export const geoCodeAddress = async (address) => {
	const url = 'proxy/google-map-proxy/geocode/json';
	const params = {
		address: address,
	}
	try {
		// Use default api instance instead of googleMaps, so it goes to your backend
		const response = await api.get(url, { params });
		return response;
	} catch (error) {
		console.error("Error fetching data from Google Maps API proxy", error);
		throw error;
	}
}

export const googleMapSearch = async (query, options: any = {}) => {
	const url = 'proxy/google-map-proxy/places:searchText';
	const headers = {
		'Content-Type': 'application/json',
		// The proxy will inject the API key using backend environment variables
		'X-Goog-FieldMask': 'places.businessStatus,places.iconBackgroundColor,places.types,places.iconMaskBaseUri,places.rating,places.displayName,places.formattedAddress,places.websiteUri'
	};
	const data: any = {
		textQuery: query,
		maxResultCount: options.maxResultCount || 20
	}

	if (options.locationBias) {
		data.locationBias = options.locationBias;
	}

	if (options.pageToken) {
		data.pageToken = options.pageToken;
	}

	try {
		// Call your backend proxy using the configured api axios instance
		const response = await api.post(url, data, { headers });
		return response.data;
	} catch (error) {
		console.error("Error fetching data from Google Maps API proxy", error);
		throw error;
	}
}

// Helper function to fetch multiple pages of Google Places results
export const googleMapSearchPaginated = async (query, options: any = {}) => {
	const maxResults = options.maxResultCount || 20;
	const maxPages = Math.ceil(maxResults / 20); // Google Places API returns max 20 per page
	const allPlaces = [];
	let nextPageToken = null;
	let pageCount = 0;

	const locationBias = options.locationBias;

	do {
		const response = await googleMapSearch(query, {
			maxResultCount: 20, // Always request 20 per page (API limit)
			locationBias: pageCount === 0 ? locationBias : undefined, // Only include locationBias on first request
			pageToken: nextPageToken || undefined
		});

		if (response.places && Array.isArray(response.places)) {
			allPlaces.push(...response.places);
		}

		nextPageToken = response.nextPageToken;
		pageCount++;

		// Stop if we have enough results or no more pages
		if (allPlaces.length >= maxResults || !nextPageToken || pageCount >= maxPages) {
			break;
		}
	} while (nextPageToken && pageCount < maxPages);

	// Return only the requested number of results
	return {
		places: allPlaces.slice(0, maxResults),
		nextPageToken: allPlaces.length >= maxResults ? nextPageToken : null
	};
}


export const requestNextPage = async (accessToken, nextPageToken, gId = 'me') => {
	const token = resolveLatestGmailToken(accessToken);
	const userId = resolveGmailUserId(gId);
	return await googleMailApi.get(`/gmail/v1/users/${userId}/messages`, {
		headers: { Authorization: `Bearer ${token}` },
		params: { access_token: token, pageToken: nextPageToken },
	})
		.then(async (res) => {
			const parsedResponse = await fetchGmailMessageBatch(userId, res.data.messages, token);
			dispatch({ type: MAIL_RETRIEVED })
			return { messages: parsedResponse, nextPageToken: res.data.nextPageToken };
		})
}

export const requestLastPage = async (accessToken, lastPageToken, gId = 'me') => {
	const token = resolveLatestGmailToken(accessToken);
	const userId = resolveGmailUserId(gId);
	return await googleMailApi.get(`/gmail/v1/users/${userId}/messages`, {
		headers: { Authorization: `Bearer ${token}` },
		params: { access_token: token, pageToken: lastPageToken },
	})
		.then(async (res) => {
			const parsedResponse = await fetchGmailMessageBatch(userId, res.data.messages, token);
			dispatch({ type: MAIL_RETRIEVED })
			return { messages: parsedResponse, nextPageToken: res.data.nextPageToken };
		})
}

const resolveGmailUserId = (gId) => (!gId || gId === 'me' || /^\d+$/.test(String(gId)) ? 'me' : gId);

const resolveLatestGmailToken = (fallbackToken?: string) =>
	getStoredGmailAccessToken() || String(fallbackToken || '').replace(/^Bearer\s+/i, '').trim();

const fetchGmailMessageBatch = async (userId, messageIds, accessToken) => {
	const token = resolveLatestGmailToken(accessToken);
	if (!token || !Array.isArray(messageIds) || messageIds.length === 0) {
		return [];
	}
	const boundary = 'foo_bar';
	let header = '';
	messageIds.forEach((element: { id: any; }) => {
		header += '\n' + `--${boundary}` + '\n' + `Content-Type: application/http` + '\n' + '\n' + `GET /gmail/v1/users/${userId}/messages/${element.id}?format=full&access_token=${token}`;
	});
	header += '\n' + `--${boundary}--`;
	const res = await googleMailBatch.post(`/gmail/v1`, header, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return parseBatchResponse(res.data);
};

export const searchMail = async (accessToken, query, filter) => {
	let dateFilter = '';

	if (filter && filter.startDate && filter.endDate) {
		dateFilter = `after:${filter.startDate} before:${filter.endDate}`;
	}

	const fullQuery = `${query} ${dateFilter}`.trim();
	const token = resolveLatestGmailToken(accessToken);
	if (!token) {
		return [];
	}

	const listRes = await googleMailApi.get(`/gmail/v1/users/me/messages`, {
		headers: { Authorization: `Bearer ${token}` },
		params: { q: fullQuery, access_token: token },
	});
	const messageIdArr = listRes.data?.messages;
	if (!Array.isArray(messageIdArr) || messageIdArr.length === 0) {
		return [];
	}

	const latestToken = resolveLatestGmailToken(token);
	const parsedResponse = await fetchGmailMessageBatch('me', messageIdArr, latestToken);
	return (Array.isArray(parsedResponse) ? parsedResponse : []).filter(
		(item) => item && item.id && !item.error
	);
}

export const listGmailMessageIds = async (query, maxResults = 20) => {
	const token = resolveLatestGmailToken();
	if (!token) {
		return [];
	}
	const res = await googleMailApi.get(`/gmail/v1/users/me/messages`, {
		headers: { Authorization: `Bearer ${token}` },
		params: {
			q: query,
			maxResults,
			access_token: token,
		},
	});
	const messages = res.data?.messages;
	if (!Array.isArray(messages)) {
		return [];
	}
	return messages.map((item: { id?: string }) => item?.id).filter(Boolean);
}

export const getGmailMessageHeaders = async (messageIds, headerNames = ['From']) => {
	const token = resolveLatestGmailToken();
	const ids = Array.isArray(messageIds) ? messageIds.filter(Boolean).slice(0, 5) : [];
	if (!token || ids.length === 0) {
		return [];
	}
	const headers = { Authorization: `Bearer ${token}` };
	const results = await Promise.all(
		ids.map(async (id) => {
			const res = await googleMailApi.get(`/gmail/v1/users/me/messages/${id}`, {
				headers,
				params: {
					format: 'metadata',
					metadataHeaders: headerNames,
					access_token: token,
				},
			});
			return res.data;
		})
	);
	return results.filter(Boolean);
}

export const lookupDataByEmail = async (emails: string[]) => {
	const unique = [...new Set(
		(emails || [])
			.map((email) => String(email || '').trim().toLowerCase())
			.filter((email) => email.includes('@'))
	)].slice(0, 20);
	if (!unique.length) {
		return [];
	}
	const res = await noMonitor.post('/data/lookup-by-email', { emails: unique });
	const matches = res?.data?.matches ?? res?.data;
	return Array.isArray(matches) ? matches : [];
}

export const searchGmail = async (accessToken, query, gId = 'me') => {
	const token = resolveLatestGmailToken(accessToken);
	const userId = resolveGmailUserId(gId);
	return await googleMailApi.get(`/gmail/v1/users/${userId}/messages`, {
		headers: { Authorization: `Bearer ${token}` },
		params: { q: query, access_token: token },
	})
		.then(async (res) => {
			const parsedResponse = await fetchGmailMessageBatch(userId, res.data.messages, token);
			dispatch({ type: MAIL_RETRIEVED })
			return parsedResponse;
		})
}

export const getAllGmail = async (gId, accessToken, tag) => {
	const token = resolveLatestGmailToken(accessToken);
	if (!token) {
		throw new Error('No Google access token available');
	}
	const userId = resolveGmailUserId(gId);
	const headers = { Authorization: `Bearer ${token}` };
	return await googleMailApi.get(`/gmail/v1/users/${userId}/messages`, {
		headers,
		params: {
			access_token: token,
			...(tag ? { q: `label:${tag}` } : {}),
		},
	})
		.then(async (res) => {
			const messageIdArr = res.data.messages;
			const nextPageToken = res.data.nextPageToken;
			const estimateSize = res.data.resultSizeEstimate;
			try {
				if (!Array.isArray(messageIdArr) || messageIdArr.length === 0) {
					return { messages: [], nextPageToken, estimateSize };
				}
				const parsedResponse = await fetchGmailMessageBatch(userId, messageIdArr, token);
				dispatch({ type: MAIL_RETRIEVED })
				return { messages: parsedResponse, nextPageToken, estimateSize };
			} catch (e) {
				console.warn('[getAllGmail] Failed to parse batch response');
				return { messages: [], nextPageToken, estimateSize };
			}
		})
}

export const sendGoogleMail = async (token: string, mail: {}) => {
	const headers = {
		'Authorization': `Bearer ${token}`,
		'Content-Type': "message/rfc822"
	}
	return await googleMailCount.post(`/gmail/v1/users/me/messages/send`, mail, { headers })
}

export const markRead = async (mailId: string, accessToken: string) => {

	const headers = {
		'Authorization': `Bearer ${accessToken}`,
		'Content-Type': 'application/json'
	};

	return await googleMailCount.post(`/gmail/v1/users/me/threads/${mailId}/modify`, { addLabelIds: [], removeLabelIds: ['UNREAD'] }, { headers }).then(res => { return res })
}

export const refreshGoogleAccess = async () => {
	return refreshMailToken();
}

export const googleOAuth = async (googleAuth) => {
	return await monitoredRequest.post('/google/code', googleAuth)
}

export const getGmailCount = async (gId, accessToken) => {
	const token = resolveLatestGmailToken(accessToken);
	if (!token) {
		throw new Error('No access token provided');
	}
	const headers = {
		'Authorization': `Bearer ${token}`,
		'Content-Type': 'application/json'
	};
	const userId = resolveGmailUserId(gId);
	return await googleMailApi.get(`/gmail/v1/users/${userId}/labels/INBOX`, {
		headers,
		params: { access_token: token },
	})
}

export const getGmailLabelCounts = async (accessToken, labelIds = ['INBOX', 'STARRED', 'SENT', 'DRAFT', 'SPAM', 'TRASH']) => {
	const token = resolveLatestGmailToken(accessToken);
	if (!token) {
		throw new Error('No access token provided');
	}
	const headers = {
		Authorization: `Bearer ${token}`,
		'Content-Type': 'application/json',
	};
	const entries = await Promise.all(
		labelIds.map(async (labelId) => {
			try {
				const res = await googleMailApi.get(`/gmail/v1/users/me/labels/${labelId}`, {
					headers,
					params: { access_token: token },
				});
				return [
					labelId,
					{
						total: Number(res.data?.messagesTotal || 0),
						unread: Number(res.data?.messagesUnread || 0),
					},
				];
			} catch {
				return [labelId, { total: 0, unread: 0 }];
			}
		})
	);
	return Object.fromEntries(entries);
}

export const getGDriveItemsList = async (accessToken: string) => {
	const header = { 'authorization': 'Bearer ' + accessToken }
	return await googleDriveAPi.get(`/files?fields=files(id,name,mimeType,thumbnailLink,hasThumbnail,iconLink)`, { headers: header })
		.then(res => {
			if (res.status != 200) {
				console.log('Failed to retrieve Google Drive File List')
			}
			else
				return res
		})
		.catch(err => {
			console.log(err)
		})
}

export const getGDriveFolderContent = async (accessToken: string, file: string) => {
	const header = { 'authorization': 'Bearer ' + accessToken }
	return await googleDriveAPi.get(`/files?q=%27${file}%27%20in%20parents&fields=files(id,name,mimeType,thumbnailLink,hasThumbnail,iconLink)`, { headers: header })
		.then(res => {
			if (res.status != 200) {
				console.log('Failed to retrieve Google Drive File List')
			} else
				return res
		})
		.catch(err => {
			console.log(err)
		})
}


export const getGDriveItem = async (accessToken: string, file: string, mimeType: string) => {
	const header = { 'authorization': 'Bearer ' + accessToken }
	return await googleDriveAPi.get(`/files/${file}`, { headers: header, params: { alt: 'media' } })
		.then(res => {
			if (res.status != 200) {
				console.log(`Failed to retrieve item "${file}"`)
			}
			return res
		})
		.catch(err => console.log(err))
}

//Get list of calendar events


export const getGoogleCalendarList = async (accessToken: string, calendarIds: string[], params: GoogleCalTypes) => {
	const header = { 'authorization': 'Bearer ' + accessToken };


	const responses = await Promise.all(
		calendarIds.map(async calendarId => {
			try {
				const response = await googleAPI.get(`/calendar/v3/calendars/${calendarId}/events`, { headers: header, params: params });
				return response.data;
			} catch (error) {
				return null; // Return null for failed requests
			}
		})
	);

	return responses;
}



//Stripe APIs
//Generate Stripe APIs

export const createPaymentIntent = async (body) => {
	return await api.post(`/payments/create-payment-intent`, body, { withCredentials: true })
		.then(res => {
			if (res.status != 200) {
				console.log('FAILED!!', res)
			}
			else return res
		})
		.catch((err) => {
			console.log(err)
		})
}
//Internal APIs______________________________________________________________________________________________________________________________________________
//Retrieve user query history
export const retrieveQueriesByID = async (id) => {
	await api.get(`/userActionHistory/${id}`, { withCredentials: true })
		.then(res => {
			const queryHistory = res.data.data;
			dispatch({ type: HISTORY_RETRIEVED, payload: queryHistory });
		})
		.catch(err => (dispatch({ type: GET_ERRORS, payload: err?.response || 'Query history not retrieved' })))
}
export const insertQueryHistory = (id, payload) => { api.put(`/userActionHistory/${id}`, payload) }

//Get User Queries
export const quoteToday = async () => await noMonitor.get('/quotes')
export const addTwofa = (id: String, payload) => api.post(`/${id}/add2fa`, payload, { withCredentials: true }).catch(err => dispatch({ type: GET_ERRORS, payload: err.response }))
export const disableMfa = (id: String) => api.delete(`/${id}/remove2fa`, { withCredentials: true }).catch(err => dispatch({ type: GET_ERRORS, payload: err.response }))
export const toggleCanBeInvited = (payload) => api.put(`/user/security/can-be-invited`, payload, { withCredentials: true }).catch(err => dispatch({ type: GET_ERRORS, payload: err.response }))
export const setRestrictRequestsToNetwork = async (restrictRequestsToNetwork: boolean) => {
	const res = await api.put(`/user/security/request-privacy`, { restrictRequestsToNetwork }, { withCredentials: true });
	return res.data as { success: boolean; restrictRequestsToNetwork: boolean };
}

export const retrieveData = responseData => {
	return {
		type: DATA_QUERY,
		payload: responseData
	};
};

//Check if company exists
export const companyCheck = async (companyData, formValues) => {
				await noCreds.get('/get-product-list').then(res => {
					dispatch({ type: UPDATE_PRODUCTS, payload: res.data.data })
				})
					.catch(e => {
						dispatch({ type: SHOW_AUTH_MODAL, data: { content: e.data.data, type: 'error' } })
					});
				dispatch({ type: SHOW_REGISTER_MODAL, payload: formValues });
				return
		}

export const getTags = async (id) => {
	console.log('requested tags')
	return await noMonitor.get(`/get-tags/${id}`)
}

export const generateBusinessCard = async (data, platform = null) => {
	// Detect platform if not provided
	const detectedPlatform = platform || (typeof Platform !== 'undefined' ? Platform.OS : 'ios');
	return await noMonitor.post('/user/generate-business-card', {
		barcodeValue: data,
		platform: detectedPlatform // Send platform: 'ios' or 'android'
	})
}

//Register User
export const registerUser = async (userData: any) => {
	return await api
		.post("/register", userData, { withCredentials: true })
		.then((res) => {
			if (res.status !== 201) {
				return () => {
					(
						dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
					)
				}
			} else {
				dispatch({ type: BACK_TO_LOGIN })
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message, type: 'success' } })
			}
		})
		.catch((err) => {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: err?.response.data, type: 'error' } })
		});
}

export const sendWhatsAppRegisterOtp = async (payload: { phone: string }) => {
	return await api
		.post('/register/whatsapp/send-otp', payload, { withCredentials: true })
		.then((res) => res.data);
};

export const verifyWhatsAppRegisterOtp = async (payload: { phone: string; code: string }) => {
	return await api
		.post('/register/whatsapp/verify-otp', payload, { withCredentials: true })
		.then((res) => res.data);
};

export const completeWhatsAppRegister = async (payload: {
	phone: string;
	code: string;
	password: string;
	name?: string;
	rememberMe?: boolean;
}) => {
	return await api.post('/register/whatsapp/complete', payload, { withCredentials: true });
};

export const addUserTags = async (payload) => {
	return await noMonitor
		.post('/user/add-tags', payload).then(() => { return { success: true } })
}

export const addFavourite = async (id, type, body) => {
	let url = body?.content;
	return await monitorFail.post(`/set-favourite/${type}/${id}`, body).then(async res => {
		console.log(url);
	}).catch(()=>{
		dispatch({ type: HANDLE_FAVOURITE, payload: { id, type, body: url } })
	})
}

export const getDataFavorites = async () => {
	return await noMonitor.get('/data/get-favourites').then(res => {
		if (res.status === 200) {
			return res.data.data
		}
	}
	)
}

export const getProjectFavorites = async () => {
	return await noMonitor.get('/projects/get-favourites').then(res => {
		if (res.status === 200) {
			return res.data.data
		}
	}
	)
}


export const updateUserProfile = async (payload) => {
	const body = { content: payload };
	// `noMonitor`: background profile patches (onboarding, preferences, flags) must not trigger
	// the global loading modal — especially right after onboarding / beside the post-onboarding tour.
	return await noMonitor
		.post('/user/update-profile', body)
		.then((res: { data: { changes: any; success?: boolean } }) => {
			console.log('formik api response', res.data);
			return {
				success: res.data.success !== false,
				data: res.data,
			};
		})
		.catch((error) => {
			console.error('Error updating user profile:', error);
			return { success: false, error: error.message || 'Failed to update profile' };
		});
};

export const updateUserDepartment = async (targetUserId: string, department: string) => {
	const body = { targetUserId, department }
	return await monitorFail
		.post('/user/update-department', body).then((res: {
			data: { success: boolean; message?: string; };
		}) => {
			console.log('✅ User department updated:', res.data);
			return res.data
		})
		.catch((error) => {
			console.error('❌ Error updating user department:', error);
			throw error;
		})
}

/**
 * Save dismissed save prompts to backend
 * @param dismissedPrompts Array of chat fingerprints that user dismissed save prompts for
 */
export const saveDismissedSavePrompts = async (dismissedPrompts: string[]) => {
	try {
		const body = { content: { easyModeDismissedSavePrompts: dismissedPrompts } };
		return await noMonitor
			.post('/user/update-profile', body)
			.then((res: { data: { success?: boolean; changes?: any } }) => {
				console.log('✅ Dismissed save prompts saved:', res.data);
				return {
					success: res.data.success !== false,
					data: res.data
				};
			})
			.catch((error) => {
				console.error('❌ Error saving dismissed save prompts:', error);
				return { success: false, error: error.message || 'Failed to save dismissed prompts' };
			});
	} catch (error) {
		console.error('❌ Error saving dismissed save prompts:', error);
		return { success: false, error: error?.message || 'Failed to save dismissed prompts' };
	}
}

/**
 * Get dismissed save prompts from user profile
 * Returns empty array if not found or on error
 */
export const getDismissedSavePrompts = async (): Promise<string[]> => {
	try {
		const session = await getSession();
		if (session?.data?.userData?.easyModeDismissedSavePrompts) {
			const dismissed = session.data.userData.easyModeDismissedSavePrompts;
			console.log('✅ Loaded dismissed save prompts from backend:', dismissed?.length || 0);
			return Array.isArray(dismissed) ? dismissed : [];
		}
		return [];
	} catch (error) {
		console.error('❌ Error loading dismissed save prompts:', error);
		return [];
	}
}

export const assignUserToDepartment = async (companyId: string, payload: { user: string; departmentId: string }) => {
	const body = { 
		data: { user: payload.user },
		departmentId: payload.departmentId 
	}
	// CompanyId comes from session, not URL params
	return await monitorFail
		.post(`/company/assign-department`, body).then((res: {
			data: { success: boolean; };
		}) => {
			console.log('✅ User assigned to department:', res.data);
			return res.data
		})
		.catch((error) => {
			console.error('❌ Error assigning user to department:', error);
			throw error;
		})
}

export const updateUserContext = async (contextData) => {
	const body = { context: contextData }
	return await monitorFail
		.post('/user/update-context', body).then((res: {
			data: { data?: { context?: any }; context?: any };
			success?: boolean;
		}) => {
			const envelope = res.data?.data && typeof res.data.data === 'object'
				? res.data.data
				: res.data;
			const context = envelope?.context;
			if (context && typeof context === 'object') {
				dispatch({
					type: UPDATE_USER,
					payload: {
						context,
						profile_information: { context },
					},
				});
			}
			return { success: true, data: context }
		})
		.catch((error) => {
			console.error('Error updating user context:', error);
			throw error;
		})
}

// User branding functions
export const getUserBranding = async () => {
	return await api.get('/user/branding')
		.then((res) => {
			console.log('branding get api response', res.data);
			return { success: true, data: res.data }
		})
		.catch((error) => {
			console.error('Error fetching user branding:', error);
			throw error;
		})
}

export const updateUserBranding = async (brandingData) => {
	return await monitorFail
		.put('/user/branding', brandingData).then((res: {
			data: any;
			success?: boolean;
		}) => {
			console.log('branding update api response', res.data);
			return { success: true, data: res.data }
		})
		.catch((error) => {
			console.error('Error updating user branding:', error);
			throw error;
		})
}

export const uploadBrandingImage = async (imageType: 'logo' | 'favicon', imageData: string, fileName?: string, mimeType?: string) => {
	return await monitorFail
		.post(`/user/branding/upload/${imageType}`, {
			imageData,
			fileName,
			mimeType
		}).then((res: {
			data: any;
			success?: boolean;
		}) => {
			console.log('branding image upload api response', res.data);
			return { success: true, data: res.data }
		})
		.catch((error) => {
			console.error('Error uploading branding image:', error);
			throw error;
		})
}

export const handleOAuth = async (request: any) => {
	return await monitorFail
		.post('/register/oauth', request)
}

export const getWeChatAuthUrl = async (params: {
	redirectUri: string;
	state: string;
	inWeChatBrowser?: boolean;
}) => {
	const res = await noMonitor.get('/oauth/wechat/auth-url', { params });
	return res.data as { success?: boolean; authUrl?: string };
}

export const wechatOAuthLogin = async (payload: {
	code: string;
	redirectUri: string;
	promoCode?: string;
	productId?: string;
	notificationToken?: string;
}) => {
	return await monitorFail.post('/register/oauth/wechat', payload);
}

export const authoriseGMail = async (gmailUser) => {
	return await monitorFail
		.post(`/authorisations/gmail`, gmailUser)
		.then(res => {
					return res
				})
}

export const authoriseMicrosoftMail = async (payload: {
	accessToken: string;
	refreshToken?: string;
	scopes?: string[] | string;
	userId?: string;
	type?: string;
}) => {
	return await monitorFail.post(`/authorisations/microsoft-mail`, payload)
}

export const updateNotificationToken = async (payload, userId) => {
	const body = { userId: userId, token: payload }
	await noMonitor.post('/user/update-notification-token', body)
}

export const passwordReset = async (formValues) => {
	const request = String(formValues || '').trim().toLowerCase();
	// Use publicApi (no global loading modal) — SMTP can be slow and monitoredRequest
	// would leave the set-password screen stuck on a spinner until gateway timeout.
	// Post `{}` (not `null`) — Express body-parser returns 400 for a JSON null body.
	// Encode email so `@` is safe in the path.
	return await publicApi
		.post(`/password-reset/${encodeURIComponent(request)}`, {}, { validateStatus: () => true })
		.then((res => {
			if (res.status !== 201) {
				dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content: res.data?.error || 'Please check the provided email address',
						type: 'error',
					},
				})
				return { success: false };
			}
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message, type: 'success' } })
			return { success: true };
		}))
		.catch(e => {
			console.error(e)
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please check the provided email address', type: 'error' } })
			return { success: false };
		})
};

export const validatePasswordReset = async (code: { code: string, newPassword: string }) => {
	if (code.code.length > 6) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please ensure that you have entered the correct code', type: 'error' } })
	}
	return await api.post(`/validatePasswordReset`, {
		code: code.code,
		newPassword: code.newPassword,
	})
		.then(res => {
			if (res.status !== 200) {
				console.error(res)
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
				return { success: false };
			} else {
				dispatch({ type: HIDE_PASSWORD_RESET_MODAL })
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message, type: 'success' } })
				return { success: true };
			}
		})
		.catch((e) => {
			console.error(e);
			dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: e?.response?.data?.error || 'Password reset failed',
					type: 'error',
				},
			});
			return { success: false };
		})
}

export const unlockUser = async (formValues) => {
	await api.post('/unlock', formValues)
		.then((res => {
			if (res.status !== 200) {
				dispatch({ type: FAILED, data: 'Request failed, please check your password and try again' })
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data, type: 'error' } });
			}
			else {
				dispatch({ type: HIDE_LOCK_MODAL });
			}
		}))
		.catch((err => {
			console.log(err);
		}))
};


//Set logged in user
export const setCurrentUser = decoded => {
	dispatch({
		type: SUCCESS,
		loading: false,
		error: null,
		data: decoded,
		isAuthenticated: true
	});
};

//Messaging APIs
export const getAllUsers = async (page?: number, pageSize?: number) => {
	const params: any = {};
	if (page !== undefined) params.page = page;
	if (pageSize !== undefined) params.limit = pageSize;
	return await noMonitor.get(`/users`, { params }).then(res => res.data.data);
}

export const getAllMessagingUsers = async (page?: number, pageSize?: number, filterExternal?: boolean) => {
	const params: any = {};
	if (page !== undefined) params.page = page;
	if (pageSize !== undefined) params.limit = pageSize;
	if (filterExternal !== undefined) params.external = filterExternal;
	return await noMonitor.get(`/users/messaging`, { params }).then(res => res.data);
}

// Message request functions
export const sendMessageRequest = async (queryClient: any, targetUserId: string, message?: string) => {
	queryClient.invalidateQueries({ queryKey: ['all-users'] });
	return await api.post(`/message-request/send`, { targetUserId, message }).then(res => res.data);
}

export const getMessageRequests = async () => {
	return await noMonitor.get(`/message-request/list`).then(res => res.data.data);
}

export const acceptMessageRequest = async (invitationId: string) => {
	return await api.post(`/message-request/accept`, { invitationId }).then(res => res.data);
}

export const rejectMessageRequest = async (invitationId: string) => {
	return await api.post(`/message-request/reject`, { invitationId }).then(res => res.data);
}

export const getUserHierarchy = async () => {
	return await noMonitor.get(`/users/hierarchy`).then(res => { return res.data })
}

export const getLargeProfile = async (selectedId) => await api.post(`/users/profile`, selectedId).then(res => { if (res.status !== 200) { return null } let avatar = res.data.data.toString?.(); let prepend = 'data:image/png;base64,'; let selectedAvatar = prepend + avatar; return selectedAvatar });
export const getUserConversations = async () => await noMonitor.get(`/conversations/recent-conversations`).then(res => res.data.data);
export const getConversation = async (data) => await monitorFail.post(`/conversations/messages`, data);
export const postMessage = async (userId, data) => await api.post(`/conversations/messages/${userId}/${data.participantId}`, data);

export const getChatConversationMembers = async (conversationId: string) => {
	if (!conversationId) throw new Error('conversationId required');
	return await noMonitor.get(`/conversations/${encodeURIComponent(conversationId)}/members`).then((res) => res.data);
};

export const leaveChatConversation = async (conversationId: string) => {
	if (!conversationId) throw new Error('conversationId required');
	return await api.post(`/conversations/${encodeURIComponent(conversationId)}/leave`, {}, { withCredentials: true }).then((res) => res.data);
};

export const setChatConversationMuted = async (conversationId: string, muted: boolean) => {
	if (!conversationId) throw new Error('conversationId required');
	return await api
		.post(`/conversations/${encodeURIComponent(conversationId)}/mute`, { muted }, { withCredentials: true })
		.then((res) => res.data);
};

// User loading 
export const setUserLoading = () => {
	return {
		type: USER_LOADING
	};
};

// @PaymentIntents
export const paymentSheet = async (req) => { return await noMonitor.post('/payments/payment-sheet', req).then(res => { return res.data.data }).catch(() => { throw new Error("No response") }) }

// Generate payment link for product (data item)
export const generateProductPaymentLink = async (params: {
	dataFolderId: string;
	dataItemId: string;
	paymentProvider: 'stripe' | 'paystack';
	recipientId?: string;
	businessPhoneNumberId?: string;
}) => {
	return await monitoredRequest.post('/payments/generate-product-payment-link', params).then(res => res.data);
};

// Product sales tracing - record when payment link is sent via messaging
export const recordPaymentLinkSent = async (params: {
	dataFolderId: string;
	dataItemId: string;
	source: 'whatsapp' | 'telegram';
	recipientId?: string;
	businessPhoneNumberId?: string;
}) => {
	return await monitoredRequest.post('/product-sales/record-link-sent', params).then(res => res.data);
};

// Get product sales stats for a product/item
export const getProductSalesStats = async (params?: {
	dataItemId?: string;
	dataFolderId?: string;
	type?: string;
	startDate?: string;
	endDate?: string;
	mode?: 'live' | 'draft';
}) => {
	const filtered = params ? Object.fromEntries(
		Object.entries(params).filter(([, v]) => v != null && v !== '')
	) as Record<string, string> : {};
	const qs = Object.keys(filtered).length ? '?' + new URLSearchParams(filtered).toString() : '';
	return await monitoredRequest.get('/product-sales/stats' + qs).then(res => res.data);
};

// Get product sales summary for dashboard
export const getProductSalesDashboard = async () => {
	return await monitoredRequest.get('/product-sales/dashboard').then(res => res.data);
};

/**
 * Switch company or user context
 * 
 * @param options - Switch options (supports multiple formats for backward compatibility)
 *   - String: Legacy format - companyNumber as string
 *   - Object: New format - { companyNumber?: string, targetUserId?: string }
 *   - Undefined/null: Switch to personal account
 * 
 * @returns Promise that resolves with switch response data
 * 
 * @example
 * // Switch to company (legacy format - still supported)
 * await switchCompany('companyId123')
 * 
 * @example
 * // Switch to company (new format)
 * await switchCompany({ companyNumber: 'companyId123' })
 * 
 * @example
 * // Switch to another user (premium feature)
 * await switchCompany({ targetUserId: 'userId456' })
 * 
 * @example
 * // Switch to personal account
 * await switchCompany() // or switchCompany({ companyNumber: null })
 */
export const switchCompany = async (options?: { companyNumber?: string | null, targetUserId?: string | null } | string | null) => {
	// Handle legacy string parameter for backward compatibility
	let companyNumber: string | null = null;
	let targetUserId: string | null = null;

	if (typeof options === 'string') {
		// Legacy usage: switchCompany(companyNumber)
		companyNumber = options && options.trim() !== '' ? options.trim() : null;
	} else if (options && typeof options === 'object') {
		// New usage: switchCompany({ companyNumber, targetUserId })
		companyNumber = options.companyNumber !== undefined
			? (options.companyNumber && typeof options.companyNumber === 'string' && options.companyNumber.trim() !== ''
				? options.companyNumber.trim()
				: null)
			: null;

		targetUserId = options.targetUserId !== undefined
			? (options.targetUserId && typeof options.targetUserId === 'string' && options.targetUserId.trim() !== ''
				? options.targetUserId.trim()
				: null)
			: null;
	}
	// If options is undefined or null, both remain null (switch to personal account)

	// Determine switch type for logging and error handling
	const switchType = targetUserId ? 'user' : (companyNumber ? 'company' : 'personal');
	console.log(`🔄 [Frontend] Switching context:`, { switchType, companyNumber, targetUserId });

	try {
		const response = await noMonitor.post(`/user/switch-company`, {
			companyNumber: companyNumber,
			targetUserId: targetUserId
		});

		const responseData = response.data;

		// Handle different response types based on what was switched
		if (targetUserId) {
			// User switch response - session userId stays original; targetUserId scopes data
			console.log('✅ [Frontend] User switched successfully:', {
				originalUserId: responseData.userId,
				targetUserId: responseData.targetUserId,
				targetUser: responseData.targetUser
			});
			dispatch({
				type: SWITCH_COMPANY_SUCCESSFUL,
				payload: {
					switchType: 'user',
					originalUserId: responseData.userId,
					targetUserId: responseData.targetUserId,
					targetUser: responseData.targetUser,
				}
			});
		} else if (companyNumber) {
			// Company switch response - user switched to a company
			console.log('✅ [Frontend] Company switched successfully:', {
				companyNumber: responseData.companyNumber,
				companyName: responseData.companyName,
				demo: responseData.demo,
			});
			dispatch({
				type: SWITCH_COMPANY_SUCCESSFUL,
				payload: {
					companyData: responseData,
					switchType: 'company',
					companyNumber: responseData.companyNumber,
					companyName: responseData.companyName,
					demo: !!responseData.demo,
				}
			});
		} else {
			// Personal account switch response - switched back to personal account
			console.log('✅ [Frontend] Switched to personal account');
			dispatch({
				type: SWITCH_COMPANY_SUCCESSFUL,
				payload: {
					companyData: responseData,
					switchType: 'personal',
					companyNumber: null
				}
			});
		}

		const userId = store.getState()?.promiseData?.data?.userData?.id;
		await recordOnlineContextAccess(
			buildSessionScopeId({
				companyId: companyNumber,
				targetUserId,
				userId,
			})
		);

		try {
			const { syncWatchSessionLater } = await import('@services/watchSessionSync');
			syncWatchSessionLater({
				userId: userId ?? null,
				companyId: companyNumber || null,
				companyName: responseData?.companyName || null,
				targetUserId: targetUserId || null,
			});
		} catch {
			/* watch bridge optional */
		}

		return responseData;
	} catch (e) {
		console.error(`❌ [Frontend] Error switching ${switchType}:`, e);
		dispatch({ type: SWITCH_COMPANY_FAILED });
		throw e; // Re-throw to allow caller to handle error
	}
}

export const postAvatar = async (userId, image) => { return await monitoredRequest.post(`/upload/user/${userId}/avatar`, image) }
export const postDeskImage = async (userId, image) => await monitoredRequest.post(`/upload/user/${userId}/deskImage`, image);
export const postLogo = async (companyId, image) => await api.post(`/upload/company/${companyId}`, image);

export const sendUserReg = async (userDetails) => {
	await api.post(`/adduser`, userDetails, { withCredentials: true })
		.then(res => {
			return
		})
}

export const logoutUser = async () => {
	return await logOut.get('/logout').then(() => {
		return true
	}).catch(() => {
		return true
	})
}

export const addData = async (formValues, companyId, userId, dataId) => {
	console.log(formValues);
	await api.post(`/${companyId}/${userId}/post-data/${dataId}`, formValues, { withCredentials: true })
		.then(() => {
			dispatch({ type: PENDING_REFRESH })
		})
}

export const refreshAchievements = async (userId) => {
	return await api.get(`/achievements/${userId}`, { withCredentials: true })
		.then(res => {
			return res.data.data
		})
}

export const getProducts = async () => {
	// Using noCreds instance to bypass auth and CORS
	return await noCreds.get('/get-product-list')
		.then(res => {
			if (res.status != 200) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
			} else {
				dispatch({ type: UPDATE_PRODUCTS, payload: res.data.data })
				return res.data.data
			}
		})
}

export const getProductsInternal = async () => {
	const res = await noMonitor.get('/get-products-list');
	if (res.status !== 200) {
		const msg = res.data?.error || 'Could not load products';
		dispatch({ type: SHOW_AUTH_MODAL, data: { content: msg, type: 'error' } });
		throw new Error(msg);
	}
	// Handle multiple response shapes: { data: [...] }, { success: true, data: [...] }, nested data, or raw array
	const payload = res.data;
	let data: unknown = payload?.data;
	if (!Array.isArray(data) && Array.isArray(payload)) {
		data = payload;
	} else if (!Array.isArray(data) && data != null && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
		data = (data as { data: unknown[] }).data;
	} else if (!Array.isArray(data) && Array.isArray(payload?.products)) {
		data = payload.products;
	}
	const list = Array.isArray(data) ? data : [];
	dispatch({ type: UPDATE_PRODUCTS, payload: list });
	return list;
}

export const assignExemptProduct = async (
	userId: string,
	productId?: string,
	companyNumber?: string | null,
) => {
	const res = await noMonitor.post('/product/assign-exempt-product', {
		userId,
		...(productId ? { productId } : {}),
		...(companyNumber ? { companyNumber } : {}),
	});
	if (res.status !== 200) {
		throw new Error(res.data?.error || 'Failed to assign exempt product');
	}
	if (res.data?.error) {
		throw new Error(res.data.error);
	}
	return res.data as { message?: string };
};

export const getProductById = async (productId: string) => {
	if (!productId) {
		throw new Error('Product ID is required');
	}
	
	// Public pricing endpoint used on Login before authentication.
	return await publicApi.get(`/stripe/plan-product/${productId}`)
		.then(res => {
				return res.data;
		})
		.catch(error => {
			console.error('Error fetching product by ID:', error);
			throw error;
		})
}

export const getSubscriptions = async () => {
	return await noMonitor.get('/payment/get-user-subscription')
		.then(res => {
			if (res.status == 204) {
				return 'No subscriptions active'
			} else {
				return res.data
			}
		})
}

export const mailLogin = async (formValues, id) => {
	await api.post('/mail')
		.then(res => { console.log(res) })
}

export const getStream = async (companyId, userId) => {
	return await api.get(`/${companyId}/${userId}/eventStream`)
}

export const getActivityStreamLog = async (companyId, userId) => {
	return await api.get(`/${companyId}/${userId}/eventLog`)
}

export const getDataTypes = async (id) => {
	return await noMonitor.get(`/user/get-data-count`)
}
export const getCompanySchema = async (companyId) => {
	return await noMonitor.post(`/company/company-schema`, { companyId: companyId })
}

export const retrieveCompanyBillingInfo = async (companyId) => {
	return await api.get(`/billing/company/${companyId}`);
}

export const retrieveUserBillingInfo = async (userId) => {
	return await api.get(`/billing/${userId}`)
}

export const getIntegrations = async () => {
	return await noMonitor.get('/integrations').then(res => { return res.data.data })
}

export const getIntegrationsWithAuth = async () => {
	return await noMonitor.get(`/integrations?t=${new Date().getTime()}`).then(res => { return res.data })
}

export const getIntegrationSuggestions = async () => {
	return await noMonitor.post('/integrations/suggest', {}).then(res => { return res.data })
}

// Integration Request API functions
export const createIntegrationRequest = async (requestData) => {
	return await monitoredRequest.post('/integrations/requests', requestData);
}

export const getIntegrationRequests = async (params = {}) => {
	const queryString = new URLSearchParams(params).toString();
	return await noMonitor.get(`/integrations/requests?${queryString}`).then(res => res.data);
}

export const getPopularIntegrationRequests = async (category = null) => {
	const queryString = category ? `?category=${category}` : '';
	return await noMonitor.get(`/integrations/requests/popular${queryString}`).then(res => res.data);
}

export const getIntegrationRequestStats = async () => {
	return await noMonitor.get('/integrations/requests/stats').then(res => res.data);
}

export const voteOnIntegrationRequest = async (requestId, voteType) => {
	return await monitoredRequest.post(`/integrations/requests/${requestId}/vote`, { voteType });
}

export const updateIntegrationRequestStatus = async (requestId, status, adminNotes = null) => {
	return await monitoredRequest.put(`/integrations/requests/${requestId}/status`, { status, adminNotes });
}

export const addCompany = async (payload) => {
	const res = await monitoredRequest.post(`/add-new-company`, payload);
	return res?.data;
}

export const listLinkedOrganisations = async () => {
	const res = await noMonitor.get(`/company/linked-organisations`);
	return res?.data;
};

export const listLinkedOrganisationPackages = async () => {
	const res = await noMonitor.get(`/company/linked-organisation-packages`);
	return res?.data;
};

export const createLinkedOrganisation = async (payload: {
	company_name: string;
	ownerEmail?: string;
	packageId?: string;
	packageMonths?: number;
	notes?: string;
}) => {
	const res = await monitoredRequest.post(`/company/linked-organisations`, payload);
	return res?.data;
};

export const convertLinkedOrganisation = async (
	childId: string,
	payload?: { billingEmail?: string; productId?: string }
) => {
	const res = await monitoredRequest.post(
		`/company/linked-organisations/${childId}/convert`,
		payload || {}
	);
	return res?.data;
};

export const listLinkedOrganisationGrants = async (childId: string) => {
	const res = await noMonitor.get(`/company/linked-organisations/${childId}/grants`);
	return res?.data;
};

export const grantLinkedOrganisationDatasets = async (
	childId: string,
	payload: {
		datasetIds: string[];
		permissions?: { read?: boolean; write?: boolean };
	}
) => {
	const res = await monitoredRequest.post(
		`/company/linked-organisations/${childId}/grants`,
		payload
	);
	return res?.data;
};

export const revokeLinkedOrganisationGrant = async (childId: string, grantId: string) => {
	const res = await monitoredRequest.delete(
		`/company/linked-organisations/${childId}/grants/${grantId}`
	);
	return res?.data;
};

export const getSponsorshipStatus = async () => {
	const res = await noMonitor.get(`/company/sponsorship-status`);
	return res?.data;
};

export const listSharingScopes = async () => {
	const res = await noMonitor.get(`/company/sharing-scopes`);
	return res?.data;
};

export const createSharingScope = async (payload: {
	name: string;
	description?: string;
	datasetIds?: string[];
}) => {
	const res = await monitoredRequest.post(`/company/sharing-scopes`, payload);
	return res?.data;
};

export const updateSharingScope = async (
	scopeId: string,
	payload: {
		name?: string;
		description?: string;
		datasetIds?: string[];
	}
) => {
	const res = await monitoredRequest.patch(`/company/sharing-scopes/${scopeId}`, payload);
	return res?.data;
};

export const deleteSharingScope = async (scopeId: string) => {
	const res = await monitoredRequest.delete(`/company/sharing-scopes/${scopeId}`);
	return res?.data;
};

export const createSharingScopeDataset = async (payload: {
	dataTitle: string;
	description?: string;
	scopeId?: string;
	dataSchema?: unknown[];
}) => {
	const res = await monitoredRequest.post(`/company/sharing-scopes/create-dataset`, payload);
	return res?.data;
};

export const migrateDemoSnapshot = async (payload: {
	sourceDemoCompanyId: string;
	targetCompanyId: string;
	manifest: Record<string, unknown>;
	idempotencyKey: string;
}) => {
	const res = await monitoredRequest.post('/company/migrate-demo-snapshot', payload);
	return res?.data;
}

export const setDefaultCompany = async (companyId: string | null) => {
	const res = await monitoredRequest.put(`/user/default-company`, { companyId });
	return res?.data;
}

export const getCalendarSchema = async () => {
	return await noMonitor.get('/calendar/get-calendar-schema')
		.then(res => {
			if (res.status != 203) {
				return dispatch({
					type: SHOW_AUTH_MODAL,
					data: { content: 'Could not retrieve schema', type: 'error' },
				});
			} else {
				return res.data.data;
			}
		})
		.catch((e) => {
			dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content:
						(e as Error)?.message || 'Could not retrieve calendar schema',
					type: 'error',
					details: serializeForReduxDetails(e),
				},
			});
		})
}

export const getProjects = async () => {
	return await noMonitor.get(`/projects/get-projects`)
}

export const getProjectsPaginated = async (options: {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}) => {
	return await monitorFail.get(`/projects/get-projects-paginated`, {
		params: {
			page: options.page || 1,
			limit: options.limit || 12,
			sortBy: options.sortBy || 'updatedAt',
			sortOrder: options.sortOrder || 'desc'
		}
	});
}

export const addProjects = async (project, id) => {
	const dateRanges = project.date_range;
	const newProj = { ...project, start_date: dateRanges.startDate, end_date: dateRanges.endDate }
	return await monitorFail.post('/projects/add-project', { userId: id, project: newProj }).then(() => { return { success: true } })
}

export const addLocalCalendars = async (calendars) => {
	return await noMonitor.post('/user/set-local-calendars', calendars)
}

export const getCalendarContent = async (date) => {
	return await noMonitor.post('/calendar/get-calendar', { date: date })
}

export const addCalendarEntry = async (calendarId, id) => {
	if (!calendarId) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please ensure you have selected a calendar first', type: 'error' } })
	}

	// If no calendarId is selected, try to find or create a default 'smartsaas' calendar
	if (!calendarId.calendarId) {
		try {
			// 1. Check existing calendars
			const calendarsRes = await getCalendars();
			const calendars = calendarsRes?.data || [];

			// 2. Look for 'smartsaas' calendar (by name or title)
			let defaultCal = calendars.find(c =>
				c.name?.toLowerCase() === 'smartsaas' ||
				c.title?.toLowerCase() === 'smartsaas'
			);

			if (!defaultCal) {
				// 3. Create 'smartsaas' calendar if it doesn't exist
				const createRes = await addSSCal({
					title: 'smartsaas',
					name: 'smartsaas',
					color: '#007AFF',
					type: 'smartsaas'
				});

				if (createRes.data?.success && createRes.data?.data) {
					defaultCal = createRes.data.data;
				}
			}

			if (defaultCal) {
				calendarId.calendarId = defaultCal.calendarId || defaultCal._id;
			}
		} catch (error) {
			console.error('Error ensuring default calendar:', error);
			// We continue; if calendarId is still missing, the backend will return an error
		}
	}

	return await monitoredRequest.post('/calendar/add-calendar-entry', { userId: id, calendar: calendarId }).then(() => { return { success: true } })
}

export const updateCalendarEntry = async (entryId: string, calendar: any) => {
	if (!entryId || !calendar) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Missing required fields for updating calendar entry', type: 'error' } })
	}
	return await monitoredRequest.put('/calendar/update-calendar-entry', { entryId, calendar })
}

export const removeCalendarEntry = async (entryId: string) => {
	if (!entryId) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Missing entry ID for deletion', type: 'error' } })
	}
	return await monitoredRequest.post('/calendar/remove-calendar-entry', { entryId })
}

export const updateProject = async (project) => {
	if (!project) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'No project data in request ', type: 'error ' } })
	}
}

export const getProjectSchema = async () => {
	return await noMonitor.get('/projects/get-project-schema')
		.then(res => {
			return res.data.data;
		})
}

export const getAddNetworkSchema = async () => {
	return await noMonitor.get('/user/get-networking-schema')
		.then(res => {
			return res.data.data;
		})
}

export const changeWPStatus = async (id, status) => {
	return await api.post('/projects/change-wp-status', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, status: status })
		.then(res => {
			if (!res) {
				return dispatch({
					type: SHOW_AUTH_MODAL,
					data: { content: 'No project data in request ', type: 'error' },
				});
			} else return { success: true }
		})
		.catch((e) => {
			dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: (e as Error)?.message || 'Request failed',
					type: 'error',
					details: serializeForReduxDetails(e),
				},
			});
		})
}

export const changeWorkPackageTitle = async (wpId, projectId, title) => {
	return await noMonitor.post('/projects/change-wp-title', { projectId, wpId, title })
}


export const changeTaskTitle = async (id, title) => {
	return await noMonitor.post('/projects/change-task-title', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, taskId: id.taskId, title: title })
}

export const changeProjectTitle = async (projectId, value) => {
	return await noMonitor.post('/projects/change-project-title', { projectId: projectId, title: value })
}

export const changeProjectStatus = async (projectId, status) => {
	return await noMonitor.post('/projects/change-project-status', { projectId: projectId, status: status })
}

export const changeProjectDescription = async (projectId, value) => {
	return await noMonitor.post('/projects/change-project-description', { projectId: projectId, description: value })
}

export const changeTaskDescription = async (id, description) => {
	return await noMonitor.post('/projects/change-task-description', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, taskId: id.taskId, description: description })
		.then(res => {
			if (!res) {
				return dispatch({
					type: SHOW_AUTH_MODAL,
					data: { content: 'No project data in request ', type: 'error' },
				});
			} else return { success: true }
		})
		.catch((e) => {
			dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: (e as Error)?.message || 'Request failed',
					type: 'error',
					details: serializeForReduxDetails(e),
				},
			});
		})
}

export const deleteTask = async (id) => {
	// Backend expects: { projectId, workPackageId, taskId }
	return await noMonitor.post(`/projects/delete-task`, {
		projectId: id.projectId || id.dataId,
		workPackageId: id.workPackageId || id.extraId,
		taskId: id.taskId || id._id
	})
}

export const duplicateTask = async (id) => {
	return await noMonitor.post(`/projects/duplicate-task`, id)
}

export const duplicateEmailTemplate = async (id) => {
	return await monitorFail.post(`/templates/email/duplicate-template`, id)
}

export const duplicateChildTemplate = async (id: string, title: string) => {
	return await monitorFail.post(`/templates/email/duplicate-child-template`, { id, title })
}

export const saveEmailTemplateCopy = async (template) => {
	return await monitorFail.post(`/templates/email/save-template-copy`, template)
}

export const changeTaskStatus = async (id, status) => {
	// Backend expects: { id: { projectId, workPackageId, taskId }, status }
	return await monitorFail.post('/projects/change-task-status', { 
		id: {
			projectId: id.dataId,
			workPackageId: id.extraId, 
			taskId: id._id
		},
		status
	})
}

// Enhanced kanban drag and drop APIs
export const moveTask = async (projectId, taskId, moveData) => {
	const { sourceStatus, destinationStatus, sourceIndex, destinationIndex, workPackageId } = moveData;
	return await monitorFail.post(`/projects/${projectId}/tasks/${taskId}/move`, {
		sourceStatus,
		destinationStatus,
		sourceIndex,
		destinationIndex,
		workPackageId
	});
}

export const bulkMoveTasks = async (projectId, tasks) => {
	return await api.post(`/projects/${projectId}/tasks/bulk-move`, {
		tasks
	});
}

export const debugProjectData = async (projectId, userId) => {
	return await noMonitor.get(`/projects/${userId}/debug/${projectId}`);
}

export const changeTaskNotes = async (id, notes) => {
	return await api.post('/projects/change-task-notes', { id, notes })
}

export const deleteWorkPackage = async (id) => {
	return await monitoredRequest.post(`/projects/delete-wp`, id)
}

export const addTask = async (taskData, id) => {
	const { dateRange, ...rest } = taskData;
	rest.start_date = dateRange?.startDate;
	rest.end_date = dateRange?.endDate;

	const newTask = {
		...rest,
		dateRange: undefined
	}

	const userId = typeof id === 'object' ? id?.globalId : undefined;
	const projectId = typeof id === 'object' ? id?.dataId : undefined;
	const workPackageId = typeof id === 'object' ? id?.extraId : undefined;

	return await api.post('/projects/add-task', { userId, projectId, workPackageId, task: newTask })
}

export const addWorkPackage = async (workPackage, id) => {
	const { dateRange, ...rest } = workPackage;
	rest.start_date = dateRange?.startDate || rest.start_date;
	rest.end_date = dateRange?.endDate || rest.end_date;

	const userId = typeof id === 'object' ? id?.globalId : undefined;
	const projectId = typeof id === 'object' ? (id?.dataId || id?._id) : id;

	return await monitorFail.post('/projects/add-workpackage', { userId, projectId, workPackage: rest })
		.then(res => {
			return { data: { _id: res.data.data, ...rest, start_date: rest.start_date || new Date().toISOString(), end_date: rest.end_date || new Date().toISOString(), task_list: [], team: [] } }
		})
}

export const getTaskSchema = async () => {
	return await noMonitor.get('/projects/get-task-schema')
		.then(res => {
			if (!res) {
				return dispatch({
					type: SHOW_AUTH_MODAL,
					data: { content: 'No project data in request ', type: 'error' },
				});
			} else {
				return res.data.data;
			}
		})
		.catch((e) => {
			dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: (e as Error)?.message || 'Request failed',
					type: 'error',
					details: serializeForReduxDetails(e),
				},
			});
		})
}

export const getWPSchema = async () => {
	return await noMonitor.get('/projects/get-workpackage-schema')
		.then(res => {
			return res.data.data;
		})
}

export const dispatchAppAction = async (action) => {
	//TODO Destructure userId and determine if user is logged into a company, then it would be best to assign company values and user values independently, i.e. increment user count individually to company account. Hide details of the action from the user and instad use vague metrics to give them more useful information

	let payload = { actionType: action.type, payload: action.payload, appVersion: Constants.expoConfig?.version }

	await noMonitor.put(`/userAppHistory/${'uhkj'}`, payload)
		.then((res) => {
			if (!res) {
				//TODO add to local storage until connection is established
				return;
			}
			dispatch({ type: UPDATE_ACHIEVEMENTS, payload: res.data.points })
		}).catch(e => {
			console.error(e)
		});
	return;
}

export const getLoginMessages = async () => {
	return await noMonitor.get(`/login-messages`).then(res => {
		if (res.status != 200) {
			console.error(res)
		}
		return res.data.data
	})
}

export const getData = async (userId) => {
	return await noMonitor.post(`/data/get-data`, { id: userId }).then(res => {
		return res.data.data
	})
}

type DataFolderType = {
	userId: string,
	dataFilter?: string
}

export const getDataFolders = async ({ userId, dataFilter }: DataFolderType) => {
	try {
		const res = await noMonitor.post(`/data/get-data-folders`, { id: userId, dataFilter }).then(res => res.data);
		// Handle the nested response structure
		if (res && res.data && Array.isArray(res.data)) {
			console.log('🔍 Found data array with', res.data.length, 'items');
			return res.data;
		} else if (res && Array.isArray(res)) {
			console.log('🔍 Found direct array with', res.length, 'items');
			return res;
		} else {
			return [];
		}
	} catch (error: any) {
		// Interceptor may still reject true mutation duplicates; ignore those quietly.
		if (!(error?.isDuplicate || error?.message === 'Duplicate request prevented')) {
			console.error("Error fetching data folders:", error);
		}
		return [];
	}
};

export const getFolderStats = async (folderId: string, month?: string | Date) => {
	return await noMonitor.post(`/data/get-folder-stats`, { id: folderId, month }).then(res => {
		return res.data
	})
}

export const getAllFolderStats = async () => {
	return await noMonitor.get(`/user/get-all-folder-stats`).then(res => {
		return res.data.data
	})
}

// POI (Points of Interest) lead generation
export const addPOIsToDataModel = async (
	selectedPOIs: any[],
	dataModelId: string | null,
	createNew: boolean,
	modelName?: string,
	modelTags?: string[]
) => {
	try {
		const res = await api.post('/data/add-pois-to-model', {
			selectedPOIs,
			dataModelId,
			createNew,
			modelName,
			modelTags
		});
		return res.data;
	} catch (error) {
		console.error('Error adding POIs to data model:', error);
		throw error;
	}
};

// Article scraping for knowledge base
export const scrapeArticleToKnowledge = async (
	url: string,
	title?: string,
	type: 'internal' | 'external' = 'internal'
) => {
	try {
		console.log('📤 [API] Sending scrape article request:', { url, title, type });
		const res = await api.post('/ai/scrape-article', {
			url,
			title,
			type
		}, { timeout: 120000 });
		console.log('✅ [API] Scrape article response received:', res.data);
		return res.data;
	} catch (error) {
		console.error('❌ [API] Error scraping article:', error);
		console.error('❌ [API] Error response:', error.response?.data);
		throw error;
	}
};

// Advanced data analytics
export const keywordLookup = async (params: {
	itemId: string;
	dataSetId?: string;
	dataSetIds?: string[];
	dataTags?: string | string[];
	keywords: string | string[];
	includeParticipation?: boolean;
	inferOpposite?: boolean;
	limit?: number;
}) => {
	try {
		const res = await noMonitor.post('/data/keyword-lookup', params);
		return res.data;
	} catch (error) {
		console.error('Error running keyword lookup:', error);
		return { success: false, data: null };
	}
};

export const getDataInsights = async (params: {
	timeRange?: '7d' | '30d' | '90d' | '1y';
	groupBy?: 'hour' | 'day' | 'week' | 'month' | 'year';
	includeMetrics?: 'all' | 'basic';
}) => {
	try {
		const res = await noMonitor.post('/data/insights', params);
		return res.data;
	} catch (error) {
		console.error('Error fetching data insights:', error);
		return { success: false, data: null };
	}
};

export const getFolderComparison = async (folderIds: string[], metrics: string[] = ['items', 'views', 'interactions']) => {
	try {
		const res = await noMonitor.post('/data/folder-comparison', { folderIds, metrics });
		return res.data;
	} catch (error) {
		console.error('Error fetching folder comparison:', error);
		return { success: false, data: [] };
	}
};

export const getPredictiveAnalytics = async (analysisType: 'growth' | 'usage' = 'growth', period: '30d' | '90d' | '1y' = '30d') => {
	try {
		const res = await noMonitor.post('/data/predictive-analytics', { analysisType, period });
		return res.data;
	} catch (error) {
		console.error('Error fetching predictive analytics:', error);
		return { success: false, data: null };
	}
};

export const getProductList = async (userId, mode: 'live' | 'draft' = 'live') => {
	const queryString = mode ? `?mode=${mode}` : '';
	return await api.get(`/get-product-list/${userId}${queryString}`, { withCredentials: true }).then(res => {
		if (res.status != 200) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
			return;
		}
		return res.data.data
	})
}

export const getSampleSchemas = async () => {
	return await noMonitor.get('/get-schemas', { withCredentials: true }).then(res => {
		return res.data.data
	})
}

// Database Connection API Methods
export const createDatabaseConnection = async (connectionData) => {
	return await monitoredRequest.post('/database-connections', connectionData)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			throw new Error(res.data.error || 'Failed to create database connection');
		})
		.catch(error => {
			console.error('Error creating database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to create database connection', type: 'error' } });
			throw error;
		});
}

export const getDatabaseConnections = async (scope) => {
	const params = scope ? { scope } : {};
	return await monitoredRequest.get('/database-connections', { params })
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			return [];
		})
		.catch(error => {
			console.error('Error fetching database connections:', error);
			return [];
		});
}

export const getDatabaseConnection = async (id) => {
	return await monitoredRequest.get(`/database-connections/${id}`)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			throw new Error(res.data.error || 'Database connection not found');
		})
		.catch(error => {
			console.error('Error fetching database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to fetch database connection', type: 'error' } });
			throw error;
		});
}

export const updateDatabaseConnection = async (id, connectionData) => {
	return await monitoredRequest.put(`/database-connections/${id}`, connectionData)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			throw new Error(res.data.error || 'Failed to update database connection');
		})
		.catch(error => {
			console.error('Error updating database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to update database connection', type: 'error' } });
			throw error;
		});
}

export const deleteDatabaseConnection = async (id) => {
	return await monitoredRequest.delete(`/database-connections/${id}`)
		.then(res => {
			if (res.data.success) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || 'Database connection deleted successfully', type: 'success' } });
				return true;
			}
			throw new Error(res.data.error || 'Failed to delete database connection');
		})
		.catch(error => {
			console.error('Error deleting database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to delete database connection', type: 'error' } });
			throw error;
		});
}

export const testDatabaseConnection = async (id) => {
	return await monitoredRequest.post(`/database-connections/${id}/test`)
		.then(res => {
			if (res.data.success) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || 'Connection test successful', type: 'success' } });
				return res.data.data;
			}
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || 'Connection test failed', type: 'error' } });
			return res.data;
		})
		.catch(error => {
			console.error('Error testing database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Connection test failed', type: 'error' } });
			throw error;
		});
}

export const getDatabaseTables = async (connectionId) => {
	return await monitoredRequest.get(`/database-connections/${connectionId}/tables`)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			return [];
		})
		.catch(error => {
			console.error('Error fetching database tables:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to fetch database tables', type: 'error' } });
			return [];
		});
}

export const importDatabaseSchema = async (connectionId, tableNames, tags) => {
	return await monitoredRequest.post(`/database-connections/${connectionId}/import-schema`, {
		tableNames,
		tags
	})
		.then(res => {
			if (res.data.success) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || `Successfully imported ${res.data.data?.length || 0} table schema(s)`, type: 'success' } });
				return res.data.data;
			}
			throw new Error(res.data.error || 'Failed to import database schema');
		})
		.catch(error => {
			console.error('Error importing database schema:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to import database schema', type: 'error' } });
			throw error;
		});
}

export const saveData = async (payload) => {
	const { id } = payload;
	const res = await monitoredRequest.post(`/data/add-new-data/${id}`, payload);
	const data = res?.data;
	return data != null ? data : { success: true };
}

/**
 * Fetch a live source once (URL or market quotes) and return detected columns + sample rows.
 * Pass `dataId` when testing an existing dataset so blank credentials reuse the saved ones.
 */
export const previewLiveSource = async (liveSource, dataId?: string) => {
	const res = await monitorFail.post(`/data/live-source/preview`, { liveSource, ...(dataId ? { dataId } : {}) }, { timeout: 30000 });
	return res?.data;
}

export const syncLiveDataset = async (dataId: string) => {
	const res = await monitorFail.post(`/data/live-source/${dataId}/sync`, {}, { timeout: 30000 });
	return res?.data;
}

/** Patch refresh settings, pause (`enabled: false`) or detach (`remove: true`). */
export const updateLiveSource = async (dataId: string, patch) => {
	const res = await monitorFail.patch(`/data/live-source/${dataId}`, patch);
	return res?.data;
}

/** Numeric column over time, one series per entity (symbol / key column). */
export const getLiveTimeline = async (dataId: string, field?: string) => {
	const res = await monitorFail.get(`/data/live-source/${dataId}/timeline`, { params: field ? { field } : undefined });
	return res?.data;
}

export const uploadData = async (payload, onProgress?: (data: any) => void) => {
	const { id } = payload;
	
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Preparing data upload...',
			progress: 0
		});

		// Use fetch with SSE support for progress tracking
		fetch(`${USER_API_BASE_URL}/data/upload-new-data/${id}`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(payload)
		}).then(response => {
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// React Native doesn't support response.body.getReader(), so fallback to text()
			const reader = response.body?.getReader?.();
			if (!reader || Platform.OS !== 'web') {
				// For React Native, read entire response as text and process SSE manually
				return response.text().then(text => {
					function processLine(line) {
						line = line.trim();
						if (line.startsWith('data: ')) {
							try {
								const jsonData = line.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									
									// Update progress in modal
									dispatchSSEProgress(data);
									
									// Call optional progress callback
									if (onProgress) {
										onProgress(data);
									}
								}
							} catch (e) {
								console.warn('Failed to parse SSE data:', line);
							}
						}
					}

					const lines = text.split('\n');
					lines.forEach(processLine);

					dispatchSSEProgress({
						status: 'completed',
						message: 'Data upload completed successfully!',
						progress: 100
					});

					// Hide modal after a brief delay
					setTimeout(() => {
						const { dispatch } = store;
						dispatch({ type: HIDE_ALL_MODALS });
					}, 1500);

					resolve({ success: true });
				});
			}

			const decoder = new TextDecoder();
			let buffer = '';

			const processStream = () => {
				return reader.read().then(({ done, value }) => {
					if (done) {
						dispatchSSEProgress({
							status: 'completed',
							message: 'Data upload completed successfully!',
							progress: 100
						});

						// Hide modal after a brief delay
						setTimeout(() => {
							const { dispatch } = store;
							dispatch({ type: HIDE_ALL_MODALS });
						}, 1500);

						resolve({ success: true });
						return;
					}

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split('\n');
					buffer = lines.pop() || ''; // Keep incomplete line in buffer

					lines.forEach(line => {
						if (line.trim() && line.startsWith('data: ')) {
							try {
								const data = JSON.parse(line.slice(6));
								
								// Update progress in modal
								dispatchSSEProgress(data);
								
								// Call optional progress callback
								if (onProgress) {
									onProgress(data);
								}
							} catch (e) {
								console.warn('Failed to parse SSE data:', line);
							}
						}
					});

					return processStream();
				});
			};

			return processStream();
		}).catch(error => {
			console.error('Upload error:', error);
			
			// Show error in modal
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'Upload failed',
				progress: 0
			});

			// Hide modal after error display
			setTimeout(() => {
				const { dispatch } = store;
				dispatch({ type: HIDE_ALL_MODALS });
			}, 3000);

			reject(error);
		});
	});
}

export const getDataContent = async (dataId: string, type: string) => {
	return await monitorFail.get(`/data/get-data-content/${dataId}?type=${type}`)
}

export const getDataContentPaginated = async (dataId: string, options: {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}) => {
	console.log("getting paginated data content for dataId:", dataId, "with options:", options);
	return await monitorFail.post(`/data/get-data-content-paginated`, {
		id: dataId,
		page: options.page || 1,
		limit: options.limit || 10,
		sortBy: options.sortBy || 'updatedAt',
		sortOrder: options.sortOrder || 'desc'
	});
}

export const getDataFoldersPaginated = async (userId: string, options: {
	page: number;
	/** Preferred page size key (matches DataPaginationState). */
	pageSize?: number;
	/** Alias used by some callers / older code. */
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	dataFilter?: string;
}) => {
	const pageSize = options.pageSize ?? options.limit ?? 10;
	console.log("getting paginated data folders for userId:", userId, "with options:", { ...options, pageSize });
	const response = await monitorFail.post(`/data/get-data-folders-paginated`, {
		id: userId,
		page: options.page,
		limit: pageSize,
		sortBy: options.sortBy || 'updatedAt',
		sortOrder: options.sortOrder || 'desc',
		dataFilter: options.dataFilter
	});
	return response.data;
}
export const confirmAiTool = async (token: string, selection?: Record<string, unknown>) => {
	const res = await noMonitor.post('/ai/confirm-tool', selection ? { token, selection } : { token });
	return res.data;
}

export const getReminders = async (params: { status?: string; limit?: number } = {}) => {
	const res = await noMonitor.get('/reminders', { params });
	return res.data;
}

export const createReminder = async (payload: {
	text: string;
	remindAt?: string;
	inMinutes?: number;
	channel?: string;
	timezone?: string;
}) => {
	const res = await noMonitor.post('/reminders', payload);
	return res.data;
}

export const cancelReminder = async (reminderId: string) => {
	const res = await noMonitor.post('/reminders/cancel', { reminderId });
	return res.data;
}

export const getAgentActivity = async (params: { limit?: number; before?: string; status?: string } = {}) => {
	const res = await noMonitor.get('/agents/activity', { params });
	return res.data;
}

export const dismissAgentActivity = async (token: string) => {
	const res = await noMonitor.post('/agents/activity/dismiss', { token });
	return res.data;
}

export const undoAgentActivity = async (id: string) => {
	const res = await noMonitor.post('/agents/activity/undo', { id });
	return res.data;
}

export const getAssistantSuggestions = async () => {
	const res = await noMonitor.get('/assistant/suggestions');
	return res.data;
}

export const resolveAssistantSuggestion = async (id: string, action: 'accept' | 'dismiss') => {
	const res = await noMonitor.post(`/assistant/suggestions/${encodeURIComponent(id)}/${action}`);
	return res.data;
}

export const addDataItem = async (payload, ids) => {
	const { globalId, dataId } = ids;
	return await noMonitor.post(`/data/add-data-content/${globalId}/${dataId}`, payload).then(res => {
		return res
	})
}

export const getDataSchema = async (userId, dataId) => {
	return await noMonitor.get(`/data/get-data-schema/${userId}/${dataId}`)
}

export const getProfileSchema = async () => {
	return await noMonitor.get('/user/get-profile-schema')
}

export const getUserSocialMedia = async (targetUserId = null) => {
	const endpoint = targetUserId
		? `/user/social-media/${targetUserId}`
		: '/user/social-media';
	return await noMonitor.get(endpoint);
}

export const updateUserSocialMedia = async (socialMediaData) => {
	return await noMonitor.post('/user/social-media/update', { socialMedia: socialMediaData });
}

// Custom Views API functions
export const getCustomViews = async (dataId) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.get(`/custom-views?dataId=${dataId}`);
}

export const getCustomView = async (viewId, dataId) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.get(`/custom-views/${viewId}?dataId=${dataId}`);
}

export const saveCustomView = async (viewData) => {
	if (!viewData.dataId) {
		throw new Error('dataId is required in viewData');
	}
	return await noMonitor.post('/custom-views', viewData);
}

export const deleteCustomView = async (viewId, dataId) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.delete(`/custom-views/${viewId}?dataId=${dataId}`);
}

export const duplicateCustomView = async (viewId, dataId, newName = null) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.post(`/custom-views/${viewId}/duplicate`, { newName, dataId });
}

export const getCustomViewTemplates = async () => {
	return await noMonitor.get('/custom-views/templates');
}

// AI Project Creation
export const createAIProject = async (projectData) => {
	return await noMonitor.post('/ai/create-project', projectData, { timeout: 120000 });
}

export const generateAIProjectFromDescription = async (projectDescription: string, options: {
	industry?: string;
	projectType?: string;
	timeline?: string;
	teamSize?: string;
} = {}) => {
	return await noMonitor.post('/ai/create-project', {
		projectDescription,
		industry: options.industry || 'general',
		projectType: options.projectType || 'standard',
		timeline: options.timeline || 'medium',
		teamSize: options.teamSize || 'small'
	}, { timeout: 120000 });
}

export const getAIProjectTemplates = async () => {
	return await noMonitor.get('/ai/project-templates', { timeout: 120000 });
}

export const getAddInvoiceTemplateSchema = async () => {
	return await noMonitor.get('/templates/invoices/add-template-schema');
}

export const addTagsToData = async (dataId, tag) => {
	return await noMonitor.post('/data/add-tag', { dataId: dataId, tags: tag })
}

export const searchUsers = async (text) => {
	return await noMonitor.post('/search-users', { text: text }).then(res => { return res.data.records })
}

export const getReport = async (dataId) => {
	return await api.post('/data/get-report-data', { dataId: dataId })
}

export const getCatalogDeleteBlocks = async (ids: string[]) => {
	return await monitorFail.post('/data/catalog-delete-check', { ids }).then((res) => res.data);
};

export const deleteDataItem = async (
	dataId,
	options?: { expectedUpdatedAt?: string; force?: boolean }
) => {
	const params: Record<string, string> = {};
	if (options?.expectedUpdatedAt) {
		params.expectedUpdatedAt = options.expectedUpdatedAt;
	}
	if (options?.force) {
		params.force = 'true';
	}
	return await monitorFail.delete(`/data/delete-item/${dataId}`, {
		params,
		headers: options?.expectedUpdatedAt
			? { 'x-expected-updated-at': options.expectedUpdatedAt }
			: undefined,
	});
}


export const deleteData = async (dataId) => {
	console.log('deleting data', dataId)
	return await noMonitor.delete(`/data/delete-data/${dataId}`)
}

export const getDeletedItems = async (itemType?: 'data' | 'project' | 'calendar') => {
	return await noMonitor.get('/recovery/deleted-items', {
		params: itemType ? { itemType } : undefined,
	}).then((res) => res.data);
}

export const restoreDeletedItem = async (archiveId: string) => {
	return await noMonitor.post(`/recovery/restore/${archiveId}`).then((res) => res.data);
}

export const purgeDeletedItem = async (archiveId: string) => {
	return await noMonitor.delete(`/recovery/purge/${archiveId}`);
}

export const moveDataItem = async (dataId, folderId) => {
	return await noMonitor.patch(`/data/move-item/${dataId}`, { targetId: folderId })
}

export const deleteProject = async (projectId) => {
	return await monitoredRequest.delete(`/projects/delete-project/default/${projectId}`)
}

export const getProfileCompletion = async (globalId) => {
	return await noMonitor.get(`/user/profile-calculator`)
}

export const getProjectCount = async (globalId) => {
	return await noMonitor.get(`/${globalId}/get-project-count`)
}

export const getDataCount = async () => {
	return await noMonitor.get(`/user/get-data-count`)
}

export const saveNewsItem = async (globalId, news) => {
	return await noMonitor.post(`/${globalId}/post-saved-news`, news)
}

export const getSavedNews = async (globalId) => {
	return await noMonitor.get(`/${globalId}/get-saved-news`);
}

export const getProject = async (projectId, globalId) => {
	return await noMonitor.get(`/projects/${globalId}/get-project/${projectId}`)
}

export const addCalendar = async (globalId, calendar) => {
	return await monitoredRequest.post(`/calendar/${globalId}/add-calendar`, calendar)
}

export const addStickyNote = async (note) => {
	return await noMonitor.post(`/notes/post-sticky-note`, note)
}

export const pinNote = async (noteId) => {
	return await noMonitor.put(`/notes/pin-note/${noteId}`)
}

export const unPinNote = async (noteId) => {
	return await noMonitor.put(`/notes/unpin-note/${noteId}`)
}

export const modifyStickyNote = async (note) => {
	return await noMonitor.post(`/notes/modify-sticky-note/${note._id}`, note)
}

export const removeStickyNote = async (globalId, noteId) => {
	return await noMonitor.delete(`/notes/remove-sticky-note/${noteId}`)
}

export const getStickyNotes = async () => {
	return await noMonitor.get(`/notes/get-sticky-notes`).then(res => {
		return res.data.data.sticky_notes;
	})
}

export const getCompanyPinnedNotes = async () => {
	return await noMonitor.get(`/notes/company-pinned`).then(res => res.data.data);
}

export const pinNoteCompanyWide = async (noteId) => {
	return await noMonitor.put(`/notes/company-pin/${noteId}`).then(res => res.data.data);
}

export const unpinCompanyNote = async (pinId) => {
	return await noMonitor.delete(`/notes/company-pin/${pinId}`);
}

export const markNoteComplete = async (globalId, note) => {
	return await noMonitor.post(`/notes/mark-note-complete/${note._id}`, note)
}

export const setNoteColor = async (globalId, note) => {
	return await noMonitor.post(`/notes/change-note-color/${note._id || note.id}`, note)
}

export const getInitialModels = async (req) => {
	return await noMonitor.post(`/get-initial-models`, req);
}

export const identifyOnboardingModels = async (businessDescription, tags = [], userId, companyId) => {
	return await noMonitor.post(`/ai/onboarding/identify-models`, {
		businessDescription,
		tags,
		userId,
		companyId
	});
}

/** Signed-out guest preview: one cheap blueprint (datasets, project, templates, features) for a business idea. */
export const createPreviewBlueprint = async (prompt: string, captchaToken?: string | null) => {
	return await publicApi.post(`/preview/blueprint`, {
		prompt,
		...(captchaToken ? { captchaToken } : {}),
	});
};

/** After sign-up: copy the guest preview into the signed-in account (idempotent per user). */
export const claimPreview = async (previewId: string) => {
	return await noMonitor.post(`/preview/claim`, { previewId });
};

/** Onboarding: best-guess product rows from company website (backend → ML) */
export const inferOnboardingProductCatalog = async (websiteUrl: string, companyName = '') => {
	return await noMonitor.post(`/ai/onboarding/infer-product-catalog`, {
		websiteUrl,
		companyName,
	});
};

/** Onboarding: scrape site + create dataset (user_datas) and rows (data_items) for products/services */
export const bootstrapOnboardingProductCatalog = async (websiteUrl: string, companyName = '') => {
	return await noMonitor.post(`/ai/onboarding/bootstrap-product-catalog`, {
		websiteUrl,
		companyName,
	});
};

export const manipulateHierarchy = async (query, hierarchy, dataSchema = null) => {
	return await noMonitor.post(`/ai/hierarchy/manipulate`, {
		query,
		hierarchy,
		dataSchema
	});
};

/** AI-suggested automation flows (uses injectAIContext: user, company, authorised integrations, schema). */
export const suggestAutomationFlow = async (body: {
	description: string;
	dataSchemaId?: string;
	dataSchema?: unknown;
	mode?: string;
	context?: unknown;
	contextSelections?: unknown;
}) => {
	return await noMonitor
		.post('/ai/flow/suggest-automation', body, { timeout: 120000 })
		.then((res) => res.data);
};

export const postInitialModel = async (modelList, userId) => {
	return await noMonitor.post(`/data/${userId}/add-initial-data`, modelList);
}

export const getStandardisedReports = async (userId, previousList) => {
	return await noMonitor.post(`/data/post-standardised-reports/${userId}`, previousList)
}

export const validate2FA = async (userId, token) => {
	return await monitoredRequest.post(`/security/${userId}/validate-two-fa`, { token: token })

}

export const validateOTP = async (storedCred) => {
	return await monitoredRequest.post(`/validate-otp`, storedCred)
}

export const setTutorial = async () => {
	return await noMonitor.get(`/user/set-tutorial-modal`)
}

export const setPublicDataSwipeTutorial = async () => {
	return await noMonitor.get(`/user/set-public-swipe`)
}
export const setInvitationSwipeTutorial = async () => {
	return await noMonitor.get('/user/set-invite-swipe')
}
export const getAvatarById = async (id) => {
	return await noMonitor.get(`/get-avatar-image/${id}`).then(res => { return res.data.data.profile_information.avatar.img })
}

export const getRoomCounts = async () => {
	return await noMonitor.get('/chat-rooms')
}
export const getRoom = async (room) => {
	return await noMonitor.get(`/networking-room/${room}`)
}

export const updateTC = async (userId, tcVersion) => {
	return await noMonitor.post('/user/terms/update', { user: userId, version: tcVersion })
}

export const getActivityLog = async (userId) => {
	return await noMonitor.get(`/activity-log/${userId}`);
}

export const addLocation = async (location) => {
	return await noMonitor.post('/company/add-networking', location)
}

export const getAchievementHistory = async () => {
	return await noMonitor.get(`/achievements-history`).then(res => { return res.data.data })
}

export const shareDataItem = async (body) => {
	return await monitoredRequest.post(`/share/data/invite`, body).then(res => res.data.data)
}

export const shareProject = async (body) => {
	return await monitoredRequest.post(`/share/project/invite`, body).then(res => res.data.data)
};

export const acceptCompanyInvitation = async (invitationId) => {
	return await monitoredRequest.post(`/company/accept-invitation`, invitationId).then(res => res.data.data)
}

export const addUserToCompany = async (data) => {
	return await monitoredRequest.post('/users/invite-user', data).then(res => res.data)
}

export const getUser = async (userId) => {
	return await noMonitor.get(`/user/${userId}/get-small-profile`).then(res => res.data.data)
}

export const getUserById = async (userId) => {
	return await noMonitor.get(`/users/getUser/${userId}`).then(res => res.data).catch(e => console.error(e))
}

export const getPublicProfile = async (userId) => {
	return await noMonitor.get(`/users/public-profile/${userId}`).then(res => res.data).catch(e => console.error(e))
}

// Engagement tracking methods
export const trackFormView = async (formId, source = 'unknown') => {
	try {
		return await noMonitor.post('/engagement/track-form-view', { formId, source }).then(res => res.data).catch(e => {
			console.error('Error tracking form view:', e);
			return { success: false };
		});
	} catch (error) {
		console.error('Error tracking form view:', error);
		return { success: false };
	}
}

export const trackFormSubmission = async (formId, source = 'unknown', completed = false) => {
	try {
		return await noMonitor.post('/engagement/track-form-submission', { formId, source, completed }).then(res => res.data).catch(e => {
			console.error('Error tracking form submission:', e);
			return { success: false };
		});
	} catch (error) {
		console.error('Error tracking form submission:', error);
		return { success: false };
	}
}

export const trackDataContribution = async (dataId, actionType = 'create') => {
	try {
		return await noMonitor.post('/engagement/track-data-contribution', { dataId, actionType }).then(res => res.data).catch(e => {
			console.error('Error tracking data contribution:', e);
			return { success: false };
		});
	} catch (error) {
		console.error('Error tracking data contribution:', error);
		return { success: false };
	}
}

export const trackDataInteraction = async (dataId, interactionType, targetUserId = null) => {
	try {
		return await noMonitor.post('/engagement/track-data-interaction', { dataId, interactionType, targetUserId }).then(res => res.data).catch(e => {
			console.warn('Error tracking data interaction:', e);
			return { success: false };
		});
	} catch (error) {
		console.warn('Error tracking data interaction:', error);
		return { success: false };
	}
}

export const getInvitations = async () => {
	console.log('getting invites');
	return await noMonitor.get(`/share/data/invitations`).then(res => res.data.data)
}

export const getInvitationCount = async () => {
	console.log('calling invitation count')
	return await noMonitor.get(`/share/data/invitation-count`).then(res => res.data.data)
}

export const acceptInvitation = async (invitationId) => {
	return await monitoredRequest.post(`/share/data/accept-invitation/${invitationId}`).then(res => res.data.data)
}

export const rejectInvitation = async (invitationId) => {
	return await noMonitor.post(`/share/data/reject-invitation/${invitationId}`).then(res => res.data.data)
}

export const revokeInvitation = async (invitationId) => {
	return await monitoredRequest.post(`/share/data/revoke-invitation/${invitationId}`).then(res => res.data.data)
}

export const getPublicData = async (
	sort,
	page = 1,
	limit = 20,
	search = '',
	filters: { minSize?: string; maxSize?: string; location?: string } = {},
	options: { recommend?: boolean; featuredLimit?: number; recommendationContext?: unknown } = {},
) => {
	const params = new URLSearchParams();
	if (sort) params.append('sortBy', sort);
	if (page) params.append('page', page.toString());
	if (limit) params.append('limit', limit.toString());
	if (search) params.append('search', search);

	if (filters?.minSize) params.append('minSize', filters.minSize);
	if (filters?.maxSize) params.append('maxSize', filters.maxSize);
	if (filters?.location) params.append('location', filters.location);
	if (options?.recommend) params.append('recommend', 'true');
	if (options?.featuredLimit) params.append('featuredLimit', options.featuredLimit.toString());
	if (options?.recommendationContext) params.append('recommendationContext', JSON.stringify(options.recommendationContext));
	
	const queryString = params.toString();
	const url = `/data/public-data${queryString ? `?${queryString}` : ''}`;
	
	return await noMonitor.get(url).then(res => res.data)
}

export const getTrendingDataTags = async (limit = 20) => {
	return await noMonitor
		.get(`/data/trending-tags`, { params: { limit } })
		.then((res) => res.data);
}

export const getDatasetCategorySuggestions = async (payload: {
	dataId: string;
	dataTitle?: string;
	dataSchema?: any[];
	dataTags?: string[];
}) => {
	return await noMonitor
		.post('/data/dataset-category-suggestions', payload)
		.then((res) => res.data);
};

export const setDatasetCategory = async (payload: {
	dataId: string;
	category?: string;
	customLabel?: string;
	dismiss?: boolean;
}) => {
	return await noMonitor
		.post('/data/set-dataset-category', payload)
		.then((res) => res.data);
};

export const getCompanyList = async () => {
	return await noMonitor.get(`/get-company-list`).then(res => res.data.data)
}

export const updateDataItem = async (
	dataId,
	values,
	options?: { expectedUpdatedAt?: string; force?: boolean }
) => {
	return await monitorFail
		.post(`/data/update-item`, {
			dataId,
			values,
			expectedUpdatedAt: options?.expectedUpdatedAt,
			force: options?.force,
		})
		.then((res) => res.data);
}


export const hideFirstTutorial = async () => {
	await noMonitor.get(`/user/hide-first-tutorial`)
}

export const completeTutorial = async (tutorialId: string, versionCode: number) => {
	return await noMonitor.post(`/user/complete-tutorial`, { tutorialId, versionCode }).then(res => res.data);
}

export const getPreviewData = async () => {
	await noMonitor.post('/data/get-preview-data',)
}
export const saveEmailTemplate = async (payload) => {
	return await monitoredRequest.post('/templates/post-email-template', payload).then((res) => res.data)
}

export const updateEmailTemplate = async (payload) => {
	return await monitoredRequest.post('/templates/update-email-template', payload)
}

export const getInitialTemplates = async () => {

}

export const getEmailTemplates = async () => {
	return await noMonitor.get('/templates/get-email-templates').then(res => { return res.data.data })
}

export const getWebpageTemplates = async () => {
	return await noMonitor.get('/templates/webpages/get-templates').then(res => { return res.data })
}

export const getSingleWebpageTemplate = async (id: string) => {
	return await api.get(`/templates/webpages/get-single-template/${id}`).then(res => { return res.data })
}

export const deleteWebpageTemplate = async (id: string) => {
	return await api.delete(`/templates/webpages/delete-template/${id}`).then(res => { return res.data })
}

export const saveWebpageTemplate = async (data: any) => {
	return await api.post('/templates/webpages/post-template', data).then(res => { return res.data })
}

export const updateWebpageTemplate = async (data: {
	_id: string;
	title?: string;
	description?: string;
	content?: any;
	tags?: string[];
	isPublished?: boolean;
	urlSlug?: string;
	githubSource?: {
		owner: string;
		repo: string;
		branch?: string;
		path: string;
		sha?: string;
	} | null;
}) => {
	return await api.post('/templates/webpages/update-template', data).then(res => { return res.data })
}

/** App mode (AI site builder) — see smartsaas-backend src/routes/siteBuilderRouter.mjs */
export const getSites = async () => {
	return await api.get('/sites').then(res => res.data)
}

export const createSite = async (data: { name?: string; description?: string; prompt?: string; fromWebpageTemplateId?: string }) => {
	return await api.post('/sites', data).then(res => res.data)
}

export const getSite = async (siteId: string) => {
	return await api.get(`/sites/${siteId}`).then(res => res.data)
}

export const deleteSite = async (siteId: string) => {
	return await api.delete(`/sites/${siteId}`).then(res => res.data)
}

export const startSitePreview = async (siteId: string) => {
	return await api.post(`/sites/${siteId}/sandbox/start`, {}, { timeout: 180000 }).then(res => res.data)
}

export const stopSitePreview = async (siteId: string) => {
	return await api.post(`/sites/${siteId}/sandbox/stop`).then(res => res.data)
}

export const sitePreviewHeartbeat = async (siteId: string) => {
	return await noMonitor.post(`/sites/${siteId}/sandbox/heartbeat`).then(res => res.data)
}

export const getSiteFiles = async (siteId: string, version?: number) => {
	return await api.get(`/sites/${siteId}/files`, { params: version ? { version } : undefined }).then(res => res.data)
}

export const saveSiteFiles = async (siteId: string, data: { baseVersion: number; changes: Record<string, string | null> }) => {
	return await api.put(`/sites/${siteId}/files`, data).then(res => res.data)
}

export const promptSite = async (siteId: string, data: { prompt: string; focusPaths?: string[] }) => {
	return await api.post(`/sites/${siteId}/prompt`, data).then(res => res.data)
}

export const getSiteVisualElement = async (siteId: string, ref: string) => {
	return await api.get(`/sites/${siteId}/visual-element`, { params: { ref } }).then(res => res.data)
}

export const visualEditSite = async (siteId: string, data: { baseVersion: number; op: Record<string, unknown> }) => {
	return await api.post(`/sites/${siteId}/visual-edit`, data).then(res => res.data)
}

export const getSiteVersions = async (siteId: string) => {
	return await api.get(`/sites/${siteId}/versions`).then(res => res.data)
}

export const restoreSiteVersion = async (siteId: string, version: number) => {
	return await api.post(`/sites/${siteId}/versions/${version}/restore`).then(res => res.data)
}

export const publishSite = async (siteId: string) => {
	return await api.post(`/sites/${siteId}/publish`).then(res => res.data)
}

export const getSiteDeployments = async (siteId: string) => {
	return await api.get(`/sites/${siteId}/deployments`).then(res => res.data)
}

export const getSiteDeployment = async (siteId: string, deploymentId: string) => {
	return await api.get(`/sites/${siteId}/deployments/${deploymentId}`).then(res => res.data)
}

export const activateSiteDeployment = async (siteId: string, deploymentId: string) => {
	return await api.post(`/sites/${siteId}/deployments/${deploymentId}/activate`).then(res => res.data)
}

export const addSiteDomain = async (siteId: string, hostname: string) => {
	return await api.post(`/sites/${siteId}/domains`, { hostname }).then(res => res.data)
}

export const refreshSiteDomain = async (siteId: string, hostname: string) => {
	return await api.post(`/sites/${siteId}/domains/${encodeURIComponent(hostname)}/refresh`).then(res => res.data)
}

export const removeSiteDomain = async (siteId: string, hostname: string) => {
	return await api.delete(`/sites/${siteId}/domains/${encodeURIComponent(hostname)}`).then(res => res.data)
}

export const getSiteGitHub = async (siteId: string) => {
	return await api.get(`/sites/${siteId}/github`).then(res => res.data)
}

export const getSiteAnalytics = async (siteId: string, days = 30) => {
	return await api.get(`/sites/${siteId}/analytics`, { params: { days } }).then(res => res.data)
}

export const linkSiteGitHub = async (
	siteId: string,
	data: { owner?: string; repo: string; branch?: string; create?: boolean; isPrivate?: boolean },
) => {
	return await api.post(`/sites/${siteId}/github`, data).then(res => res.data)
}

export const setSiteGitHubAutoPush = async (siteId: string, autoPush: boolean) => {
	return await api.patch(`/sites/${siteId}/github`, { autoPush }).then(res => res.data)
}

export const unlinkSiteGitHub = async (siteId: string) => {
	return await api.delete(`/sites/${siteId}/github`).then(res => res.data)
}

export const pushSiteToGitHub = async (siteId: string) => {
	return await api.post(`/sites/${siteId}/github/push`).then(res => res.data)
}

export const duplicateWebpageTemplate = async (id: string) => {
	return await api.post(`/templates/webpages/duplicate-template/${id}`).then(res => { return res.data })
}

export const getInvoiceTemplates = async () => {
	return await noMonitor.get('/templates/invoices/get-templates').then(res => {
		const payload = res.data?.data ?? res.data?.records;
		return Array.isArray(payload) ? payload : [];
	});
}

export const getSentInvoices = async (
	pagination: { page?: number | string; limit?: number | string; sortBy?: string; sortOrder?: string } = {},
) => {
	const queryParams = new URLSearchParams();
	if (pagination.page) queryParams.append('page', String(pagination.page));
	if (pagination.limit) queryParams.append('limit', String(pagination.limit));
	if (pagination.sortBy) queryParams.append('sortBy', pagination.sortBy);
	if (pagination.sortOrder) queryParams.append('sortOrder', pagination.sortOrder);

	const queryString = queryParams.toString();
	const url = `/invoices/sent${queryString ? `?${queryString}` : ''}`;
	return await noMonitor.get(url).then(res => { return res.data })
}

export const getInvoiceById = async (invoiceId) => {
	return await noMonitor.get(`/invoices/${invoiceId}`).then(res => { return res.data.data })
}

export const markInvoiceAsViewed = async (invoiceId) => {
	return await noMonitor.patch(`/invoices/${invoiceId}/viewed`).then(res => { return res.data })
}

export const searchInvoiceRecipients = async (query = '', limit = 8) => {
	const res = await noMonitor.get('/invoices/recipients', { params: { q: query, limit } });
	return Array.isArray(res.data?.data) ? res.data.data : [];
}

export const sendInvoiceEmail = async (
	invoiceId: string,
	payload: { toEmail: string; subject?: string; message?: string }
) => {
	const res = await noMonitor.post(`/invoices/${invoiceId}/send`, payload);
	return res.data;
}

export const getEmailTemplate = async (templateId) => {
	return await noMonitor.get(`/templates/get-single-email-template/${templateId}`).then(res => { return res.data.data })
}

export const editEmailTemplate = async (templatedId, content) => {
	return await noMonitor.post(`/templates/edit-email-template/${templatedId}`, content)
}

export const deleteEmailTemplate = async (templateId) => {
	return await noMonitor.delete(`/templates/delete-email-template/${templateId}`)
}

export const getServiceList = async (userId) => {
	return await noCreds.get(`/get-service-list/${userId}`).then(res => { return res.data.data })
}

export const getStoreInfo = async (storeId) => {
	return await noCreds.get(`/get-store-info/${storeId}`).then(res => { return res.data.data })
} 

// Helper function to dispatch SSE progress to AuthModal
// Reducer expects `type` + `content` (not modalType / modalContent) so progress updates apply correctly.
export const dispatchSSEProgress = (progressData: any) => {
	const { dispatch } = store;
	const message =
		(typeof progressData?.message === 'string' && progressData.message) ||
		(typeof progressData?.text === 'string' && progressData.text) ||
		'Processing...';
	const sseData = { ...progressData, message };
	dispatch({
		type: SHOW_AUTH_MODAL,
		data: {
			type: 'progress',
			content: message,
			sseData,
		},
	});
};

function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result as string);
		reader.onerror = () => reject(new Error('Failed to read image data'));
		reader.readAsDataURL(blob);
	});
}

/** Server expects img.uri as a data: URL with base64. Convert file:// / content:// / http(s) / blob: URIs. */
async function ensureUriIsDataUrl(uri: string, mimeType: string): Promise<string> {
	if (uri.startsWith('data:')) {
		return uri;
	}
	if (/^https?:\/\//i.test(uri)) {
		const response = await fetch(uri);
		const blob = await response.blob();
		return blobToDataUrl(blob);
	}
	if (Platform.OS === 'web') {
		const response = await fetch(uri);
		const blob = await response.blob();
		return blobToDataUrl(blob);
	}
	try {
		const file = new ExpoFile(uri);
		const base64String = await file.base64();
		return `data:${mimeType || 'image/jpeg'};base64,${base64String}`;
	} catch {
		const response = await fetch(uri);
		const blob = await response.blob();
		return blobToDataUrl(blob);
	}
}

async function prepareAddDataImagePayload(image: any): Promise<{ img: { uri: string; fileName: string; mimeType: string } }> {
	if (typeof image === 'string') {
		if (image.startsWith('data:')) {
			const mimeType = image.match(/data:([^;]+)/)?.[1] || 'image/png';
			const fileName = `image.${mimeType.split('/')[1] || 'png'}`;
			return { img: { uri: image, fileName, mimeType } };
		}
		return {
			img: {
				uri: `data:image/png;base64,${image}`,
				fileName: 'image.png',
				mimeType: 'image/png',
			},
		};
	}
	if (typeof File !== 'undefined' && image instanceof File) {
		const dataUrl = await blobToDataUrl(image);
		return {
			img: {
				uri: dataUrl,
				fileName: image.name || 'image.png',
				mimeType: image.type || 'image/png',
			},
		};
	}
	if (typeof Blob !== 'undefined' && image instanceof Blob) {
		const dataUrl = await blobToDataUrl(image);
		return {
			img: {
				uri: dataUrl,
				fileName: 'image.png',
				mimeType: image.type || 'image/png',
			},
		};
	}
	if (image && typeof image === 'object') {
		if (image.base64) {
			const dataUrl = image.base64.startsWith('data:')
				? image.base64
				: `data:${image.type || 'image/png'};base64,${image.base64}`;
			return {
				img: {
					uri: dataUrl,
					fileName: image.fileName || image.name || 'image.png',
					mimeType: image.mimeType || image.type || 'image/png',
				},
			};
		}
		if (image.uri || image.url) {
			const rawUri = image.uri || image.url;
			const fileName = image.fileName || image.name || 'image.png';
			const mimeType = image.mimeType || image.type || 'image/jpeg';
			const dataUri = await ensureUriIsDataUrl(rawUri, mimeType);
			return { img: { uri: dataUri, fileName, mimeType } };
		}
	}
	throw new Error('Unsupported image format');
}

/**
 * Detect SSE body when `Content-Type` is missing from JS (CORS: `text/event-stream` is not a
 * safelisted response Content-Type for credentialed cross-origin XHR/fetch).
 */
function addDataImageBodyLooksLikeSse(text: string): boolean {
	if (!text || typeof text !== 'string') return false;
	const trimmed = text.trim();
	if (trimmed.startsWith('data:')) return true;
	return /\n\s*data:\s*\{/.test(text);
}

/** Shared SSE line handler for add-data-image responses (server sends JSON in `data:` lines). */
function createAddDataImageSseConsumer(
	resolve: (value: any) => void,
	reject: (reason: Error) => void,
	onProgress?: (data: any) => void
) {
	let sseSettled = false;

	function processLine(line: string) {
		line = line.trim();
		if (line.startsWith('data: ')) {
			try {
				const jsonData = line.substring(6).trim();
				if (jsonData) {
					const data = JSON.parse(jsonData);
					console.log('SSE data received:', data);

					dispatchSSEProgress(data);

					if (onProgress) {
						onProgress(data);
					}

					switch (data.status) {
						case 'starting':
						case 'uploading':
						case 'processing':
						case 'progress':
							console.log(`Upload ${data.status}: ${data.message}`);
							break;
						case 'completed':
							console.log('Upload completed successfully');
							if (!sseSettled) {
								sseSettled = true;
								resolve(data.data || data.url || data);
							}
							return;
						case 'error':
							console.error('Upload error:', data.message);
							if (!sseSettled) {
								sseSettled = true;
								reject(new Error(data.message || 'Upload failed'));
							}
							return;
					}
				}
			} catch (parseError) {
				console.warn('Failed to parse SSE data:', line, parseError);
			}
		} else if (line === '' || line.startsWith(':')) {
			// Empty line or comment, ignore
		} else {
			console.log('Non-data SSE line:', line);
		}
	}

	function processBuffer(bufferData: string) {
		bufferData.split('\n').forEach(processLine);
	}

	return {
		processLine,
		processBuffer,
		get settled() {
			return sseSettled;
		},
	};
}

export const addDataImage = async (dataId: string, contentId: string, image: any, onProgress?: (data: any) => void) => {
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Preparing upload...',
			progress: 0
		});

		prepareAddDataImagePayload(image)
			.then((payload) => {
				const url = `${USER_API_BASE_URL}/upload/add-data-image/${dataId}/${contentId}`;
				const body = JSON.stringify(payload);

				// XMLHttpRequest provides real upload progress (bytes sent). fetch() does not.
				if (typeof XMLHttpRequest !== 'undefined') {
					const xhr = new XMLHttpRequest();
					xhr.open('POST', url);
					xhr.withCredentials = true;
					xhr.setRequestHeader('Content-Type', 'application/json');
					xhr.setRequestHeader('Accept', 'text/event-stream');

					let lastUploadPct = 0;
					xhr.upload.onprogress = (e) => {
						if (e.lengthComputable && e.total > 0) {
							const pct = Math.max(2, Math.min(52, Math.round((e.loaded / e.total) * 52)));
							if (pct - lastUploadPct >= 2 || e.loaded === e.total) {
								lastUploadPct = pct;
								const evt = {
									status: 'uploading',
									message: 'Uploading image to server...',
									progress: pct,
								};
								dispatchSSEProgress(evt);
								onProgress?.(evt);
							}
						}
					};

					xhr.onerror = () => {
						const err = new Error('Network request failed');
						dispatchSSEProgress({
							status: 'error',
							message: err.message,
							progress: 0,
						});
						reject(err);
					};

					xhr.onload = () => {
						const text = xhr.responseText || '';
						const contentType = xhr.getResponseHeader('content-type');
						const treatAsSse =
							(!!contentType && contentType.includes('text/event-stream')) ||
							addDataImageBodyLooksLikeSse(text);

						if (xhr.status < 200 || xhr.status >= 300) {
							let message = `HTTP error! status: ${xhr.status}`;
							try {
								const parsed = JSON.parse(text);
								if (parsed?.error && typeof parsed.error === 'string') {
									message = parsed.error;
								}
							} catch {
								if (text && text.length < 500) {
									message = text;
								}
							}
							dispatchSSEProgress({
								status: 'error',
								message,
								progress: 0,
							});
							reject(new Error(message));
							return;
						}

						if (!treatAsSse) {
							console.warn('addDataImage: response is not SSE, content-type:', contentType);
							try {
								resolve(JSON.parse(text));
							} catch {
								reject(new Error('Invalid response format'));
							}
							return;
						}

						const consumer = createAddDataImageSseConsumer(resolve, reject, onProgress);
						consumer.processBuffer(text);
						if (!consumer.settled) {
							reject(new Error('Upload response incomplete'));
						}
					};

					xhr.send(body);
					return;
				}

				fetch(url, {
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'text/event-stream',
					},
					body,
				})
					.then((response) => {
						console.log(
							'Response received:',
							response.status,
							response.headers.get('content-type')
						);

						return response.text().then((fullText) => {
							if (!response.ok) {
								let message = `HTTP error! status: ${response.status}`;
								try {
									const parsed = JSON.parse(fullText);
									if (parsed?.error && typeof parsed.error === 'string') {
										message = parsed.error;
									}
								} catch {
									if (fullText && fullText.length < 500) {
										message = fullText;
									}
								}
								dispatchSSEProgress({
									status: 'error',
									message,
									progress: 0,
								});
								throw new Error(message);
							}

							const contentType = response.headers.get('content-type');
							const treatAsSse =
								(!!contentType && contentType.includes('text/event-stream')) ||
								addDataImageBodyLooksLikeSse(fullText);

							if (treatAsSse) {
								const consumer = createAddDataImageSseConsumer(resolve, reject, onProgress);
								consumer.processBuffer(fullText);
								if (!consumer.settled) {
									reject(new Error('Upload response incomplete'));
								}
								return;
							}

							console.warn('addDataImage: response is not SSE, content-type:', contentType);
							try {
								resolve(JSON.parse(fullText));
							} catch {
								reject(new Error('Invalid response format'));
							}
						});
					})
					.catch((error: any) => {
						console.error('Fetch error:', error);
						dispatchSSEProgress({
							status: 'error',
							message: error.message || 'Upload failed',
							progress: 0,
						});
						reject(error);
					});
			})
			.catch((error) => {
				console.error('addDataImage prepare error:', error);
				dispatchSSEProgress({
					status: 'error',
					message: error?.message || 'Invalid image format',
					progress: 0
				});
				reject(error);
			});
	});
}

export const getDataContentById = async (dataId, contentId, mode: 'live' | 'draft' = 'live') => {
	const queryString = mode ? `?mode=${mode}` : '';
	const response = await noMonitor.get(`/data/content/${dataId}/${contentId}${queryString}`);
	// Return both data and schema from the response
	return {
		data: response.data.data,
		schema: response.data.schema,
		...response.data.data // Spread data properties for backward compatibility
	};
}

export const deleteInvoiceTemplate = async (id) => {
	return await noMonitor.delete(`/templates/invoices/delete-template/${id}`).then((res) => { if (res.status === 200) { return { success: true } } });
}

export const deleteDocumentTemplate = async (documentId: string) => {
	return await noMonitor.post('/templates/documents/delete-template', { documentId }).then((res) => {
		if (res.status === 200) return { success: true };
		return res.data;
	});
}

export const postInvoiceTemplate = async (data) => {
	return await noMonitor.post('/templates/invoices/post-template', data).then((res) => {
		if (res.status === 200) {
			return { success: true, invoice_logo: res.data?.invoice_logo, _id: res.data?._id };
		}
	});
}

export const setDefaultInvoiceTemplate = async (id: string) => {
	return await noMonitor.post(`/templates/invoices/set-default/${id}`).then((res) => res.data);
}

export const postDocumentTemplate = async (data) => {
	return await noMonitor.post('/templates/documents/post-template', data);
}

export const getFeedbackSchema = async () => {
	return await noMonitor.get('/feedback/get-feedback-schema').then(res => { return res.data.data })
}

export const getFeedback = async (userId) => {
	return await noMonitor.get(`/feedback/get-feedback/${userId}`).then(res => { return res.data.data })
}

export const postFeedback = async (data) => {
	return await noMonitor.post('/feedback/post-feedback', data);
};

export const postReportProblem = async (data: { title?: string; description?: string; screenPage?: string; platform?: string }) => {
	return await noMonitor.post('/feedback/report-problem', data);
};

export const postOrganisationEnquiry = async (data: {
	company_name: string;
	contact_email: string;
	phone?: string;
	team_size: string;
	requirements: string;
}) => {
	return await noMonitor.post('/feedback/organisation-enquiry', data);
};

export const generateAIFunction = async (data) => {
	// Check if this is a data-specific function generation request
	const endpoint = data.dataId ? '/ai/generate-data-function' : '/ai/generate-function';
	// Support both description (structured) and textContent (natural language)
	const payload = {
		...data,
		// If textContent is provided, use it; otherwise use description
		textContent: data.textContent || undefined,
		description: data.textContent ? undefined : data.description
	};
	return await noMonitor.post(endpoint, payload, { timeout: 120000 }).then(res => { return res.data })
}

// Specific function for generating data utilities
export const generateDataUtility = async (data) => {
	return await noMonitor.post('/ai/generate-data-function', data, { timeout: 120000 }).then(res => { return res.data })
}

export const getProductServiceList = async () => {
	return await noMonitor.get('/data/product-and-service').then(res => { return res.data })
}

export const draftCalendarFromPrompt = async (prompt: string, followUp = false) => {
	return await noMonitor.post('/calendar/draft-from-prompt', { prompt, followUp }).then(res => res.data);
}

export const getMarketplaceData = async (options: {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	search?: string;
	dataFilter?: string;
	mode?: 'live' | 'draft';
} = {}) => {
	const params = new URLSearchParams();
	if (options.page) params.append('page', options.page.toString());
	if (options.limit) params.append('limit', options.limit.toString());
	if (options.sortBy) params.append('sortBy', options.sortBy);
	if (options.sortOrder) params.append('sortOrder', options.sortOrder);
	if (options.search) params.append('search', options.search);
	if (options.dataFilter && options.dataFilter !== 'All') params.append('dataFilter', options.dataFilter);
	if (options.mode) params.append('mode', options.mode);

	const queryString = params.toString();
	const url = `/data/marketplace${queryString ? `?${queryString}` : ''}`;
	return await noMonitor.get(url).then(res => { return res.data })
}

export const getAllDataItems = async (options: {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	search?: string;
	dataFilter?: string;
} = {}) => {
	const params = new URLSearchParams();
	if (options.page) params.append('page', options.page.toString());
	if (options.limit) params.append('limit', options.limit.toString());
	if (options.sortBy) params.append('sortBy', options.sortBy);
	if (options.sortOrder) params.append('sortOrder', options.sortOrder);
	if (options.search) params.append('search', options.search);
	if (options.dataFilter && options.dataFilter !== 'All') params.append('dataFilter', options.dataFilter);

	const queryString = params.toString();
	const url = `/data/all-items${queryString ? `?${queryString}` : ''}`;
	return await noMonitor.get(url).then(res => { return res.data })
}

export const searchRegEx = async (text) => {
	const query = encodeURIComponent(String(text || '').trim());
	if (!query) {
		return { success: true, results: [], data: [] };
	}
	return await noMonitor.get(`/user/searchall?q=${query}`).then(res => { return res.data })
}

export const getIntegrationApiAction = async (action) => {
	return await noMonitor.post(`${action}`, { redirectUrl: Linking.createURL('/integrations'), refreshUrl: Linking.createURL('/integrations') })
}

export const createStorePaymentIntent = async () => {
	return await noMonitor.post('/stripe/connect/create-payment-intent').then((res) => { console.log(res); return res })
}

export const createStripeStandardConnectLink = async (email?: string) => {
	// Stripe Account Links require HTTPS return/refresh URLs. Native uses the same explicit
	// HTTPS URL for the API and for `WebBrowser.openAuthSessionAsync` (must match exactly).
	const integrationReturnUrl =
		Platform.OS === 'web'
			? Linking.createURL('/universal/integrations')
			: getStripeConnectAccountLinkReturnUrl();
	return await noMonitor
		.post('/stripe/connect/create-standard', {
			email,
			redirectUrl: integrationReturnUrl,
			refreshUrl: integrationReturnUrl,
		})
		.then((res) => res?.data?.url || res?.data?.data?.url);
}

export const getStripeId = async () => {
	return await noMonitor.post('/stripe/get-stripe-id').then(res => { return res.data.stripeId });
}

export const registerStripeLocation = async (data) => {
	return await noMonitor.post('/stripe/update-user-location', data)
}

export const registerStripeReader = async (data) => {
	return await noMonitor.post('/stripe/register-reader', data).then(res => { return res.data })
}

export const confirmPaymentIntent = async (data) => {
	return await noMonitor.post('/stripe/confirm-intent', data).then(res => { return res.data })
}

export const getAIForm = async (data) => {
	return await monitoredRequest.post('/ai/get-form', data, { timeout: 120000 }).then(res => { return res.data })
}

export const getFeed = async (
	distance,
	lat?: string,
	lon?: string,
	pageParam?: number,
	sortBy?: string,
	category?: string,
	options?: {
		recommend?: boolean;
		featuredLimit?: number;
		recommendationContext?: Record<string, any>;
		servedPostIds?: string[];
		includeNews?: boolean;
	}
) => {
	// Client infinite queries use 0-based pageParam; API expects 1-based page.
	const page = (typeof pageParam === 'number' ? pageParam : 0) + 1;
	const params = new URLSearchParams({ distance: String(distance), lat: lat || '', lon: lon || '', page: String(page) });
	if (sortBy) params.set('sortBy', sortBy);
	if (category) params.set('category', category);
	if (options?.recommend) params.set('recommend', 'true');
	if (options?.featuredLimit) params.set('featuredLimit', String(options.featuredLimit));
	if (options?.recommendationContext) params.set('recommendationContext', JSON.stringify(options.recommendationContext));
	if (options?.servedPostIds?.length) params.set('servedPostIds', options.servedPostIds.join(','));
	if (options?.includeNews === false) params.set('includeNews', 'false');
	return await noMonitor.get(`/feed?${params.toString()}`).then(res => { return res.data })
}

export const postFeedEvents = async (events: Array<{
	action: string;
	postId?: string;
	authorId?: string;
	dwellMs?: number;
	tags?: string[];
}>) => {
	if (!events?.length) return { ok: true, appended: 0 };
	return await noMonitor.post('/feed/events', { events }).then(res => res.data);
}

export const getExternalAds = async (count: number = 3, lat?: string, lon?: string, distance?: number) => {
	try {
		const response = await noMonitor.get(`/feed/external-ads?count=${count}&lat=${lat}&lon=${lon}&distance=${distance}`);

		// Handle different response structures
		const ads = response?.data?.data || response?.data || [];
		return Array.isArray(ads) ? ads : [];
	} catch (error) {
		console.error('Failed to fetch external ads:', error);
		return [];
	}
}

export const trackAdImpression = async (adId: string, adNetwork?: string, adCampaignId?: string, adCreativeId?: string) => {
	return await noMonitor.post('/feed/external-ads/track-impression', {
		adId,
		adNetwork,
		adCampaignId,
		adCreativeId
	}).then(res => { return res.data })
}

export const trackAdClick = async (adId: string, clickUrl: string, adNetwork?: string, adCampaignId?: string, adCreativeId?: string) => {
	return await noMonitor.post('/feed/external-ads/track-click', {
		adId,
		clickUrl,
		adNetwork,
		adCampaignId,
		adCreativeId
	}).then(res => { return res.data })
}

export const postToFeed = async (data) => {
	return await monitoredRequest.post('/feed/post', data).then(res => { return res.data })
}

export const getCompanyBoard = async (params?: {
	department?: string;
	since?: string;
	countOnly?: boolean;
}) => {
	const q = new URLSearchParams();
	if (params?.department) q.set('department', params.department);
	if (params?.since) q.set('since', params.since);
	if (params?.countOnly) q.set('countOnly', 'true');
	const suffix = q.toString() ? `?${q.toString()}` : '';
	return await noMonitor.get(`/feed/company-board${suffix}`).then((res) => res.data);
};

export const postCompanyBoard = async (data: {
	content: string;
	boardDepartment?: string;
	data_link?: string;
	image?: unknown;
	location?: { latitude?: number; longitude?: number; lat?: number; lng?: number };
	url?: string;
	appLink?: string;
	integrations?: Record<string, unknown>;
}) => {
	return await monitoredRequest.post('/feed/company-board', data).then((res) => res.data);
};

export const patchCompanyBoard = async (postId: string, data: { content: string; data_link?: string }) => {
	return await monitoredRequest.patch(`/feed/company-board/${encodeURIComponent(postId)}`, data).then((res) => res.data);
};

export const hideCompanyBoardPost = async (postId: string) => {
	return await monitoredRequest.post(`/feed/company-board/${encodeURIComponent(postId)}/hide`).then((res) => res.data);
};

export const unhideCompanyBoardPost = async (postId: string) => {
	return await monitoredRequest.post(`/feed/company-board/${encodeURIComponent(postId)}/unhide`).then((res) => res.data);
};

export const deleteCompanyBoardPost = async (postId: string) => {
	return await monitoredRequest.delete(`/feed/company-board/${encodeURIComponent(postId)}`).then((res) => res.data);
};

export const getAgentFeed = async (params?: { limit?: number; before?: string; agentId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.set('limit', String(params.limit));
	if (params?.before) queryParams.set('before', params.before);
	if (params?.agentId) queryParams.set('agentId', params.agentId);
	const query = queryParams.toString();
	return await noMonitor.get(`/agent-feed${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getAgentTasks = async (params?: { limit?: number; before?: string; agentId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.set('limit', String(params.limit));
	if (params?.before) queryParams.set('before', params.before);
	if (params?.agentId) queryParams.set('agentId', params.agentId);
	const query = queryParams.toString();
	return await noMonitor.get(`/ai/agent-tasks${query ? `?${query}` : ''}`).then(res => res.data);
}

export const cancelAgentTask = async (taskId: string) => {
	return await monitoredRequest.post(`/ai/agent-tasks/${encodeURIComponent(taskId)}/cancel`).then(res => res.data);
}

export const getGlobalBotRules = async () => {
	return await noMonitor.get('/agents/rules/global').then(res => res.data);
}

export const updateGlobalBotRules = async (payload: { globalRulesMarkdown?: string }) => {
	return await monitoredRequest.put('/agents/rules/global', payload).then(res => res.data);
}

export const getAgentRules = async (agentId: string) => {
	return await noMonitor.get(`/agents/${agentId}/rules`).then(res => res.data);
}

export const getAgentToolCatalog = async () => {
	return await noMonitor.get('/agents/tool-catalog').then(res => res.data);
}

export const updateAgentRules = async (
	agentId: string,
	payload: {
		botRulesMarkdown?: string;
		useBotOverride?: boolean;
		coreFiles?: Record<string, string>;
		toolEnabled?: Record<string, boolean>;
	}
) => {
	return await monitoredRequest.put(`/agents/${agentId}/rules`, payload).then(res => res.data);
}

export type AgentNotificationPrefs = { browser: boolean; pinNote: boolean };

/** Current user's task-completion delivery prefs for one agent. */
export const getAgentNotificationPrefs = async (agentId: string): Promise<AgentNotificationPrefs> => {
	return await noMonitor.get(`/agents/${agentId}/notification-prefs`).then(res => res.data?.data);
}

export const updateAgentNotificationPrefs = async (
	agentId: string,
	payload: Partial<AgentNotificationPrefs>
): Promise<AgentNotificationPrefs> => {
	return await noMonitor.put(`/agents/${agentId}/notification-prefs`, payload).then(res => res.data?.data);
}

/** Append or replace content in an agent's core files (e.g. MEMORY.md for logging retries). */
export const patchAgentCoreFiles = async (
	agentId: string,
	updates: Array<{ fileName: string; content: string; op?: 'append' | 'replace' }>
) => {
	return await monitoredRequest.patch(`/agents/${agentId}/core-files`, { updates }).then(res => res.data);
}

/** Re-initialise all agents for the current user/company: reset base docs (Soul, tools, user, agents, identity, heartbeat, bootstrap, memory). */
export const reinitialiseAgents = async () => {
	return await monitoredRequest.post('/agents/reinitialise').then(res => res.data);
}

/** List agents for the current session scope (user or company). */
export const getAgents = async () => {
	return await noMonitor.get('/agents').then(res => res.data);
}

/** User-approved handoff of an uploaded photo or video to the deployed sales marketer. */
export const dispatchMediaSocialBrief = async (payload: {
	title?: string;
	description?: string;
	mediaUrl: string;
	mediaType?: string;
	assetType?: string;
	extraContext?: string;
}) => {
	return await noMonitor.post('/agents/media-social-brief', payload).then((res) => res.data);
}

/** Set or clear the department shown for an agent on the hierarchy. */
export const updateAgentDepartment = async (agentId: string, department: string | null) => {
	return await noMonitor
		.put(`/agents/${agentId}`, { department: department || null })
		.then((res) => res.data);
}

/** Converted monthly fee for deploying one agent ($50 USD, in the billing currency). */
export const getAgentDeployQuote = async () => {
	return await noMonitor.get('/agents/deploy-quote').then(res => res.data);
}

/** Per-agent AI billing/usage for the current company or personal scope. */
export const getAgentUsage = async (agentId?: string) => {
	const path = agentId ? `/agents/${agentId}/usage` : '/agents/usage';
	return await noMonitor.get(path).then(res => res.data);
}

/** Decommission (delete) an agent and sync subscription seat quantity. */
export const deleteAgent = async (agentId: string) => {
	return await monitoredRequest.delete(`/agents/${agentId}`).then(res => res.data);
}

export const deleteComment = async (commentId, feedId) => {
	return await noMonitor.post('/feed/remove-comment', { commentId: commentId, feedId: feedId })
}

export const addLikeToPost = async (postId) => {
	return await noMonitor.post(`/feed/like/${postId}`).then(res => { return res.data })
}

export const addShareToPost = async (postId) => {
	return await noMonitor.post(`/feed/share/${postId}`).then(res => { return res.data })
}

export const removeLikeFromPost = async (postId) => {
	return await noMonitor.post(`/feed/unlike/${postId}`).then(res => { return res.data })
}

export const removeShareFromPost = async (postId) => {
	return await monitoredRequest.post(`/feed/unshare/${postId}`).then(res => { return res.data })
}

export const addCommentToPost = async (postId, comment) => {
	return await noMonitor.post(`/feed/comment/${postId}`, comment).then(res => { return res.data })
}

export const blockUser = async (targetUserId: string) => {
	return await noMonitor.post('/feed/block-user', { targetUserId }).then(res => res.data);
};

export const unblockUser = async (targetUserId: string) => {
	return await noMonitor.post('/feed/unblock-user', { targetUserId }).then(res => res.data);
};

export const hidePost = async (postId: string) => {
	return await noMonitor.post('/feed/hide-post', { postId }).then(res => res.data);
};

export const unhidePost = async (postId: string) => {
	return await noMonitor.post('/feed/unhide-post', { postId }).then(res => res.data);
};

export const getBlockedUsers = async () => {
	return await noMonitor.get('/feed/blocked-users').then(res => res.data?.blockedUsers || []);
};

export const getHiddenPostsCount = async () => {
	return await noMonitor.get('/feed/hidden-posts-count').then(res => res.data?.hiddenPostsCount ?? 0);
};

export const addNestedComment = async (feedId, commentId, comment) => {
	return await noMonitor.post(`/feed/comment/add-comment/${feedId}`, { comment, commentId: commentId }).then(res => { return res.data })
}

export const getAICommentAnalysis = async (comment) => {
	return await noMonitor.get(`/ai/comment-analysis/${comment}`, { timeout: 120000 }).then(res => { return res.data })
}

export const getExtendedData = async (dataId, localUserId) => {
	return await noMonitor.post(`/data/extended/${dataId}`, { localUserId: localUserId }).then(res => { return res.data })
}

export const storeDeviceToken = async (token) => {
	await noMonitor.post('/user/store-device-token', token)
}

export const replaceSchema = async (schema) => {
	const { id } = schema;
	return await monitoredRequest.post(`/data/replace-schema/${id}`, schema)
}

export const getDataItemById = async (dataId) => {
	return await noMonitor.get(`/data/item/getItem/${dataId}`).then(res => { return res.data })
}

/** In-flight GETs by product id — avoids optimizedInterceptor duplicate rejection when Strict Mode / re-renders fire twice within 500ms. */
const retrieveProductInflight = new Map<string, Promise<unknown>>();

export const retrieveProduct = async (productId) => {
	const id = String(productId ?? '');
	let inflight = retrieveProductInflight.get(id);
	if (inflight) {
		return inflight;
	}
	inflight = noMonitor
		.get(`/stripe/product/${id}`)
		.then((res) => res.data)
		.finally(() => {
			if (retrieveProductInflight.get(id) === inflight) {
				retrieveProductInflight.delete(id);
			}
		});
	retrieveProductInflight.set(id, inflight);
	return inflight;
};

export const getPlanProducts = async () => {
	return await noMonitor.get('/stripe/plan-products').then(res => { 
		if (res.status !== 200) {
			throw new Error(res.data?.error || 'Failed to fetch plan products');
		}
		return res.data;
	}).catch(error => {
		console.error('Error fetching plan products:', error);
		throw error;
	});
}

export const getPlanSubscriptions = async () => {
	// Public pricing endpoint used on Login before authentication.
	return await publicApi.get('/stripe/plan-subscriptions').then(res => { 
		if (res.status !== 200) {
			throw new Error(res.data?.error || 'Failed to fetch plan subscriptions');
		}
		return res.data;
	}).catch(error => {
		console.error('Error fetching plan subscriptions:', error);
		throw error;
	});
}

export const createNewAppSubscription = async (receiptData?: any) => {
	return await noMonitor.post('/payments/app-subscription', { receiptData }).then(res => {
		if (res.status !== 200) {
			throw new Error(res.data?.error || 'Failed to create app subscription');
		}
		return res.data;
	}).catch(error => {
		console.error('Error creating app subscription:', error);
		throw error;
	});
}

export const getCustomerPortal = async (accountId) => {
	return await noMonitor.post(`/stripe/customer/portal`, accountId).then(res => { return res.data })
}

export const getCheckoutSession = async (productDetails: {
	productId: string;
	promoCode?: string;
	returnUrl?: string;
}) => {
	return await noMonitor.post(`/stripe/checkouts/session`, productDetails).then(res => { return res.data })
}

export const syncCheckoutSession = async (params: { sessionId: string }) => {
	return await noMonitor.post(`/stripe/checkouts/session/sync`, params).then(res => res.data);
}

export const getWinBackOffer = async (notify: boolean = false) => {
	return await noMonitor.get(`/user/win-back-offer?notify=${notify ? 'true' : 'false'}`).then(res => res.data);
}

export const claimWinBackOffer = async () => {
	return await noMonitor.post('/user/win-back-offer/claim').then(res => res.data);
}

export const createSubscriptionPaymentSheet = async (params: { priceId: string; productId?: string; billingId?: string }) => {
	return await noMonitor.post('/payments/subscription-payment-sheet', params).then(res => { return res.data.data }).catch(() => { throw new Error("No response") })
}

/** CoinGate prepaid stablecoin (USDC) subscription checkout — returns hosted paymentUrl */
export const createCryptoSubscriptionCheckout = async (params: {
	productId: string;
	priceId?: string;
	successUrl?: string;
	cancelUrl?: string;
	successOrigin?: string;
}) => {
	return await noMonitor.post('/payments/crypto/subscription', params).then((res) => res.data);
}

export const syncCryptoSubscriptionCheckout = async (params: { orderId: string }) => {
	return await noMonitor.post('/payments/crypto/sync', params).then((res) => res.data);
}

export const getCryptoPaymentStatus = async () => {
	return await noMonitor.get('/payments/crypto/status').then((res) => res.data);
}

export const changeSubscription = async (params: { productId?: string; priceId?: string; prorationBehavior?: string }) => {
	return await noMonitor.post('/stripe/change-subscription', params).then(res => { return res.data }).catch(() => { throw new Error("No response") })
}

export const syncCalendars = async (calendars, deviceInfo) => {
	return await monitoredRequest.post('/calendar/sync-calendars', { calendars, deviceInfo })
}
export const syncCalendarEvents = async (events) => {
	return await monitorFail.post('/calendar/sync-events', events)
}

export const getCalendars = async () => {
	return await noMonitor.get('/calendar/get-calendars').then(res => res.data);
}

export const makeDataPublic = async (dataId: string) => {
	return await noMonitor.post(`/data/make-public/${dataId}`)
}

export const unpublishData = async (dataId: string) => {
	return await noMonitor.delete(`/data/make-public/${dataId}`)
}

export const updateDataSettings = async (
	dataId: string,
	settings: Record<string, any>,
	extra: { completable?: boolean } = {},
) => {
	return await noMonitor.post(`/data/update-settings`, { dataId, settings, ...extra }).then((res) => res.data);
}
export const updateWorkPackage = async (id, workPackage) => {
	return await monitoredRequest.post(`/projects/update-workpackage/${id}`, workPackage)
}

export const getKnowledgeBase = async (linkedId, options: any = {}) => {
	const { page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc' } = options;
	const params = new URLSearchParams({
		page: page.toString(),
		limit: limit.toString(),
		sortBy,
		sortOrder
	});

	console.log('📤 [API-getKnowledgeBase] Requesting knowledge with:', {
		linkedId,
		params: params.toString(),
		url: `/knowledge?${params.toString()}`
	});

	if (linkedId) {
		params.set('linkedId', linkedId);
	}
	const response = await noMonitor.get(`/knowledge?${params.toString()}`);

	console.log('✅ [API-getKnowledgeBase] Response received:', {
		status: response.status,
		dataKeys: Object.keys(response.data || {}),
		internal: response.data?.internal?.length || 0,
		external: response.data?.external?.length || 0
	});

	return response;
}

export const listKnowledgeDecks = async () => {
	const response = await noMonitor.get('/knowledge/decks');
	return response.data;
};

export const getKnowledgeDeck = async (deckId: string) => {
	const response = await noMonitor.get(`/knowledge/decks/${deckId}`);
	return response.data;
};

export const saveKnowledgeDeck = async (deck: { id?: string; title: string; document: any }) => {
	const response = await noMonitor.post('/knowledge/decks', deck);
	return response.data;
};

export const getInternalKnowledgePaginated = async (linkedId, options: any = {}) => {
	const { page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc' } = options;
	const params = new URLSearchParams({
		page: page.toString(),
		limit: limit.toString(),
		sortBy,
		sortOrder
	});

	return await noMonitor.get(`/knowledge/get-internal/?${params.toString()}`)
}

export const genEmail = async (data) => {
	return await noMonitor.post(`/ai/generate-email`, { data: data }, { timeout: 120000 })
}

export const genKnowledge = async (data: { prompt?: string, url?: string }) => {
	return await noMonitor.post(`/ai/generate-knowledge`, data, { timeout: 120000 })
}

export const getContributionChart = async (reportId) => {
	return await noMonitor.get(`/reports/contribution-chart/${reportId}`).then(res => res.data)
}

export const getAIReportQuery = async (reportId, query) => {
	return await noMonitor.post(`/ai/report-query/${reportId}`, { query: query }, { timeout: 120000 }).then(res => res.data)
}

export const googleAuthCallback = async (code) => {
	return await monitoredRequest.post(`/authorisations/oauth2callback`, { code: code })
}

export const getAddRoomSchema = async () => {
	return await noMonitor.get('/messaging/get-room-schema').then(res => res.data.schema)
}

export const buildLink = async (data: {
	targetUser: string,
	sourceUser: string,
	linkType: string,
	direction: string
}) => {
	return await monitoredRequest.post('/company/build-link', { targetUser: data.targetUser, sourceUser: data.sourceUser, linkType: data.linkType, direction: data.direction })
}

export const getSalesStats = () => {
	return noMonitor.get('/sales/get-stats')
}

export const saveForecast = async (forecastData: any) => {
	return await monitoredRequest.post('/sales/forecast/save', forecastData).then(res => res.data)
}

export const getForecasts = async () => {
	return await noMonitor.get('/sales/forecast/list').then(res => res.data)
}

export const getForecast = async (forecastId?: string, forecastName?: string) => {
	const params = new URLSearchParams();
	if (forecastId) params.append('forecastId', forecastId);
	if (forecastName) params.append('forecastName', forecastName);
	const queryString = params.toString();
	return await noMonitor.get(`/sales/forecast/get${queryString ? '?' + queryString : ''}`).then(res => res.data)
}

export const deleteForecast = async (forecastId: string) => {
	return await monitoredRequest.delete(`/sales/forecast/${forecastId}`).then(res => res.data)
}

/** Unified ProductSales + optional forecast projection (session auth). */
export const getSalesAnalytics = async (params: {
	startDate?: string;
	endDate?: string;
	filterType?: 'day' | 'week' | 'month';
	forecastId?: string;
	/** Comma-separated data_item ObjectIds — isolate metrics to those products */
	dataItemIds?: string;
}) => {
	const search = new URLSearchParams();
	if (params.startDate) search.set('startDate', params.startDate);
	if (params.endDate) search.set('endDate', params.endDate);
	if (params.filterType) search.set('filterType', params.filterType);
	if (params.forecastId) search.set('forecastId', params.forecastId);
	if (params.dataItemIds) search.set('dataItemIds', params.dataItemIds);
	const q = search.toString();
	return await noMonitor.get(`/sales/analytics${q ? `?${q}` : ''}`).then((res) => res.data);
};

export const listAnalyticsConnectors = async () => {
	return await noMonitor.get('/sales/analytics/connectors').then((res) => res.data);
};

export const saveAnalyticsConnector = async (payload: Record<string, string>) => {
	return await noMonitor.post('/sales/analytics/connectors', payload).then((res) => res.data);
};

export const deleteAnalyticsConnector = async (connectorId: string) => {
	return await noMonitor.delete(`/sales/analytics/connectors/${connectorId}`).then((res) => res.data);
};

export const getAnalyticsConnectorReport = async (connectorId: string, days = 30) => {
	return await noMonitor.get(`/sales/analytics/connectors/${connectorId}/report?days=${days}`).then((res) => res.data);
};

export const getOpenBankingStatus = async () => {
	return await noMonitor.get('/sales/open-banking/status').then((res) => res.data);
};

export const getOpenBankingInstitutions = async (country: string) => {
	return await noMonitor.get(`/sales/open-banking/institutions?country=${encodeURIComponent(country)}`).then((res) => res.data);
};

export const connectOpenBanking = async (payload: {
	institutionId: string;
	institutionName?: string;
	country?: string;
}) => {
	return await noMonitor.post('/sales/open-banking/connect', payload).then((res) => res.data);
};

export const getOpenBankingConnections = async () => {
	return await noMonitor.get('/sales/open-banking/connections').then((res) => res.data);
};

export const syncOpenBankingConnection = async (connectionId: string) => {
	return await noMonitor.post(`/sales/open-banking/connections/${connectionId}/sync`).then((res) => res.data);
};

export const disconnectOpenBanking = async (connectionId: string) => {
	return await noMonitor.delete(`/sales/open-banking/connections/${connectionId}`).then((res) => res.data);
};

export const getOpenBankingCashActuals = async (year: number) => {
	return await noMonitor.get(`/sales/open-banking/cash-actuals?year=${year}`).then((res) => res.data);
};

export const getOpenBankingTransactions = async (year: number) => {
	return await noMonitor.get(`/sales/open-banking/transactions?year=${year}`).then((res) => res.data);
};

export const setOpenBankingCategory = async (transactionId: string, category: string) => {
	return await noMonitor.patch(`/sales/open-banking/transactions/${transactionId}`, { category }).then((res) => res.data);
};

export const getSalesKanbanStages = async (params?: { storeId?: string }) => {
	const search = new URLSearchParams();
	if (params?.storeId) search.set('storeId', params.storeId);
	const q = search.toString();
	return await noMonitor.get(`/sales/kanban/stages${q ? `?${q}` : ''}`).then((res) => res.data);
};

export const updateSalesKanbanStages = async (params: { storeId?: string; stages: Array<any> }) => {
	return await monitoredRequest.put('/sales/kanban/stages', params).then((res) => res.data);
};

export const getSalesKanbanOrders = async (params?: { storeId?: string; startDate?: string; endDate?: string }) => {
	const search = new URLSearchParams();
	if (params?.storeId) search.set('storeId', params.storeId);
	if (params?.startDate) search.set('startDate', params.startDate);
	if (params?.endDate) search.set('endDate', params.endDate);
	const q = search.toString();
	return await noMonitor.get(`/sales/kanban/orders${q ? `?${q}` : ''}`).then((res) => res.data);
};

export const moveSalesOrderStage = async (
	orderId: string,
	payload: {
		destinationStageId?: string;
		destinationStageName?: string;
		destinationIndex: number;
	}
) => {
	return await monitoredRequest.post(`/sales/kanban/orders/${orderId}/move`, payload).then((res) => res.data);
};

export const analyzeForecast = async (forecastId: string) => {
	return await monitoredRequest.post(`/ai/forecast/analyze/${forecastId}`, {}, { timeout: 120000 }).then(res => res.data)
}

/** `silent` skips the global loading modal (used for autosave) */
export const saveBusinessPlan = async (businessPlanData: any, options: { silent?: boolean } = {}) => {
	const client = options.silent ? noMonitor : monitoredRequest;
	return await client.post('/sales/business-plan/save', businessPlanData).then(res => res.data)
}

export const getBusinessPlans = async () => {
	return await noMonitor.get('/sales/business-plan/list').then(res => res.data)
}

export const getBusinessPlan = async (businessPlanId?: string, title?: string) => {
	const params = new URLSearchParams();
	if (businessPlanId) params.append('businessPlanId', businessPlanId);
	if (title) params.append('title', title);
	const queryString = params.toString();
	return await noMonitor.get(`/sales/business-plan/get${queryString ? '?' + queryString : ''}`).then(res => res.data)
}

export const deleteBusinessPlan = async (businessPlanId: string) => {
	return await monitoredRequest.delete(`/sales/business-plan/${businessPlanId}`).then(res => res.data)
}

export const getBusinessPlanRevisions = async (businessPlanId: string, params: { limit?: number; before?: string } = {}) => {
	return await noMonitor.get(`/sales/business-plan/${businessPlanId}/revisions`, { params }).then(res => res.data)
}

export const getBusinessPlanRevision = async (businessPlanId: string, revisionId: string) => {
	return await noMonitor.get(`/sales/business-plan/${businessPlanId}/revisions/${revisionId}`).then(res => res.data)
}

export const restoreBusinessPlanRevision = async (businessPlanId: string, revisionId: string) => {
	return await noMonitor.post(`/sales/business-plan/${businessPlanId}/revisions/${revisionId}/restore`).then(res => res.data)
}

export const labelBusinessPlanRevision = async (businessPlanId: string, revisionId: string, label: string) => {
	return await noMonitor.patch(`/sales/business-plan/${businessPlanId}/revisions/${revisionId}`, { label }).then(res => res.data)
}

export const conductMarketResearch = async (query: string, context?: string, title?: string) => {
	return await monitoredRequest.post('/ai/market-research', { query, context, title }, { timeout: 120000 }).then(res => res.data)
}

// Document Draft API methods
export const saveDocumentDraft = async (draftData: any) => {
	return await monitoredRequest.post('/documents/draft/save', draftData).then(res => res.data)
}

export const getDocumentDrafts = async () => {
	return await noMonitor.get('/documents/draft/list').then(res => res.data)
}

export const getDocumentDraft = async (draftId?: string, documentId?: string) => {
	const params = new URLSearchParams();
	if (draftId) params.append('draftId', draftId);
	if (documentId) params.append('documentId', documentId);
	const queryString = params.toString();
	return await noMonitor.get(`/documents/draft/get${queryString ? '?' + queryString : ''}`).then(res => res.data)
}

export const deleteDocumentDraft = async (draftId: string) => {
	return await monitoredRequest.delete(`/documents/draft/${draftId}`).then(res => res.data)
}

export const sendInvoice = (invoice: any, mail: { message: string, subject: string }, target: any[] | string) => {
	//Attach invoice to email and send to target

}

export const getInventoryData = async (dataId) => {
	return await noMonitor.get(`/data/inventory/${dataId}`).then(res => res.data)
}

export const getCrmCount = async () => {
	return await noMonitor.get('/sales/get-crm-count').then(res => res.data)
}

export const getActiveCampaigns = async () => {
	return await monitorFail.get('/sales/active-campaigns').then(res => res.data)
}

export const postNewCampaign = async (campaign, id) => {
	return await noMonitor.post('/sales/create-campaign', { campaign: campaign, id }).then(res => res.data)
}

export const getDefaultEmailTemplates = async () => {
	return await noMonitor.get('/templates/get-default-email-templates').then(res => res.data)
}

export const postInternalKnowledge = async (values, onProgress?: (data: any) => void) => {
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Creating knowledge article...',
			progress: 0
		});

		// Use fetch with JSON payload
		fetch(`${USER_API_BASE_URL}/knowledge/add`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(values)
		}).then(response => {
			console.log('Knowledge response received:', response.status, response.headers.get('content-type'));

			if (!response.ok) {
				dispatchSSEProgress({
					status: 'error',
					message: `HTTP error! status: ${response.status}`,
					progress: 0
				});
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is actually SSE
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('text/event-stream')) {
				console.warn('Response is not SSE format, content-type:', contentType);
				// Try to read as regular response
				return response.text().then(text => {
					try {
						const data = JSON.parse(text);
						resolve(data._id || data.id || data);
					} catch (e) {
						reject(new Error('Invalid response format'));
					}
				});
			}

			// React Native doesn't support response.body.getReader(), so fallback to text()
			const reader = response.body?.getReader?.();
			if (!reader || Platform.OS !== 'web') {
				// For React Native, read entire response as text and process SSE manually
				return response.text().then(text => {
					function processLine(line) {
						line = line.trim();
						if (line.startsWith('data: ')) {
							try {
								const jsonData = line.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									console.log('Knowledge SSE data received:', data);

									// Dispatch to AuthModal
									dispatchSSEProgress(data);

									// Call progress callback if provided
									if (onProgress) {
										onProgress(data);
									}

									// Handle different status types
									switch (data.status) {
										case 'starting':
										case 'processing':
										case 'saving':
										case 'progress':
											console.log(`Knowledge creation ${data.status}: ${data.message}`);
											break;
										case 'completed':
											console.log('Knowledge creation completed successfully');
											resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data);
											return;
										case 'error':
											console.error('Knowledge creation error:', data.message);
											reject(new Error(data.message || 'Knowledge creation failed'));
											return;
									}
								}
							} catch (parseError) {
								console.warn('Failed to parse Knowledge SSE data:', line, parseError);
							}
						}
					}

					// Process all lines from the text response
					const lines = text.split('\n');
					let foundCompleted = false;
					let foundError = false;
					
					for (const line of lines) {
						if (foundCompleted || foundError) break;
						const trimmedLine = line.trim();
						if (trimmedLine.startsWith('data: ')) {
							try {
								const jsonData = trimmedLine.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									dispatchSSEProgress(data);
									if (onProgress) {
										onProgress(data);
									}
									if (data.status === 'completed') {
										resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data);
										foundCompleted = true;
									} else if (data.status === 'error') {
										reject(new Error(data.message || 'Knowledge creation failed'));
										foundError = true;
									}
								}
							} catch (parseError) {
								console.warn('Failed to parse Knowledge SSE data:', trimmedLine, parseError);
							}
						}
					}
					
					// If no completed/error status found, try to parse as regular JSON
					if (!foundCompleted && !foundError) {
						try {
							const data = JSON.parse(text);
							resolve(data._id || data.id || data);
						} catch (e) {
							reject(new Error('Invalid response format'));
						}
					}
				}).catch(error => {
					console.error('Knowledge fetch error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Knowledge creation failed',
						progress: 0
					});
					reject(error);
				});
			}

			let buffer = ''; // Buffer for incomplete chunks

			function readStream() {
				reader.read().then(({ done, value }) => {
					if (done) {
						console.log('Knowledge stream ended, buffer:', buffer);
						// Check if we have unprocessed data in buffer
						if (buffer.trim()) {
							processBuffer(buffer);
						}
						return;
					}

					try {
						const chunk = new TextDecoder().decode(value);
						buffer += chunk;

						// Process complete lines
						const lines = buffer.split('\n');
						// Keep the last incomplete line in buffer
						buffer = lines.pop() || '';

						lines.forEach(processLine);

						// Continue reading the stream
						readStream();

					} catch (streamError) {
						console.error('Knowledge stream reading error:', streamError);
						dispatchSSEProgress({
							status: 'error',
							message: streamError.message || 'Stream error occurred',
							progress: 0
						});
						reject(streamError);
					}
				}).catch(error => {
					console.error('Knowledge reader error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Stream reading failed',
						progress: 0
					});
					reject(error);
				});
			}

			function processLine(line) {
				line = line.trim();
				if (line.startsWith('data: ')) {
					try {
						const jsonData = line.substring(6).trim();
						if (jsonData) {
							const data = JSON.parse(jsonData);
							console.log('Knowledge SSE data received:', data);

							// Dispatch to AuthModal
							dispatchSSEProgress(data);

							// Call progress callback if provided
							if (onProgress) {
								onProgress(data);
							}

							// Handle different status types
							switch (data.status) {
								case 'starting':
								case 'processing':
								case 'saving':
								case 'progress':
									console.log(`Knowledge creation ${data.status}: ${data.message}`);
									break;
								case 'completed':
									console.log('Knowledge creation completed successfully');
									resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data); // Return the result
									return;
								case 'error':
									console.error('Knowledge creation error:', data.message);
									reject(new Error(data.message || 'Knowledge creation failed'));
									return;
							}
						}
					} catch (parseError) {
						console.warn('Failed to parse Knowledge SSE data:', line, parseError);
					}
				} else if (line === '' || line.startsWith(':')) {
					// Empty line or comment, ignore
				} else {
					console.log('Non-data Knowledge SSE line:', line);
				}
			}

			function processBuffer(bufferData) {
				bufferData.split('\n').forEach(processLine);
			}

			// Start reading the stream
			readStream();

		}).catch(error => {
			console.error('Knowledge fetch error:', error);
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'Knowledge creation failed',
				progress: 0
			});
			reject(error);
		});
	});
}

export const postExternalKnowledge = async (values, onProgress?: (data: any) => void) => {
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Creating external knowledge article...',
			progress: 0
		});

		// Use fetch with JSON payload
		fetch(`${USER_API_BASE_URL}/knowledge/add`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(values)
		}).then(response => {
			console.log('External Knowledge response received:', response.status, response.headers.get('content-type'));

			if (!response.ok) {
				dispatchSSEProgress({
					status: 'error',
					message: `HTTP error! status: ${response.status}`,
					progress: 0
				});
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is actually SSE
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('text/event-stream')) {
				console.warn('Response is not SSE format, content-type:', contentType);
				// Try to read as regular response
				return response.text().then(text => {
					try {
						const data = JSON.parse(text);
						resolve(data._id || data.id || data);
					} catch (e) {
						reject(new Error('Invalid response format'));
					}
				});
			}

			// React Native doesn't support response.body.getReader(), so fallback to text()
			const reader = response.body?.getReader?.();
			if (!reader || Platform.OS !== 'web') {
				// For React Native, read entire response as text and process SSE manually
				return response.text().then(text => {
					// Process all lines from the text response
					const lines = text.split('\n');
					let foundCompleted = false;
					let foundError = false;
					
					for (const line of lines) {
						if (foundCompleted || foundError) break;
						const trimmedLine = line.trim();
						if (trimmedLine.startsWith('data: ')) {
							try {
								const jsonData = trimmedLine.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									console.log('External Knowledge SSE data received:', data);
									dispatchSSEProgress(data);
									if (onProgress) {
										onProgress(data);
									}
									if (data.status === 'completed') {
										resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data);
										foundCompleted = true;
									} else if (data.status === 'error') {
										reject(new Error(data.message || 'External Knowledge creation failed'));
										foundError = true;
									}
								}
							} catch (parseError) {
								console.warn('Failed to parse External Knowledge SSE data:', trimmedLine, parseError);
							}
						}
					}
					
					// If no completed/error status found, try to parse as regular JSON
					if (!foundCompleted && !foundError) {
						try {
							const data = JSON.parse(text);
							resolve(data._id || data.id || data);
						} catch (e) {
							reject(new Error('Invalid response format'));
						}
					}
				}).catch(error => {
					console.error('External Knowledge fetch error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'External Knowledge creation failed',
						progress: 0
					});
					reject(error);
				});
			}

			let buffer = ''; // Buffer for incomplete chunks

			function readStream() {
				reader.read().then(({ done, value }) => {
					if (done) {
						console.log('External Knowledge stream ended, buffer:', buffer);
						// Check if we have unprocessed data in buffer
						if (buffer.trim()) {
							processBuffer(buffer);
						}
						return;
					}

					try {
						const chunk = new TextDecoder().decode(value);
						buffer += chunk;

						// Process complete lines
						const lines = buffer.split('\n');
						// Keep the last incomplete line in buffer
						buffer = lines.pop() || '';

						lines.forEach(processLine);

						// Continue reading the stream
						readStream();

					} catch (streamError) {
						console.error('External Knowledge stream reading error:', streamError);
						dispatchSSEProgress({
							status: 'error',
							message: streamError.message || 'Stream error occurred',
							progress: 0
						});
						reject(streamError);
					}
				}).catch(error => {
					console.error('External Knowledge reader error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Stream reading failed',
						progress: 0
					});
					reject(error);
				});
			}

			function processLine(line) {
				line = line.trim();
				if (line.startsWith('data: ')) {
					try {
						const jsonData = line.substring(6).trim();
						if (jsonData) {
							const data = JSON.parse(jsonData);
							console.log('External Knowledge SSE data received:', data);

							// Dispatch to AuthModal
							dispatchSSEProgress(data);

							// Call progress callback if provided
							if (onProgress) {
								onProgress(data);
							}

							// Handle different status types
							switch (data.status) {
								case 'starting':
								case 'processing':
								case 'saving':
								case 'validating':
								case 'progress':
									console.log(`External Knowledge creation ${data.status}: ${data.message}`);
									break;
								case 'completed':
									console.log('External Knowledge creation completed successfully');
									resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data); // Return the result
									return;
								case 'error':
									console.error('External Knowledge creation error:', data.message);
									reject(new Error(data.message || 'External Knowledge creation failed'));
									return;
							}
						}
					} catch (parseError) {
						console.warn('Failed to parse External Knowledge SSE data:', line, parseError);
					}
				} else if (line === '' || line.startsWith(':')) {
					// Empty line or comment, ignore
				} else {
					console.log('Non-data External Knowledge SSE line:', line);
				}
			}

			function processBuffer(bufferData) {
				bufferData.split('\n').forEach(processLine);
			}

			// Start reading the stream
			readStream();

		}).catch(error => {
			console.error('External Knowledge fetch error:', error);
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'External Knowledge creation failed',
				progress: 0
			});
			reject(error);
		});
	});
}

export const getInternalKnowledge = async () => {
	return await noMonitor.get('/knowledge/get-internal')
}

export const deleteKnowledgeArticle = async (id) => {
	console.log(id, " is the id, deleting knowledge article");
	return await monitorFail.delete(`/knowledge/delete/${id}`)
}

export const saveKnowledgeContent = async (
	id: string,
	content: string,
	options?: { reason?: 'manual-save' | 'autosave' | 'publish' | 'update' }
) => {
	await noMonitor.post(`/knowledge/update/${id}`, {
		knowledge: content,
		reason: options?.reason || 'manual-save',
	})
}

export const publishKnowledgeArticle = async (
	id: string,
	options?: {
		republish?: boolean;
		copyToAccountOnly?: boolean;
		publishToAccount?: {
			enabled: boolean;
			targetKey?: string | null;
		};
	},
) => {
	await monitoredRequest.post(`/knowledge/publish/${id}`, {
		republish: options?.republish || false,
		copyToAccountOnly: options?.copyToAccountOnly || false,
		publishToAccount: options?.publishToAccount,
	})
}

export const getKnowledgePublishSettings = async () => {
	return await noMonitor.get('/knowledge/publish-settings').then((res) => res.data)
}

export const saveKnowledgePublishSettings = async (payload: {
	enabled: boolean;
	targetKey?: string | null;
	targetLabel?: string | null;
}) => {
	return await monitoredRequest.post('/knowledge/publish-settings', payload).then((res) => res.data)
}

export const unpublishKnowledgeArticle = async (id: string) => {
	await monitoredRequest.post(`/knowledge/unpublish/${id}`)
}

export const updateKnowledgeArticleStatus = async (id: string, status: string) => {
	return await monitoredRequest.post(`/knowledge/status/${id}`, { status }).then(res => res.data)
}

export const getKnowledgeContent = async (id) => {
	return await noMonitor.get(`/knowledge/get/${id}`).then(res => res.data)
}

export const getKnowledgeView = async (id: string) => {
	return await noMonitor.get(`/knowledge/view/${id}`).then(res => res.data)
}

export const addKnowledgeAnnotation = async (id: string, body: { kind: 'comment' | 'highlight'; quote?: string; note?: string }) => {
	return await noMonitor.post(`/knowledge/view/${id}/annotations`, body).then(res => res.data)
}

export const deleteKnowledgeAnnotation = async (id: string, annotationId: string) => {
	return await noMonitor.delete(`/knowledge/view/${id}/annotations/${annotationId}`).then(res => res.data)
}

export const getKnowledgeHistory = async (id: string, limit: number = 25) => {
	return await noMonitor.get(`/knowledge/history/${id}?limit=${limit}`).then(res => res.data)
}

// Monitored URLs (page change / data tracking)
export const getMonitoredUrls = async () => {
	return await noMonitor.get('/knowledge/monitored-urls').then(res => res.data)
}

export const addMonitoredUrl = async (url: string, label?: string) => {
	return await noMonitor.post('/knowledge/monitored-urls', { url, label }).then(res => res.data)
}

export const validateMonitoredUrl = async (url: string) => {
	return await noMonitor.post('/knowledge/monitored-urls/validate', { url }).then(res => res.data)
}

export const deleteMonitoredUrl = async (id: string) => {
	return await noMonitor.delete(`/knowledge/monitored-urls/${id}`).then(res => res.data)
}

export const getTeam = async (companyId) => {
	return await apisObject.getAllUsers(companyId).then(res => res.users)
}

export const getCRMFolders = async () => {
	return await noMonitor.get('/sales/get-crm-folders').then(res => res.data)
}

export const getTemplates = async () => {
	return await noMonitor.get('/templates/get-templates').then(res => res.data)
}

export const getTemplateById = async (type, id) => {
	return await noMonitor.get(`/templates/get-template/${type}/${id}`).then(res => res.data)
}

export const updateWorkPackageDue = async (wpId: string, projectId: string, date: Object) => {
	return await noMonitor.post(`/projects/${projectId}/change-task-due-date`, { wpId: wpId, date })
}

export const updateTaskDueDate = async (taskId: string, wpId: string, projectId: string, date: Date | string | null) => {
	return await noMonitor.post(`/projects/${projectId}/change-task-due-date`, { taskId, wpId, date })
}

export const saveDataApprovalSettings = async (dataId: string, body: { enabled: boolean; fieldKeys?: string[]; approverIds?: string[] }) => {
	return noMonitor.put(`/data/${dataId}/approval-settings`, body).then(res => res.data);
};

export const saveDataLifecycle = async (dataId: string, status: 'draft' | 'published') => {
	return noMonitor.put(`/data/${dataId}/lifecycle`, { status }).then(res => res.data);
};

export const saveWelcomeEmail = async (dataId: string, body: { enabled: boolean; subject?: string; body?: string; emailField?: string }) => {
	return noMonitor.put(`/data/${dataId}/welcome-email`, body).then(res => res.data);
};

export const getEditApprovals = async (dataId?: string) => {
	return noMonitor.get('/data/edit-approvals', { params: { dataId } }).then(res => res.data);
};

export const decideEditApproval = async (id: string, decision: 'approved' | 'rejected', note?: string) => {
	return noMonitor.post(`/data/edit-approvals/${id}/decide`, { decision, note }).then(res => res.data);
};

export const saveCampaignAbTest = async (campaignId: string, body: { enabled?: boolean; metric?: string; variants: Array<{ key: string; label?: string; subject?: string; body?: string; weight?: number }> }) => {
	return noMonitor.put(`/sales/campaigns/${campaignId}/ab-test`, body).then(res => res.data);
};

export const getCampaignEffectiveness = async (campaignId: string) => {
	return noMonitor.get(`/sales/campaigns/${campaignId}/effectiveness`).then(res => res.data);
};

export const shareCampaignUpdate = async (campaignId: string, emails: string[], message: string) => {
	return noMonitor.post(`/sales/campaigns/${campaignId}/share-update`, { emails, message }).then(res => res.data);
};

export const linkCampaignProducts = async (campaignId: string, productIds: string[]) => {
	return noMonitor.put(`/sales/campaigns/${campaignId}/products`, { productIds }).then(res => res.data);
};

export const getKnowledgeContributions = async () => {
	return noMonitor.get('/reports/knowledge-contributions').then(res => res.data);
};

export const createZoomMeeting = async (meetingId: string) => {
	return noMonitor.post(`/meetings/${meetingId}/zoom`).then(res => res.data);
};

export const saveMeetingNotes = async (meetingId: string, body: { summary?: string; actionItems?: string[]; nextSteps?: string[]; shareWith?: string[] }) => {
	return noMonitor.patch(`/meetings/${meetingId}/notes`, body).then(res => res.data);
};

export const syncInvoiceAccounting = async (invoiceId: string, provider: 'xero' | 'quickbooks', realmId?: string) => {
	return noMonitor.post(`/invoices/${invoiceId}/sync-accounting`, { provider, realmId }).then(res => res.data);
};

export const setInvoiceAutoReminders = async (invoiceId: string, enabled: boolean) => {
	return noMonitor.patch(`/invoices/${invoiceId}/auto-reminders`, { enabled }).then(res => res.data);
};

export const listOneDriveFiles = async (folderId?: string) => {
	return noMonitor.get('/integrations/onedrive/files', { params: folderId ? { folderId } : undefined }).then(res => res.data);
};

export const downloadOneDriveFile = async (itemId: string) => {
	return noMonitor.get(`/integrations/onedrive/files/${itemId}/content`).then(res => res.data);
};

export const getStoreShareLink = async () => {
	return noMonitor.get('/store/share-link').then(res => res.data);
};

export const pushContactToCrm = async (provider: 'hubspot' | 'salesforce', contact: { email?: string; firstName?: string; lastName?: string; name?: string; phone?: string }) => {
	return noMonitor.post('/integrations/crm/push', { provider, contact }).then(res => res.data);
};

export const getCampaign = async (campaignId: string, page: number = 1, limit: number = 50, stageIndex?: number) => {
	return await noMonitor.post(`/sales/get-campaign`, {
		campaignId,
		page,
		limit,
		stageIndex
	}).then(res => res.data)
}

// Sales Funnel API Functions
export const moveLeadToStage = async (leadId: string, fromStage: string, toStage: string, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/move-lead`, {
		leadId,
		fromStage,
		toStage,
		campaignId
	}).then(res => res.data);
};

export const updateLeadData = async (leadId: string, leadData: any, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/update-lead`, {
		leadId,
		leadData,
		campaignId
	}).then(res => res.data);
};

export const deleteLeadFromCampaign = async (leadId: string, campaignId: string) => {
	return await monitoredRequest.delete(`/sales/funnel/remove-lead`, {
		data: { leadId, campaignId }
	}).then(res => res.data);
};

export const addLeadToCampaign = async (leadData: any, campaignId: string, stageId?: string) => {
	return await monitoredRequest.post(`/sales/funnel/add-lead`, {
		leadData,
		campaignId,
		stageId
	}).then(res => res.data);
};

export const updateCampaignStage = async (stageId: string, stageData: any, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/update-stage`, {
		stageId,
		stageData,
		campaignId
	}).then(res => res.data);
};

export const addCampaignStage = async (stageData: any, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/add-stage`, {
		stageData,
		campaignId
	}).then(res => res.data);
};

export const deleteCampaignStage = async (stageId: string, campaignId: string) => {
	return await monitoredRequest.delete(`/sales/funnel/remove-stage`, {
		data: { stageId, campaignId }
	}).then(res => res.data);
};

export const getCampaignAnalytics = async (campaignId: string, dateRange?: { start: string, end: string }) => {
	return await noMonitor.post(`/sales/funnel/analytics`, {
		campaignId,
		dateRange
	}).then(res => res.data);
};

export const getLeadHistory = async (leadId: string, campaignId: string) => {
	return await noMonitor.post(`/sales/funnel/lead-history`, {
		leadId,
		campaignId
	}).then(res => res.data);
};

export const bulkMoveLeads = async (leadIds: string[], fromStage: string, toStage: string, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/bulk-move-leads`, {
		leadIds,
		fromStage,
		toStage,
		campaignId
	}).then(res => res.data);
};

export const exportCampaignData = async (campaignId: string, format: 'csv' | 'excel' | 'json' = 'csv') => {
	return await noMonitor.post(`/sales/funnel/export`, {
		campaignId,
		format
	}).then(res => res.data);
};

export const importLeadsToCampaign = async (campaignId: string, leadsData: any[], stageId?: string) => {
	return await monitoredRequest.post(`/sales/funnel/import-leads`, {
		campaignId,
		leadsData,
		stageId
	}).then(res => res.data);
};

export const updateCampaignSettings = async (campaignId: string, settings: any) => {
	return await monitoredRequest.post(`/sales/funnel/update-settings`, {
		campaignId,
		settings
	}).then(res => res.data);
};

export const searchDataRegEx = async (dataId, query: string, keys) => {
	return await noMonitor.post('/data/search-folder', { dataId, query: query, keys })
}

export const handleChangeTaskDescription = async (id, description) => {
	return await noMonitor.post(`/projects/change-task-description`, { id, description })
}

export const handleChangeTaskTitle = async (id, title) => {
	return await noMonitor.post(`/projects/change-task-title`, { id, title })
}

export const changeTaskPosition = async (id, status) => {
	return await monitorFail.post(`/projects/change-task-position`, { id, status })
}

export const modifyTask = async (id, task) => {
	const { dateRange, ...rest } = task;
	rest.start_date = dateRange?.startDate;
	rest.end_date = dateRange?.endDate;
	const newTask = {
		...rest,
		dateRange: undefined
	}

	// Backend expects: { id: { _id: taskId, ...taskObject }, task: { dataId: projectId, extraId: workPackageId } }
	return await monitorFail.post(`/projects/modify-task`, { 
		id: { _id: id._id || id, ...newTask }, 
		task: { dataId: id.dataId, extraId: id.extraId } 
	})
}

/** Cancel in-flight agent attempt or discard finished attempt results on a task. */
export const resolveTaskAgentAttempt = async ({
	projectId,
	workPackageId,
	taskId,
	attemptId,
	action,
}: {
	projectId: string;
	workPackageId: string;
	taskId: string;
	attemptId: string;
	action: 'cancel' | 'discard';
}) => {
	return await monitorFail.post(
		`/projects/${projectId}/work-packages/${workPackageId}/${taskId}/agent-attempt`,
		{ attemptId, action }
	);
}

/** Assign a task to a deployed agent for a best-effort attempt (paid plans; 402 otherwise). */
export const assignTaskToAgent = async ({
	projectId,
	workPackageId,
	taskId,
	agentId,
}: {
	projectId: string;
	workPackageId: string;
	taskId: string;
	agentId: string;
}) => {
	return await api.post(
		`/projects/${projectId}/work-packages/${workPackageId}/${taskId}/assign-agent`,
		{ agentId }
	);
}

// ===========================================
// MODERN PROJECT MANAGEMENT API FUNCTIONS
// ===========================================

// Kanban Board APIs
export const moveKanbanTask = async (projectId: string, taskData: { taskId: string; fromStatus: string; toStatus: string; position: number }) => {
	return await api.post(`/projects/${projectId}/kanban/move-task`, taskData)
}

export const bulkKanbanActions = async (projectId: string, actionData: { taskIds: string[]; action: string; newStatus?: string; assigneeId?: string }) => {
	return await api.post(`/projects/${projectId}/kanban/bulk-actions`, actionData)
}

export const getKanbanColumns = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/kanban/columns`)
}

// Gantt Chart APIs
export const getGanttData = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/gantt/data`)
}

export const updateGanttTimeline = async (projectId: string, timelineData: any) => {
	return await api.post(`/projects/${projectId}/gantt/update-timeline`, timelineData)
}

export const manageTaskDependencies = async (projectId: string, dependencyData: any) => {
	return await api.post(`/projects/${projectId}/gantt/dependencies`, dependencyData)
}

// Analytics APIs
export const getProjectAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics`)
}

export const getProgressAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics/progress`)
}

export const getTeamPerformanceAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics/team-performance`)
}

export const getTimeTrackingAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics/time-tracking`)
}

// Team Management APIs
export const addTeamMember = async (projectId: string, teamMembers: any[]) => {
	return await api.post('/projects/add-team-member', { projectId, teamMembers })
}

export const inviteTeamMember = async (projectId: string, inviteData: { email: string; role: string }) => {
	return await api.post(`/projects/${projectId}/team/invite`, inviteData)
}

export const updateTeamMemberRole = async (projectId: string, memberId: string, roleData: { role: string }) => {
	return await api.patch(`/projects/${projectId}/team/${memberId}/role`, roleData)
}

export const updateTeamMemberNotifications = async (projectId: string, memberId: string, notificationData: { notifications: any }) => {
	return await api.patch(`/projects/${projectId}/team/${memberId}/notifications`, notificationData)
}

export const removeTeamMember = async (projectId: string, memberId: string) => {
	return await api.delete(`/projects/${projectId}/team/${memberId}`)
}

// File Management APIs  
export const uploadProjectFiles = async (projectId: string, fileData: {
	name: string;
	type: string;
	size?: number;
	fileData: string; // base64 encoded
}) => {
	return await monitoredRequest.post(`/projects/${projectId}/files/upload`, fileData).then(res => res.data)
}

export const getProjectFiles = async (projectId: string) => {
	return await monitoredRequest.get(`/projects/${projectId}/files`).then(res => {
		// Handle both response formats: { success: true, data: [...] } or direct array
		// The interceptor may flatten nested data, so check both locations
		if (Array.isArray(res.data)) {
			return { data: res.data };
		}
		return res.data;
	})
}

export const deleteProjectFile = async (projectId: string, documentId: string) => {
	return await monitoredRequest.delete(`/projects/${projectId}/files/${documentId}`).then(res => res.data)
}

export const downloadProjectFile = async (projectId: string, fileId: string) => {
	return await api.get(`/projects/${projectId}/files/${fileId}/download`, { responseType: 'blob' })
}

// Project Settings APIs
export const updateProjectSettings = async (projectId: string, settings: any) => {
	return await api.patch(`/projects/${projectId}/settings`, settings)
}

// Enhanced Task Management APIs
export const bulkCreateTasks = async (projectId: string, tasksData: any[]) => {
	return await api.post(`/projects/${projectId}/tasks/bulk-create`, { tasks: tasksData })
}

export const addTaskComment = async (projectId: string, taskId: string, commentData: { content: string }) => {
	return await api.post(`/projects/${projectId}/tasks/${taskId}/comments`, commentData)
}

export const getTaskComments = async (projectId: string, taskId: string) => {
	return await api.get(`/projects/${projectId}/tasks/${taskId}/comments`)
}

export const logTaskTime = async (projectId: string, taskId: string, timeData: { hours: number; description?: string }) => {
	return await api.post(`/projects/${projectId}/tasks/${taskId}/time-log`, timeData)
}

export const getDataTemplateCount = async () =>{
	return await noMonitor.get('/templates/count').then(res => res.data)
}

export const setEmailGlobal = async (id: string) => {
	return await monitorFail.post('/templates/make-email-global', { id: id })
}

export const getGlobalAssets = async (includeMetadata = true) => {
	const url = '/templates/get-media?metadata=true';
	return await monitorFail.get(url).then(res => {
		const data = res.data;
		// When includeMetadata is false, return just URL strings for GrapesJS Asset Manager
		if (!includeMetadata && Array.isArray(data)) {
			return data.map(asset => typeof asset === 'string' ? asset : asset.url).filter(Boolean);
		}
		// Otherwise return full metadata
		return data;
	})
}

export type SendTemplateToCrmPayload = {
	templateType: 'email' | 'invoice' | 'document' | 'webpage' | 'calendar';
	templateId: string;
	contactIds: string[];
	channel: 'email' | 'whatsapp' | 'telegram' | 'sms';
	message?: string;
	title?: string;
};

export const sendTemplateToCrm = async (payload: SendTemplateToCrmPayload) => {
	return await monitoredRequest.post('/templates/send-to-crm', payload).then((res) => res.data);
};

export const sendDocumentEmail = async (documentId: string, recipientEmail: string, message = '', includeData = false) => {
	return await api.post('/data/send-document-email', {
		documentId,
		recipientEmail,
		message,
		includeData
	}).then(res => res.data);
};

export const getDocumentShareHistory = async (documentId: string) => {
	return await api.get(`/data/share-history/${documentId}`).then(res => res.data);
};

export const uploadGlobalAsset = async (asset) => {
	return await monitorFail.post('/templates/upload-media', { files: [asset] }).then(res => {
		const data = res.data;
		// Transform to array of URL strings for GrapesJS Asset Manager
		if (Array.isArray(data)) {
			return data.map(asset => typeof asset === 'string' ? asset : asset.url).filter(Boolean);
		} else if (typeof data === 'object' && data.url) {
			return [data.url];
		}
		return data;
	})
}

export const updateGlobalAsset = async (id: string, data: any) => {
	return await monitorFail.put(`/templates/media/${encodeURIComponent(id)}`, data).then(res => res.data);
}

export const deleteGlobalAsset = async (id: string) => {
	return await monitorFail.delete(`/templates/media/${encodeURIComponent(id)}`).then(res => res.data);
}

export const deletePost = async (id) => {
	return await monitoredRequest.delete(`/feed/${id}`)
}

export const getTargetAudience = async (id) => {
}

export const revokeGoogleScope = async (scope: string, email?: string) => {
	const { getIntegrationOAuthRedirectUri } = await import('../src/navigation/screens/MidLevel/IntegrationsScreen/utils/integrationOAuthRedirect');
	const redirectUri = getIntegrationOAuthRedirectUri();

	return await monitoredRequest.post(`/google/revoke-access`, { revokeTarget: scope, email, redirectUri }).then(async res => {
		console.log(res.data, 'from revoke');
		if (res.data.authURL) {
			const WebBrowser = await import('expo-web-browser');
			const result = await WebBrowser.openAuthSessionAsync(res.data.authURL, redirectUri);
			if (result.type === 'success' && result.url) {
				const parsed = Linking.parse(result.url);
				if (parsed.queryParams?.code) {
					await monitoredRequest.post('/google/revoke-callback', { code: parsed.queryParams.code, redirectUri });
					// Force a refresh of the user data to update the UI
					store.dispatch({ type: PENDING_REFRESH, payload: true });
				}
			}
		} else if (res.data.success) {
			// Force a refresh of the user data to update the UI if revoked directly
			store.dispatch({ type: PENDING_REFRESH, payload: true });
		}
	})
}

export const authoriseInstagram = async (data) => {
	return await monitorFail.post(`/instagram/authorise`, data)
}

export const authCallback = async (code: string, integration: string, scopes: string[], verifier: any) => {
	return await monitoredRequest.post('/integrations/auth/callback', { scopes, code: code, integration: integration, codeVerifier: verifier }).then(res => res.data)
}

export const searchCompanies = async (query: string) => {
	return await monitorFail.get(`/companies/search/${query}`).then(res => res.data)
}

export const getUserRecents = async () => {
	return await noMonitor.get('/user/recents').then(res => res.data)
}

export const addRecent = async (id, dataType) => {
	return await noMonitor.post('/user/recent/add', { itemId: id, itemType: dataType })
}

export const getTrending = async (country: string = 'GB') => {
	return await noMonitor.get(`/trends/${country || 'GB'}`).then(res => res.data)
}

export const getFeedCategories = async () => {
	return await noMonitor.get('/feed/categories').then(res => res.data)
}

export const aiCommentAnalysis = async (comments, postContent) => {
	return await noMonitor.post('/ai/comment-analysis', { comments, postContent }, { timeout: 120000 })
}

export const getUserStats = async () => {
	// Backend getAllUserStatistics returns { allStats: count } which is just a number
	// We need categorizedData format, so we'll try to get stats without routeType filter
	// If backend doesn't support this, we may need to aggregate multiple routeTypes
	// For now, try omitting routeType - backend query may handle undefined differently
	try {
		// Try without routeType first - if backend supports it
		const res = await noMonitor.get('/user/get-statistics?filterType=all').then(res => res.data);
		// If response has categorizedData, return it
		if (res && res.categorizedData) {
			return res;
		}
	// Otherwise fallback to get-all-statistics (though it returns wrong format)
		return await noMonitor.get('/user/get-all-statistics').then(res => res.data);
	} catch (error) {
		console.error('Error in getUserStats:', error);
		// Fallback to get-all-statistics
		return await noMonitor.get('/user/get-all-statistics').then(res => res.data);
	}
}

/** Normalize /user/get-statistics payloads for UI. Backend categorizeData returns day/month/year buckets — not allStats. */
export function parseGetUserStatsPayload(res: any): {
	stats: any[];
	userCreated: string | Date | null;
} {
	const userCreated = res?.userCreated ?? res?.user?.createdAt ?? null;
	if (!res) {
		return { stats: [], userCreated: null };
	}
	const allStats = res.categorizedData?.allStats;
	if (Array.isArray(allStats)) {
		const stats = allStats.map((item: any) => ({
			routeType: item.metadata?.routeType || 'unknown',
			routeTo: item.routeTo || item.metadata?.routeTo || 'unknown',
			timestamp: item.timestamp || new Date(),
		}));
		return { stats, userCreated };
	}
	if (res.allStats !== undefined) {
		return { stats: [], userCreated };
	}
	if (res.categorizedData) {
		return { stats: [], userCreated };
	}
	return { stats: [], userCreated };
}

export const getUserStatsData = async (routeType, startDate, endDate, filterType) => {
	// Properly encode query parameters
	const params = new URLSearchParams({
		routeType: routeType || 'user',
		startDate: startDate || '',
		endDate: endDate || '',
		filterType: filterType || 'month'
	});
	return await noMonitor.get(`/user/get-statistics?${params.toString()}`).then(res => res.data);
}

export const getAIUsageStats = async () => {
	return await noMonitor.get('/ai/usage-stats', { timeout: 120000 }).then(res => res.data);
}

/** Easy Mode rolling memory (conversation summaries, digest, tool usage) for session user + company */
export const getAiMemory = async () => {
	return await noMonitor.get('/ai/memory').then(res => res.data);
}

export const clearAiMemory = async () => {
	return await noMonitor.delete('/ai/memory').then(res => res.data);
}

/** Notes the assistant keeps in mind ("remember that…"), editable by the user. */
export const addAiMemoryFact = async (text: string) => {
	return await noMonitor.post('/ai/memory/facts', { text }).then(res => res.data);
}

export const updateAiMemoryFact = async (id: string, text: string) => {
	return await noMonitor.patch(`/ai/memory/facts/${encodeURIComponent(id)}`, { text }).then(res => res.data);
}

export const deleteAiMemoryFact = async (id: string) => {
	return await noMonitor.delete(`/ai/memory/facts/${encodeURIComponent(id)}`).then(res => res.data);
}

/** Estimated AI spend today / this month and the user's own spending limits. */
export const getAiCost = async () => {
	return await noMonitor.get('/ai/cost').then(res => res.data);
}

export const setAiCostCaps = async (caps: { dailyUsd: string | number | null; monthlyUsd: string | number | null }) => {
	return await noMonitor.put('/ai/cost/caps', caps).then(res => res.data);
}

export const getAiCostMembers = async () => {
	return await noMonitor.get('/ai/cost/members').then(res => res.data);
}

export const setMemberAiCostCaps = async (
	userId: string,
	caps: { dailyUsd?: string | number | null; monthlyUsd?: string | number | null }
) => {
	return await noMonitor.put(`/ai/cost/members/${encodeURIComponent(userId)}/caps`, caps).then(res => res.data);
}

export const getApiCallHistory = async (startDate?: string, endDate?: string, limit = 100, offset = 0) => {
	const params = new URLSearchParams();
	if (startDate) params.append('startDate', startDate);
	if (endDate) params.append('endDate', endDate);
	params.append('limit', limit.toString());
	params.append('offset', offset.toString());
	return await noMonitor.get(`/user/api-call-history?${params.toString()}`).then(res => res.data);
}

export const getApiCallsOverTime = async (startDate?: string, endDate?: string, interval = 'day', routeType?: string) => {
	const params = new URLSearchParams();
	if (startDate) params.append('startDate', startDate);
	if (endDate) params.append('endDate', endDate);
	params.append('interval', interval);
	if (routeType) params.append('routeType', routeType);
	return await noMonitor.get(`/user/api-calls-over-time?${params.toString()}`).then(res => res.data);
}

export const getConnectedIntegrationsCount = async () => {
	return await noMonitor.get('/user/connected-integrations-count').then(res => res.data);
}

/** AI observability logs from OpenLIT / ai_usage_events (backend proxies to MongoDB) */
export const getAiLogs = async (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) => {
	const search = new URLSearchParams();
	if (params?.limit != null) search.append('limit', String(params.limit));
	if (params?.offset != null) search.append('offset', String(params.offset));
	if (params?.startDate) search.append('startDate', params.startDate);
	if (params?.endDate) search.append('endDate', params.endDate);
	const qs = search.toString();
	return await noMonitor.get(`/observability/ai-logs${qs ? '?' + qs : ''}`).then((res) => res.data);
}

export const getKnowledgeByViews = async () => {
	return await noMonitor.get('/knowledge/by-views').then(res => res.data)
}

export const cloneData = async (dataId, targetId) => {
	return await noMonitor.post(`/data/clone`, { dataId, targetId }).then(res => res.data)
}

export const transferData = async (dataId, targetId) => {
	return await noMonitor.post(`/data/transfer`, { dataId, targetId }).then(res => res.data)
}

export type OwnershipTransferTarget =
	| { targetType: 'user'; targetUser: string }
	| { targetType: 'company'; targetCompanyId: string };

/** Paid plans only — the server answers 402 (`TRANSFER_ENTITLEMENT_REQUIRED`) otherwise. */
export const transferDataOwnership = async (dataId: string, target: OwnershipTransferTarget) => {
	return await noMonitor.post(`/data/transfer-ownership`, { dataId, ...target }).then(res => res.data)
}

export const transferProjectOwnership = async (projectId: string, target: OwnershipTransferTarget) => {
	return await noMonitor.post(`/projects/transfer-ownership`, { projectId, ...target }).then(res => res.data)
}

export const cancelUserCreation = async (userId: string) => {
	return await noMonitor.post("/user/cancel-creation", { userId })
}

export const userLoginHistory = async () => {
	return await noMonitor.post("/user/login-history").then(res => res.data)
}

export const getCrmItems = async (id: string) => {
	return await noMonitor.post(`/sales/crm/individual`, { id }).then(res => res.data)
}

export const getCrmClients = async () => {
	try {
		const foldersRes = await getCRMFolders();
		const folders = foldersRes?.dataSets || foldersRes?.data?.dataSets || foldersRes || [];
		const folderList = Array.isArray(folders) ? folders : [];
		const collected: any[] = [];
		for (const folder of folderList.slice(0, 8)) {
			const folderId = folder?._id || folder?.value || folder?.id;
			if (!folderId) continue;
			const items = await getCrmItems(String(folderId)).catch(() => []);
			if (Array.isArray(items)) collected.push(...items);
		}
		return collected;
	} catch (error) {
		console.error('Error loading CRM clients:', error);
		return [];
	}
}

export const uploadDataImage = async () => {
	return await noMonitor.post(`/data/upload-image`).then(res => res.data)
}

export const getMainCharts = async (startDate, endDate) => {
	return await noMonitor.get(`/reports/combined-data?startDate=${startDate}&endDate=${endDate}`).then(res => res.data)
}

export const requestDataDownload = async (dataId: string) => {
	return await monitoredRequest.post(`/data/request-download`, { dataId })
		//Interpret pipe
		.then(res => {
			//Open download link
			console.log(res.data, 'from download');
		}).catch(e => console.error(e))
}

export const getStartupSchemas = async () => {
	return await noMonitor.get('/company/startup-schemas').then(res => { console.log(res.data); return res.data })
}

export const startCampaign = async (campaignId: string) => {
	return await monitoredRequest.post(`/campaigns/schedule/start-campaign`, { campaignId }).then(res => res.data)
}

export const changeDataTitle = async (dataId: string, title: string) => {
	return await noMonitor.post(`/data/change-title`, { dataId, title }).then(res => res.data)
}

export const createAffiliateCode = async () => {
	return await noMonitor.post('/affiliate/create-code').then(res => res.data)
}

export const getAffiliateHistory = async (code, dateRange) => {
	return await noMonitor.post('/user/affiliate/activity', { code, dateRange }).then(res => res.data)
}

export type AffiliatePayoutProviderStatus = {
	provider: string;
	label: string;
	connected: boolean;
	ready: boolean;
	reason: 'not_connected' | 'onboarding_incomplete' | 'account_unreachable' | null;
	requirementsDue?: number;
	defaultCurrency?: string | null;
};

export type AffiliatePayoutAccount = {
	providers: AffiliatePayoutProviderStatus[];
	active: AffiliatePayoutProviderStatus | null;
	ready: boolean;
	holdDays: number;
	minimumAmount: number;
	payoutCurrency: string;
	payoutsEnabled: boolean;
};

export const getAffiliatePayoutAccount = async (): Promise<AffiliatePayoutAccount> => {
	return await noMonitor.get('/user/affiliate/payout-account').then(res => res.data)
}

export const deleteAccount = async () => {
	return await monitoredRequest.delete('/user/delete-user').then((res) => res.data)
}

export const getAccountDeletionPolicy = async () => {
	return await monitoredRequest.get('/user/account-deletion-policy').then((res) => res.data)
}

export const getCompanyArchivePolicy = async () => {
	return await monitoredRequest.get('/company/archive-policy').then((res) => res.data)
}

/** Shared regulatory / gov URL monitor — active sources (session auth). */
export const getRegulatoryMonitorSources = async () => {
	return await api.get('/regulatory-monitor/sources').then((res) => res.data);
};

export const getRegulatoryMonitorPrefs = async () => {
	return await api.get('/regulatory-monitor/prefs').then((res) => res.data);
};

export const patchRegulatoryMonitorPrefs = async (body: {
	regulatoryAlertsMuted?: boolean;
	regulatoryAlertsOptIn?: boolean;
	mutedTags?: string[];
}) => {
	return await api.patch('/regulatory-monitor/prefs', body).then((res) => res.data);
};

export const getCompanyArchiveEligibility = async (companyId: string) => {
	return await monitoredRequest
		.get('/company/archive-eligibility', { params: { companyId } })
		.then((res) => res.data)
}

export const requestCompanyArchive = async (companyId: string, reason?: string) => {
	return await monitoredRequest.post('/company/archive', { companyId, reason }).then((res) => res.data)
}

export const addDepartment = async (data) => {
	return await noMonitor.post('/company/add-department', data).then(res => res.data)
}

export const updateDepartment = async (data: { oldName: string; newName?: string; level?: number }) => {
	return await noMonitor.post('/company/update-department', data).then(res => res.data)
}

export const setCompanyName = async (companyId: string, companyName: string) => {
	return await noMonitor.post('/company/set-name', { companyId, company_name: companyName }).then(res => res.data)
}

export const addCompanyLocation = async (companyId: string, location: string) => {
	return await noMonitor.patch('/company/add-location', { companyId, location }).then(res => res.data)
}

export const resetCompanyPositions = async (companyId: string) => {
	return await noMonitor.post('/company/reset-positions', { companyId }).then(res => res.data)
}

export const addUserToDepartment = async (data: any, departmentId: string) => {
	return await noMonitor.post('/company/assign-department', {data, departmentId}).then(res => res.data)
}

export const addDataDescription = async (dataId: string, description: string) => {
	return await noMonitor.post(`/data/add-description`, { dataId, description }).then(res => res.data)
}

export const updateCalendarSettings = async (data: any, calendarId: string) => {
	return await noMonitor.post('/calendar/update-settings', { data, calendarId })
}

export const updateCalendarQuickSettings = async (data: any, calendarId: string) => {
	return await noMonitor.post('/calendar/update-quick-settings', { data, calendarId })
}

export const postDataNote = async (dataId: any, message: any) => {
	return await noMonitor.post('/data/add-notes', { dataId, message })
}

export const updateDataActions = async (dataId: any, actions: any, options?: { silent?: boolean }) => {
	if(!dataId){
		throw new Error('Data ID is required');
	}
	const client = options?.silent ? noMonitor : monitoredRequest;
	return await client.post(`/data/update-actions/${dataId}`, { actions })
}

export const invokeFlow = async (dataId: string, actions?: any[], testData?: any, options?: { poll?: boolean }) => {
	if (!dataId) {
		throw new Error('Data ID is required');
	}
	const body = { actions, testData, poll: options?.poll ?? false };
	// Use noMonitor when poll is true so the flow test doesn't trigger the loading modal (we rely on the Invoking... button state)
	const client = options?.poll ? noMonitor : monitoredRequest;
	return await client.post(`/data/invoke-flow/${dataId}`, body).then(res => res.data);
}

/** Poll flow test run status (when invokeFlow was called with poll: true). Returns { status, stageIndex, logs, error, actionsCount }. Uses noMonitor so polling does not trigger the loading modal. */
export const getFlowRunStatus = async (runId: string) => {
	if (!runId) throw new Error('runId is required');
	const t0 = Date.now();
	const res = await noMonitor.get(`/data/invoke-flow-status/${runId}`);
	// Interceptor may flatten res.data.data into res.data, so status can be at res.data.status or res.data.data.status
	const raw = res.data?.data ?? res.data ?? {};
	const parsed = {
		status: (raw.status ?? res.data?.status ?? 'running').toLowerCase(),
		stageIndex: raw.stageIndex ?? res.data?.stageIndex ?? 0,
		logs: raw.logs ?? res.data?.logs ?? [],
		error: raw.error ?? res.data?.error ?? null,
		actionsCount: raw.actionsCount ?? res.data?.actionsCount,
	};
	// Debug: log when terminal status (helps trace when backend completes vs when frontend resolves)
	if (parsed.status === 'completed' || parsed.status === 'error') {
		console.log(`[FlowTest] getFlowRunStatus DONE in ${Date.now() - t0}ms`, parsed);
	}
	return parsed;
}

// Workflow Management API functions
export const getWorkflows = async (params?: { active?: boolean; category?: string; tags?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.active !== undefined) queryParams.append('active', String(params.active));
	if (params?.category) queryParams.append('category', params.category);
	if (params?.tags) queryParams.append('tags', params.tags);
	const query = queryParams.toString();
	return await api.get(`/workflows${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getWorkflow = async (workflowId: string) => {
	return await api.get(`/workflows/${workflowId}`).then(res => res.data);
}

export const createWorkflow = async (workflowData: any) => {
	return await api.post('/workflows', workflowData).then(res => res.data);
}

export const updateWorkflow = async (workflowId: string, workflowData: any) => {
	return await api.put(`/workflows/${workflowId}`, workflowData).then(res => res.data);
}

export const deleteWorkflow = async (workflowId: string) => {
	return await api.delete(`/workflows/${workflowId}`).then(res => res.data);
}

export const executeWorkflow = async (workflowId: string, triggerData?: any) => {
	return await api.post(`/workflows/${workflowId}/execute`, { triggerData }).then(res => res.data);
}

export const getWorkflowExecutions = async (workflowId: string, params?: { limit?: number; status?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.status) queryParams.append('status', params.status);
	const query = queryParams.toString();
	return await api.get(`/workflows/${workflowId}/executions${query ? `?${query}` : ''}`).then(res => res.data);
}

export const retryWorkflowExecution = async (workflowId: string, executionId: string) => {
	return await api.post(`/workflows/${workflowId}/retry/${executionId}`).then(res => res.data);
}

export const scheduleWorkflow = async (workflowId: string, cronExpression: string, timezone?: string) => {
	return await api.post(`/workflows/${workflowId}/schedule`, { cronExpression, timezone }).then(res => res.data);
}

export const unscheduleWorkflow = async (workflowId: string) => {
	return await api.delete(`/workflows/${workflowId}/schedule`).then(res => res.data);
}

export const getWorkflowWebhooks = async (workflowId: string) => {
	return await api.get(`/workflows/${workflowId}/webhooks`).then(res => res.data);
}

// Agentic Flow API functions
export const createAgenticFlow = async (dataId: string, flowConfig: any) => {
	return await api.post('/agentic-flow/create', { dataId, flowConfig }).then(res => res.data);
}

export const getAgenticFlows = async (dataId: string) => {
	return await api.get(`/agentic-flow/${dataId}`).then(res => res.data);
}

export const getAgenticFlow = async (dataId: string, flowId: string) => {
	return await api.get(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
}

export const updateAgenticFlow = async (dataId: string, flowId: string, flowConfig: any) => {
	return await api.put(`/agentic-flow/${dataId}/${flowId}`, { flowConfig }).then(res => res.data);
}

export const deleteAgenticFlow = async (dataId: string, flowId: string) => {
	return await api.delete(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
}

export const triggerAgenticFlow = async (dataId: string, flowId: string, inputData?: any, context?: any) => {
	return await api.post(`/agentic-flow/${dataId}/${flowId}/trigger`, { inputData, context }).then(res => res.data);
}

/** Runs the flow's reasoning without carrying out any actions; `flowConfig` lets an unsaved draft be tested. */
export const testAgenticFlow = async (dataId: string, flowId: string, flowConfig?: Record<string, unknown>, inputData?: Record<string, unknown>) => {
	return await api.post(`/agentic-flow/${dataId}/${flowId}/trigger`, { inputData: inputData || {}, flowConfig, dryRun: true }).then(res => res.data);
}

const buildAgenticFlowTelemetry = () => ({
	clientTimestamp: new Date().toISOString(),
	clientPlatform: Platform.OS,
	timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
});

export const executeAgenticFlow = async (flowId: string, inputData?: any, context?: any) => {
	const mergedContext = {
		...(context || {}),
		telemetryHints: {
			...((context || {}).telemetryHints || {}),
			...buildAgenticFlowTelemetry()
		}
	};
	return await api.post('/agentic-flow/execute', { flowId, inputData, context: mergedContext }).then(res => res.data);
}

export const validateAgenticFlow = async (flowConfig: any) => {
	return await api.post('/agentic-flow/validate', { flowConfig }).then(res => res.data);
}

export const getAgenticFlowStatus = async (executionId: string) => {
	return await api.get(`/agentic-flow/status/${executionId}`).then(res => res.data);
}

// Flow History API functions
export const getFlowHistory = async (dataId: string, params?: { limit?: number; offset?: number; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.offset) queryParams.append('offset', String(params.offset));
	if (params?.status) queryParams.append('status', params.status);
	if (params?.actionType) queryParams.append('actionType', params.actionType);
	if (params?.triggerType) queryParams.append('triggerType', params.triggerType);
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getActionHistory = async (actionId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.offset) queryParams.append('offset', String(params.offset));
	if (params?.status) queryParams.append('status', params.status);
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/action/${actionId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getWorkflowHistory = async (workflowId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.offset) queryParams.append('offset', String(params.offset));
	if (params?.status) queryParams.append('status', params.status);
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/workflow/${workflowId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getFlowAnalytics = async (dataId: string, params?: { startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/analytics/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getExecutionDetails = async (executionId: string) => {
	return await api.get(`/flow-history/execution/${executionId}`).then(res => res.data);
}

export const filterExecutions = async (filters: { dataId?: string; actionId?: string; workflowId?: string; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) => {
	return await api.post('/flow-history/filter', filters).then(res => res.data);
}

// Workflow AI API functions
export const generateWorkflowFromNL = async (description: string, context?: any, availableIntegrations?: string[]) => {
	return await api.post('/workflows/ai/generate', { description, context, availableIntegrations }).then(res => res.data);
}

export const optimizeWorkflow = async (workflow: any) => {
	return await api.post('/workflows/ai/optimize', { workflow }).then(res => res.data);
}

export const deleteDataNote = async (data: any) => {
}

export const getDataNotes = async (dataId: any) => {

}

export const removeIntegration = async (integration: string) => {
	await monitoredRequest.post(`/user/integrations/remove`, { integration: integration }).then(() => {
		//Remove integration from user object in store
		dispatch({ type: REMOVE_INTEGRATION, payload: integration })
	})
}

export const retrieveCompanyUserStats = async (companyId: any) => {
	return await noMonitor.post(`/company/user-stats`, companyId).then(res => res.data)
}

// HR Management API Methods
export const getHRDashboard = async () => {
	return await noMonitor.get('/company/hr/dashboard').then(res => res.data)
}

export const getEmployees = async (params?: { departmentId?: string; status?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.departmentId) queryParams.append('departmentId', params.departmentId);
	if (params?.status) queryParams.append('status', params.status);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/employees${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const updateEmployeeHRData = async (employeeId: string, hrData: any) => {
	return await noMonitor.post('/company/hr/employees', { employeeId, hrData }).then(res => res.data)
}

export const getLeaveRequests = async (params?: { status?: string; employeeId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.status) queryParams.append('status', params.status);
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/leave-requests${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const createLeaveRequest = async (data: { employeeId: string; startDate: string; endDate: string; leaveType: string; reason?: string }) => {
	return await noMonitor.post('/company/hr/leave-requests', data).then(res => res.data)
}

export const updateLeaveRequest = async (requestId: string, data: { status: string; approvedBy?: string }) => {
	return await noMonitor.put(`/company/hr/leave-requests/${requestId}`, data).then(res => res.data)
}

export const getAttendance = async (params?: { startDate?: string; endDate?: string; employeeId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/attendance${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const recordAttendance = async (data: { employeeId: string; date: string; checkIn?: string; checkOut?: string; status?: string }) => {
	return await noMonitor.post('/company/hr/attendance', data).then(res => res.data)
}

export const getPayroll = async (params?: { month?: string; year?: string; employeeId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.month) queryParams.append('month', params.month);
	if (params?.year) queryParams.append('year', params.year);
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/payroll${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const createPayroll = async (data: { employeeId: string; payPeriod: string; baseSalary?: number; deductions?: any[]; bonuses?: any[]; netPay: number }) => {
	return await noMonitor.post('/company/hr/payroll', data).then(res => res.data)
}

// Accounting Software Integration APIs
export const syncPayrollToAccounting = async (payrollId: string, accountingSoftware: 'xero' | 'sage', options?: { createInvoice?: boolean; syncContacts?: boolean }) => {
	return await noMonitor.post(`/company/hr/payroll/${payrollId}/sync-accounting`, {
		accountingSoftware,
		...options
	}).then(res => res.data)
}

export const getAccountingIntegrations = async () => {
	return await noMonitor.get('/company/hr/accounting-integrations').then(res => res.data)
}

export const connectAccountingSoftware = async (software: 'xero' | 'sage', authCode: string) => {
	return await noMonitor.post('/company/hr/accounting-integrations/connect', {
		software,
		authCode
	}).then(res => res.data)
}

export const disconnectAccountingSoftware = async (software: 'xero' | 'sage') => {
	return await noMonitor.delete(`/company/hr/accounting-integrations/${software}`).then(res => res.data)
}

export const getPerformanceReviews = async (params?: { employeeId?: string; status?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	if (params?.status) queryParams.append('status', params.status);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/performance-reviews${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const createPerformanceReview = async (data: { employeeId: string; reviewPeriod: string; goals?: any[]; achievements?: any[]; areasForImprovement?: any[]; rating: number; reviewerId?: string }) => {
	return await noMonitor.post('/company/hr/performance-reviews', data).then(res => res.data)
}

/** Company member area/RBAC flags (owner/admin only). */
export const getCompanyMemberAccess = async (targetUserId: string) => {
	return await noMonitor
		.get(`/company/hr/members/${encodeURIComponent(targetUserId)}/access`)
		.then((res) => res.data)
}

export const updateCompanyMemberAccess = async (
	targetUserId: string,
	access: Record<string, boolean>
) => {
	return await noMonitor
		.put(`/company/hr/members/${encodeURIComponent(targetUserId)}/access`, { access })
		.then((res) => res.data)
}

export const getStatCounts = async () => {
	return await noMonitor.get(`/user/stat-counts`, {}).then(res => res.data)
}
export const getMeetings = async (params: Record<string, any> = {}) => {
	const cleaned = Object.fromEntries(
		Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
	);
	return await noMonitor.post(`/user/meetings`, cleaned).then(res => res.data)
}

export const getRecentMeetings = async (limit = 10, page = 1) => {
	return await noMonitor.get(`/user/recent-meetings?limit=${limit}&page=${page}`).then(res => res.data)
}

/** Create meeting room (authenticated) - appears in meeting history with duration tracking */
export const createMeetingRoom = async (payload: {
	title?: string;
	description?: string;
	maxParticipants?: number;
	ttl?: number;
	allowScreenShare?: boolean;
	allowChat?: boolean;
	hostEmail?: string;
	password?: string;
	/** 'scheduled' for later meetings; defaults to 'active' on the server */
	status?: 'active' | 'inactive' | 'ended' | 'scheduled';
	startTime?: string | Date;
	endTime?: string | Date;
	durationMinutes?: number;
}) => {
	return await noMonitor.post('/user/meeting/create', payload).then(res => res.data)
}

export const getMeetingSchema = async () => {
	return await noMonitor.get('/calendar/get-meeting-schema').then(res => res.data)
}

export const transcribeMeeting = async (audioFile: any, meetingDetails: any) => {
	const formData = new FormData();

	// Add audio file to form data
	if (audioFile) {
		// Handle different file formats
		if (typeof audioFile === 'string') {
			// If it's a URI, we need to fetch it first
			const response = await fetch(audioFile);
			const blob = await response.blob();
			formData.append('audio', blob, 'recording.m4a');
		} else if (audioFile instanceof Blob || audioFile instanceof File) {
			formData.append('audio', audioFile, (audioFile instanceof File && audioFile.name) || 'recording.m4a');
		} else {
			// For React Native, audioFile might be an object with uri
			formData.append('audio', {
				uri: audioFile.file || audioFile.uri,
				type: 'audio/m4a',
				name: 'recording.m4a'
			} as any);
		}
	}

	// Add meeting details (title, participants, location, etc.)
	if (meetingDetails) {
		formData.append('title', meetingDetails.Title || meetingDetails['meeting-title'] || 'Untitled Meeting');

		if (meetingDetails.Participants || meetingDetails['meeting-participants']) {
			formData.append('participants', JSON.stringify(meetingDetails.Participants || meetingDetails['meeting-participants']));
		}

		if (meetingDetails.Location || meetingDetails['meeting-location'] || meetingDetails.location) {
			formData.append('location', meetingDetails.Location || meetingDetails['meeting-location'] || meetingDetails.location);
		}

		const linkedContactId = meetingDetails.dataItemId || meetingDetails['dataItemId'];
		const linkedProductId = meetingDetails.productId || meetingDetails['productId'];
		if (linkedContactId) {
			const id = typeof linkedContactId === 'object'
				? (linkedContactId._id || linkedContactId.id || linkedContactId.value)
				: linkedContactId;
			if (id) formData.append('dataItemId', String(id));
		}
		if (linkedProductId) {
			const id = typeof linkedProductId === 'object'
				? (linkedProductId._id || linkedProductId.id || linkedProductId.value)
				: linkedProductId;
			if (id) formData.append('productId', String(id));
		}

		// Add timing information
		if (meetingDetails.startTime) {
			formData.append('startTime', meetingDetails.startTime.toString());
		}
		if (meetingDetails.endTime) {
			formData.append('endTime', meetingDetails.endTime.toString());
		}
	}

	return await monitoredRequest.post('/ai/transcribe-meeting', formData, {
		headers: {
			'Content-Type': 'multipart/form-data'
		},
		timeout: 120000 // 120 second timeout for transcription
	}).then(res => {
		console.log('✅ Transcription completed:', res.data);
		return res.data;
	}).catch(error => {
		console.error('❌ Transcription failed:', error);
		throw error;
	});
}

export const modifyFolder = async (dataId, action) => {
return await monitorFail.post(`/data/modify-folder`, { dataId, action })
}

export const getCallFlows = async (companyId: string) => {
	return await noMonitor.post(`/flows/call-flows`, { companyId }).then(res => res.data)
}

export const createCallFlow = async (flowData: any) => {
	const type = ['inbound', 'outbound', 'ivr'].includes(flowData?.type) ? flowData.type : 'inbound';
	const newflowData = {
		...flowData,
		type,
		flowType: flowData?.flowType && flowData.flowType !== 'call'
			? flowData.flowType
			: (type === 'outbound' ? 'sales' : 'general'),
	};
	return await monitoredRequest.post(`/flows/create-flow`, { flowData: newflowData });
}

export const getUserPosts = async (platforms?: string[]) => {
	const body = platforms && platforms.length > 0 ? { platforms } : {};
	return await noMonitor.post('/feed/user-feed', body).then(res => {
		return res.data.feed
	});
}

// Competitor Analysis API functions
export const createCompetitor = async (competitorData) => {
	return await noMonitor.post('/competitors', competitorData).then(res => res.data);
}

export const getCompetitors = async () => {
	return await noMonitor.get('/competitors').then(res => res.data);
}

export const getCompetitor = async (competitorId) => {
	return await noMonitor.get(`/competitors/${competitorId}`).then(res => res.data);
}

export const updateCompetitor = async (competitorId, data) => {
	return await noMonitor.put(`/competitors/${competitorId}`, data).then(res => res.data);
}

export const deleteCompetitor = async (competitorId) => {
	return await noMonitor.delete(`/competitors/${competitorId}`).then(res => res.data);
}

export const discoverCompetitorSocial = async (competitorId) => {
	return await noMonitor.post(`/competitors/${competitorId}/discover-social`).then(res => res.data);
}

export const confirmCompetitorSocial = async (competitorId, profileData) => {
	return await noMonitor.post(`/competitors/${competitorId}/confirm-social`, profileData).then(res => res.data);
}

export const getCompetitorSocial = async (competitorId, status) => {
	const params = status ? { status } : {};
	return await noMonitor.get(`/competitors/${competitorId}/social`, { params }).then(res => res.data);
}

export const getSuggestedCompetitors = async () => {
	return await noMonitor.get('/competitors/suggested').then(res => res.data);
}

export const analyzeCompetitorContent = async (competitorId) => {
	return await noMonitor.post(`/competitors/${competitorId}/analyze-content`).then(res => res.data);
}

export const getCompetitorInsights = async (competitorId) => {
	return await noMonitor.get(`/competitors/${competitorId}/insights`).then(res => res.data);
}

export const getCompetitorStrategy = async (competitorId) => {
	return await noMonitor.get(`/competitors/${competitorId}/strategy`).then(res => res.data);
}

export const getCompetitorPosts = async (competitorId, network, limit, offset) => {
	const params: { network?: string; limit?: number; offset?: number } = {};
	if (network) params.network = network;
	if (limit) params.limit = limit;
	if (offset) params.offset = offset;
	return await noMonitor.get(`/competitors/${competitorId}/posts`, { params }).then(res => res.data);
}

export const updateBilling = async (plan, platform = 'ios') => {
	// Ensure platform is set correctly, defaulting to ios if undefined
	const finalPlatform = platform || 'ios';
	return await monitorFail.post('/purchase/validate', {
		...plan,
		platform: finalPlatform,
	})
}

/** User closed the App Store sheet without purchasing; server validates `reason` and logs only. */
export const reportAppStorePurchaseSheetDismissed = async (payload: {
	reason: 'user_cancelled';
	productId?: string;
	clientErrorCode?: string;
	platform?: string;
}) => {
	return await monitorFail
		.post('/payment/app-store/purchase-sheet-dismissed', payload)
		.then((res) => res.data as { ok?: boolean; identified?: boolean });
}

/** Sync held affiliate/referral code to the server before IAP verification (App Store / Play attribution). */
export const syncAffiliateReferralPending = async (code: string) => {
	return await noMonitor
		.post('/user/affiliate/pending-referral', { code })
		.then((res) => res.data);
};

export const bumpViewCount = async (feedId: string) => {
	const res = await noMonitor.post(`/feed/bump-view-count`, { feedId });
	return res?.data as { ok?: boolean; viewCount?: number; error?: string } | undefined;
}

export const likeComment = async (commentId, postId) => {
	return await noMonitor.post('/feed/like', { commentId, postId })
}

// Feed Boost API Functions
export const getBoostPricing = async () => {
	return await noMonitor.get('/feed/boost/pricing').then(res => res.data)
}

export const getPaidOutreachStatus = async () => {
	return await noMonitor.get('/sales/paid-outreach/status').then(res => res.data)
}

export const createPaidOutreachBrief = async (note?: string) => {
	return await monitoredRequest.post('/sales/paid-outreach/brief', { note: note || '' }, { timeout: 60000 }).then(res => res.data)
}

export const createPaidOutreachPaymentIntent = async (payload: {
	spendGbp: number;
	duration: 7 | 14 | 30;
	platforms: Array<'facebook' | 'google' | 'youtube'>;
}) => {
	return await monitoredRequest.post('/sales/paid-outreach/payment-intent', payload, { timeout: 60000 }).then(res => res.data)
}

export const startPaidOutreach = async (payload: {
	brief: Record<string, unknown>;
	spendGbp: number;
	duration: 7 | 14 | 30;
	platforms: Array<'facebook' | 'google' | 'youtube'>;
	destinationUrl?: string;
	publishFacebook?: boolean;
	paymentIntentId: string;
}) => {
	return await monitoredRequest.post('/sales/paid-outreach/start', payload, { timeout: 120000 }).then(res => res.data)
}

export const createPostBoost = async (postId: string, boostData: {
	boostType: 'basic' | 'premium' | 'enterprise';
	budget: number;
	duration: number;
	targetRadius?: number;
	targetAudience?: {
		ageRange?: { min: number; max: number };
		interests?: string[];
		locations?: string[];
	};
	paymentId?: string;
	transactionId?: string;
}) => {
	return await monitoredRequest.post(`/feed/${postId}/boost`, boostData).then(res => res.data)
}

export const pausePostBoost = async (postId: string) => {
	return await monitoredRequest.put(`/feed/${postId}/boost/pause`).then(res => res.data)
}

export const resumePostBoost = async (postId: string) => {
	return await monitoredRequest.put(`/feed/${postId}/boost/resume`).then(res => res.data)
}

export const cancelPostBoost = async (postId: string) => {
	return await monitoredRequest.delete(`/feed/${postId}/boost`).then(res => res.data)
}

export const getPostBoostAnalytics = async (postId: string) => {
	return await noMonitor.get(`/feed/${postId}/boost/analytics`).then(res => res.data)
}

export const getAllUserBoosts = async () => {
	return await noMonitor.get('/feed/boosts').then(res => res.data)
}

export const trackBoostInteraction = async (postId: string, interactionType: 'clicks' | 'engagement' | 'conversions') => {
	return await noMonitor.post(`/feed/${postId}/boost/track`, { interactionType }).then(res => res.data)
}

export const getBountySchema = async () => {
	return await noMonitor.get('/data/bounty-schema').then(res => res.data)
}

export const createBountyPaymentIntent = async (amount: number, currency: string = 'usd') => {
	return await monitoredRequest.post('/data/create-bounty-payment-intent', { amount, currency }).then(res => res.data)
}

export const createBounty = async (vals: any) => {
	return monitoredRequest.post('/data/post-bounty', vals)
}

export const getBounties = async () => {
	return noMonitor.get('/data/get-bounties').then(res => res.data)
}

export const flagItem = async (contentId, flag) => {
	return await noMonitor.post(`/data/flag-item`, { contentId, flag })
}

export const unflagItem = async (contentId) => {
	return await noMonitor.post(`/data/flag-item`, { contentId, flag: false })
}

export const getFlaggedItems = async () => {
	return await noMonitor.get('/data/get-flagged').then(res => res.data)
}

/** Flagged PM tasks visible to the session user / company (see backend `/flag/items?type=tasks`). */
export const getFlaggedProjectTasks = async () => {
	return await noMonitor.get('/flag/items', { params: { type: 'tasks' } }).then(res => res.data)
}

export const setProjectTaskFlagged = async (
	projectId: string,
	wpId: string,
	taskId: string,
	flag: boolean
) => {
	return await noMonitor.post(`/flag/project-task/${projectId}/${wpId}/${taskId}`, { flag })
}

export const updateDataItemHistory = async (dataId, update) => {
	return await noMonitor.post(`/data/update`, { dataId, update })
}

export const getTrackedItemHistory = async (dataId) => {
	return await noMonitor.post(`/data/get-item-history`, { dataId }).then(res => res.data)
}

export const getExpiredCampaigns = async () => {
	return await noMonitor.get('/sales/expired-campaigns').then(res => res.data)
}

export const getCompletedProjects = async () => {
	return await noMonitor.get('/projects/completed').then(res => {
		return res.data
	})
}

export const addIntegration = async (integration) => {
	return await monitoredRequest.post('/integration/add-integration', integration)
}

export const sendTestEmail = async (templateId, to, subject) => {
	return await monitoredRequest.post('/templates/send-test-email', { templateId, email: to, subject })
}

export const testWhatsApp = async () => {
	return await monitoredRequest.post('/integration/whatsapp/test')
}

export const addSSCal = async (vals) => {
	return await monitoredRequest.post('/calendar/create-calendar', vals)
}

export const getWholeCalendarSchema = async () => {
	return await noMonitor.get('/calendar/schema').then(res => res.data)
}

export const authManMail = async (vals) => {
	return await monitoredRequest.post('/integration/mail/auth', vals)
}

export const getMailBoxes = async () => {
	return await noMonitor.get('/authorisation/mailboxes').then(res => res.data)
}

export const setDefaultMailbox = async (mailboxId: string) => {
	return await monitoredRequest.post('/authorisation/mailboxes/default', { mailboxId }).then(res => res.data)
}

export const deleteMailbox = async (mailboxId: string) => {
	return await monitoredRequest.post('/authorisation/mailboxes/delete', { mailboxId }).then(res => res.data)
}

export const listMailResponseDrafts = async () => {
	return await monitoredRequest.get('/authorisation/mail/response-drafts').then((res) => res.data);
}

export const sendMailResponseDraft = async (draftId: string, includeSignature = false) => {
	return await monitoredRequest.post(`/authorisation/mail/response-drafts/${draftId}/send`, { includeSignature }).then((res) => res.data);
}

export const discardMailResponseDraft = async (draftId: string) => {
	return await monitoredRequest.delete(`/authorisation/mail/response-drafts/${draftId}`).then((res) => res.data);
}

export const readMailbox = async (params: {
	mode?: 'count' | 'list' | 'get';
	label?: string;
	labels?: string[];
	query?: string;
	maxResults?: number;
	messageId?: string;
	includeBody?: boolean;
	mailboxEmail?: string;
} = {}) => {
	return await monitoredRequest.post('/authorisation/mailboxes/read', {
		mode: params.mode || 'list',
		label: params.label,
		labels: params.labels,
		query: params.query,
		maxResults: params.maxResults ?? 50,
		messageId: params.messageId,
		includeBody: params.includeBody ?? false,
		mailboxEmail: params.mailboxEmail,
	}).then((res) => res.data)
}

export const getAffiliateDetails = async () => {
	return await noMonitor.get('/user/affiliate/details').then(res => res.data)
}

export const deleteCalendar = async (calendarId: string) => {
	console.log(calendarId)
	return await noMonitor.delete(`/calendar/remove-calendar/${calendarId}`);
}

export const deleteCalendarEntry = async (entryId: string) => {
	return await monitorFail.delete(`/calendar/remove-calendar-entry/${entryId}`)
}

export const analyseDataset = async (dataId) => {
	return await monitoredRequest.post(`/ai/analyse-dataset`, { dataId }, { timeout: 120000 })
}

export const createAIDataModel = async (text: string) => {
	return await monitoredRequest.post('/ai/create-schema', {
		description: text,
		currentSchema: [],
		tags: []
	}, { timeout: 120000 })
}

// Document Creation API Methods
export const createDocumentLayout = async (documentType: string, title: string, description: string, forecastInfo?: { forecastId: string; forecastName: string; availableCharts: string[] }) => {
	console.log('📄 [createDocumentLayout] Calling backend API');
	const requestBody: any = {
		document_type: documentType,
		title: title,
		description: description || ''
	};
	if (forecastInfo) {
		requestBody.forecast_info = forecastInfo;
	}
	return await monitoredRequest.post('/ai/document/create-layout', requestBody, { timeout: 120000 }).then(res => res.data);
}

export const generateDocumentContent = async (layoutData: any, userRequirements?: string, pageNumber?: number, forecastInfo?: { forecastId: string; forecastName: string; availableCharts: string[] }) => {
	console.log('📄 [generateDocumentContent] Calling backend API', pageNumber ? `for page ${pageNumber}` : '');
	const requestBody: any = {
		layout_data: layoutData,
		user_requirements: userRequirements || ''
	};
	if (pageNumber !== undefined) {
		requestBody.page_number = pageNumber;
	}
	if (forecastInfo) {
		requestBody.forecast_info = forecastInfo;
	}
	return await monitoredRequest.post('/ai/document/generate-content', requestBody, { timeout: 120000 }).then(res => res.data);
}

export const refineDocumentDesign = async (contentData: any, designSettings?: any) => {
	console.log('📄 [refineDocumentDesign] Calling backend API');
	return await monitoredRequest.post('/ai/document/refine-design', {
		content_data: contentData,
		design_settings: designSettings || {}
	}, { timeout: 120000 }).then(res => res.data);
}

export const validateDocument = async (documentData: any) => {
	console.log('📄 [validateDocument] Calling backend API');
	return await monitoredRequest.post('/ai/document/validate', {
		document_data: documentData
	}, { timeout: 60000 }).then(res => res.data);
}

export const createDocumentBackground = async (documentType: string, title: string, description?: string, userRequirements?: string, selectedForecast?: any) => {
	console.log('📄 [createDocumentBackground] Starting background document creation');
	return await monitoredRequest.post('/documents/create-background', {
		documentType,
		title,
		description: description || '',
		userRequirements: userRequirements || '',
		selectedForecast: selectedForecast || null
	}, { timeout: 10000 }).then(res => res.data);
}

export const retryDocumentCreation = async (draftId: string) => {
	console.log('📄 [retryDocumentCreation] Retrying document creation for draft:', draftId);
	return await monitoredRequest.post('/documents/retry-creation', {
		draftId
	}, { timeout: 10000 }).then(res => res.data);
}

export const getMapList = async (location) => {
	return await noMonitor.post('/company/list', { location })
}

// Networking API functions
export const getNetworkingBusinesses = async (params = {}) => {
	const queryParams = new URLSearchParams();
	
	Object.keys(params).forEach(key => {
		if (params[key] !== undefined && params[key] !== null) {
			if (Array.isArray(params[key])) {
				params[key].forEach(value => queryParams.append(key, value));
			} else {
				queryParams.append(key, params[key]);
			}
		}
	});
	
	return await noMonitor.get(`/networking/businesses?${queryParams.toString()}`);
}

export const getNetworkingBusinessById = async (id) => {
	return await noMonitor.get(`/networking/businesses/${id}`);
}

// Resume Extraction and Templating API functions
export const extractResume = async (fileData?: string, fileType?: string, linkedinUrl?: string) => {
	if (linkedinUrl) {
		// LinkedIn URL extraction
		return await monitoredRequest.post('/ai/resume/extract', { linkedin_url: linkedinUrl }, { timeout: 600000 });
	}

	if (fileData) {
		// File upload - handle base64 data URL
		let base64Data = fileData;
		if (fileData.includes(',')) {
			base64Data = fileData.split(',')[1];
		}

		// Send as base64 in JSON body for better cross-platform compatibility
		// Use 10 minute timeout for large documents (10+ pages)
		return await monitoredRequest.post('/ai/resume/extract', {
			file_data: base64Data,
			file_type: fileType || 'application/pdf'
		}, { timeout: 600000 });
	}

	throw new Error('Either fileData or linkedinUrl must be provided');
}

export const getResumeTemplates = async () => {
	return await noMonitor.get('/ai/resume/templates');
}

export const applyResumeTemplate = async (templateId: string, resumeData: any) => {
	return await monitoredRequest.post('/ai/resume/apply-template', {
		templateId,
		resumeData
	}, { timeout: 600000 }); // 10 minutes timeout for template processing (handles large resumes)
}

export const searchNetworkingBusinesses = async (query, location = {}) => {
	const params = new URLSearchParams({
		query,
		...location
	});
	
	return await noMonitor.get(`/networking/search?${params.toString()}`);
}

export const testNotice = async() => {
	console.log('testing notice');
	return await monitorFail.get('/test/force').then(res => {
		return true
	})
}

export const getDocumentTemplates = async () => {
	return await noMonitor.get('/templates/documents').then(res => { return res.data.documents })
}

// Document Folder APIs
export const createDocumentFolder = async (name: string, parentId?: string | null) => {
	return await monitoredRequest.post('/documents/create-folder', { name, parentId }).then(res => res.data)
}

export const getDocumentFolders = async (parentId?: string | null) => {
	return await noMonitor.post('/documents/get-folders', { parentId }).then(res => res.data)
}

export const deleteDocumentFolder = async (folderId: string) => {
	return await monitoredRequest.delete(`/documents/delete-folder/${folderId}`).then(res => res.data)
}

export const moveDocumentToFolder = async (documentId: string, folderId: string | null) => {
	return await monitoredRequest.post('/documents/move-document', { documentId, folderId }).then(res => res.data)
}

// User Document APIs
export const uploadUserDocument = async (fileData: {
	name: string;
	type: string;
	size?: number;
	fileData?: string;
	uri?: string;
	folderId?: string | null;
}, config?: any) => {
	return await monitoredRequest.post('/documents/upload', fileData, config).then(res => res.data)
}

export const getUserUploadedDocuments = async (folderId?: string | null) => {
	return await noMonitor.post('/documents/get-documents', { folderId }).then(res => res.data)
}

export const getDocumentContent = async (documentId: string) => {
	return await monitoredRequest.get(`/documents/get-content/${documentId}`).then(res => res.data)
}

export const deleteUserDocument = async (documentId: string) => {
	return await monitoredRequest.delete(`/documents/delete-document/${documentId}`).then(res => res.data)
}

export const cancelApplePlan = async () => {
	return await monitoredRequest
		.post('/payment/app-store/cancel')
		.then((res) => res.data);
};

/** Android subscriptions are stored as googleStore on the backend */
export const cancelGooglePlan = async () => {
	return await monitoredRequest
		.post('/payment/google-store/cancel')
		.then((res) => res.data);
};

/** @deprecated Use cancelGooglePlan */
export const cancelPlayPlan = cancelGooglePlan;

export const cancelStripePlan = async () => {
	return await monitoredRequest.post('/stripe/cancel').then((res) => res.data);
};

// Schema stats tracking functions
export const trackDataView = async (dataId: string) => {
	return await noMonitor.post(`/data/track-schema-view/${dataId}`).then(res => res.data);
}

export const trackDataSchemaInteraction = async (dataId: string, action: string) => {
	return await noMonitor.post(`/data/track-schema-interaction/${dataId}`, { action }).then(res => res.data);
}

export const getDataStats = async (dataId: string) => {
	return await noMonitor.get(`/data/schema-stats/${dataId}`).then(res => res.data);
}

// Utility Functions APIs
export const saveUtility = async (utilityData) => {
	return await noMonitor.post('/utilities', utilityData).then(res => res.data);
}

export const getUtilitiesForData = async (dataId: string) => {
	return await noMonitor.get(`/utilities/data/${dataId}`).then(res => res.data);
}

export const getUtility = async (utilityId: string) => {
	return await noMonitor.get(`/utilities/${utilityId}`).then(res => res.data);
}

export const updateUtility = async (utilityId: string, updates: any) => {
	return await noMonitor.put(`/utilities/${utilityId}`, updates).then(res => res.data);
}

export const deleteUtility = async (utilityId: string) => {
	return await noMonitor.delete(`/utilities/${utilityId}`).then(res => res.data);
}

export const linkUtilitiesToData = async (dataId: string, utilityIds: string[]) => {
	return await noMonitor.put(`/data/${dataId}/utilities`, { utilityIds }).then(res => res.data);
}

export const executeUtility = async (utilityId: string, dataItemId?: string) => {
	return await noMonitor.post(`/utilities/${utilityId}/execute`, { dataItemId }).then(res => res.data);
}

export const buildUtilityPipeline = async (payload: {
	dataId: string;
	description?: string;
	allowedFields?: string[];
	dataItemId?: string;
	deploy?: boolean;
}) => {
	return await noMonitor.post('/utilities/pipeline', payload).then(res => res.data);
}

export const getWhatsAppProfile = async () => {
	return await noMonitor.get('/social-media/profile/whatsapp').then(res => res.data)
}

// Telegram integration APIs
export const getTelegramProfile = async () => {
	return await noMonitor.get('/integration/telegram/profile').then(res => res.data);
};

export const setTelegramWebhook = async () => {
	return await noMonitor.post('/telegram/webhook/set').then(res => res.data);
};

export const getTelegramWebhookInfo = async () => {
	return await noMonitor.get('/telegram/webhook/info').then(res => res.data);
};

export const removeTelegramWebhook = async () => {
	return await noMonitor.post('/telegram/webhook/remove').then(res => res.data);
};

export const getWhatsAppMessages = async () => {
	return await noMonitor.get('/integration/whatsapp/messages').then(res => res.data)
}

export const getWhatsappTemplates = async () => {
	return await noMonitor.get('/integration/whatsapp/templates').then(res => res.data)
}

export const getWhatsAppCatalogs = async (wabaId?: string) => {
	const params = wabaId ? { wabaId } : {};
	// Background read. A missing WhatsApp connection is a normal empty state, not a global error modal.
	return await noMonitor.get('/integration/whatsapp/catalogs', { params }).then(res => res.data);
}

export const createWhatsAppCatalog = async (request: { name: string; vertical?: string; wabaId?: string }) => {
	return await monitoredRequest.post('/integration/whatsapp/catalogs/create', request).then(res => res.data);
}

export const selectWhatsAppCatalog = async (request: { catalogId: string; wabaId?: string; skipValidation?: boolean }) => {
	return await monitoredRequest.post('/integration/whatsapp/catalogs/select', request).then(res => res.data);
}

export const verifyWhatsAppCatalog = async (request: { catalogId: string; wabaId?: string }) => {
	return await monitoredRequest.post('/integration/whatsapp/catalogs/verify', request).then(res => res.data);
}

export const updateWhatsAppConversationModelSettings = async (conversationModelId: string) => {
	return await monitoredRequest.put('/whatsapp/conversation-model/settings', { conversationModelId })
}

export const sendTemplateMessage = async (request: {
	to: string;
	templateName: string;
	templateLanguage: string;
	parameters?: any[];
	businessPhoneNumberId: string;
}) => {
	return await monitoredRequest.post('/whatsapp/send/template', request)
}

export const verifyCouponCode = async (code:string) => {
	return await noMonitor.post('/promo/verify', {code})
}

export const redeemTrialPromoCode = async (code: string): Promise<{
	success: boolean;
	code: string;
	trialDays: number;
	trialEnd: string;
	extended: boolean;
}> => {
	return await noMonitor.post('/promo/redeem-trial', { code }).then(res => res.data)
}

/** Trial promo codes are only offered to accounts that have never had paid access. */
export const getTrialPromoEligibility = async (): Promise<{
	eligible: boolean;
	inTrial: boolean;
	reason?: string;
}> => {
	return await noMonitor.get('/promo/trial-eligibility').then(res => res.data)
}

export const verifyOTPCode = async (email?: string, code?: string) => {
	return await api.post('/validate-email-otp', { email, otpCode: code }).then(res => res.data)
}

export const getStripeProfile = async ()=> {
	return await noMonitor.get('/stripe/connect/profile').then(res => res.data);
}

export const syncShopifyProducts = async () => {
	return await monitoredRequest.post('/integrations/shopify/sync-products').then(res => res.data);
}

// Product Sync API Functions
export const getProductSyncStatus = async (dataId: string) => {
	return await monitoredRequest.get(`/products/sync/status/${dataId}`).then(res => res.data);
}

export const pullProductsFromIntegration = async (dataId: string, integrationType: string) => {
	return await monitoredRequest.post(`/products/sync/pull/${dataId}`, { integrationType }).then(res => res.data);
}

export const pushProductsToIntegration = async (dataId: string, integrationType: string) => {
	return await monitoredRequest.post(`/products/sync/push/${dataId}`, { integrationType }).then(res => res.data);
}

export const resolveProductConflict = async (itemId: string, resolution: 'local' | 'external' | 'merge', integrationType?: string) => {
	return await monitoredRequest.post('/products/sync/resolve-conflict', { itemId, resolution, integrationType }).then(res => res.data);
}

export const getAvailableIntegrations = async () => {
	return await monitoredRequest.get('/products/sync/available-integrations').then(res => res.data);
}

// Stripe Sales Statistics API Functions
export const getStripeSalesStats = async () => {
	return await noMonitor.get('/stripe/sales/stats').then(res => res.data);
}

export const getStripeMonthlySalesOverview = async (months?: number) => {
	const params = months ? { months } : {};
	return await noMonitor.get('/stripe/sales/monthly-overview', { params }).then(res => res.data);
}

export const getStripeRecentTransactions = async (limit?: number) => {
	const params = limit ? { limit } : {};
	return await noMonitor.get('/stripe/sales/recent-transactions', { params }).then(res => res.data);
}

export const getStripePaymentMethodStats = async () => {
	return await noMonitor.get('/stripe/sales/payment-methods').then(res => res.data);
}

// PayStack Sales Statistics API functions
export const getPayStackSalesStats = async () => {
	return await noMonitor.get('/paystack/sales/stats').then(res => res.data);
}

export const getPayStackMonthlySalesOverview = async (months?: number) => {
	const params = months ? { months } : {};
	return await noMonitor.get('/paystack/sales/monthly-overview', { params }).then(res => res.data);
}

export const getPayStackRecentTransactions = async (limit?: number) => {
	const params = limit ? { limit } : {};
	return await noMonitor.get('/paystack/sales/recent-transactions', { params }).then(res => res.data);
}

export const getPayStackPaymentMethodStats = async () => {
	return await noMonitor.get('/paystack/sales/payment-methods').then(res => res.data);
}

export const getXProfile = async () => {
	return await noMonitor.get('/integration/x/profile').then(res => res.data);
}

export const getInstagramProfile = async () => {
	return await noMonitor.get('/social-media/profile/instagram').then(res => res.data);
}

export const getInstagramMedia = async (limit?: number) => {
	const params = limit ? { limit } : {};
	return await noMonitor.get('/social-media/instagram/media', { params }).then(res => res.data);
}

export const getFacebookProfile = async () => {
	return await noMonitor.get('/social-media/profile/facebook').then(res => res.data);
}

export const getYoutubeProfile = async () => {
	return await noMonitor.get('/social-media/profile/youtube').then(res => res.data);
}

export const getStoreSettings = async (userId: string) => {
	return await noMonitor.get(`/store/settings/${userId}`).then(res => {
		// Backend returns { success: true, data: {...} }
		return res.data?.data || res.data || {};
	}).catch(err => {
		// Don't log duplicate request prevention - it's expected when multiple refetches occur
		if (!err?.isDuplicate) {
			console.error('[getStoreSettings] API error:', err);
		}
		throw err;
	});
}

export const updateStoreSettings = async (userId: string, settings: any) => {
		return await noMonitor.put(`/store/settings/${userId}`, settings).then(res => {
			// Backend returns { success: true, data: {...} }
			return res.data?.data || res.data || {};
		}).catch(err => {
			console.error('[updateStoreSettings] API error:', err);
			throw err;
		});
	}

export const getFloorPlan = async (userId: string | undefined, key: string) => {
	return await noMonitor.get(`/floor-plan/${key}`).then(res => {
		// Backend returns { success: true, key, data: FloorPlanFloor[] }
		return res.data?.data || [];
	}).catch(err => {
		console.error("[getFloorPlan] API error:", err);
		throw err;
	});
}

export const updateFloorPlan = async (userId: string | undefined, key: string, floors: any[]) => {
	return await noMonitor.put(`/floor-plan/${key}`, { floors }).then(res => {
		// Backend returns { success: true, key, data: FloorPlanFloor[] }
		return res.data?.data || res.data || {};
	}).catch(err => {
		console.error("[updateFloorPlan] API error:", err);
		throw err;
	});
}

export const deleteFloorPlan = async (userId: string | undefined, key: string) => {
	return await noMonitor.delete(`/floor-plan/${key}`).then(res => res.data).catch(err => {
		console.error("[deleteFloorPlan] API error:", err);
		throw err;
	});
}

export const updateYoutubeSubscription = async (settings: { notifyOnNewVideo?: boolean; autoPostOnNewVideo?: boolean; onNewVideoAction?: string; postPlatforms?: string[] }) => {
	return await monitoredRequest.put('/social-media/youtube/subscription', settings).then(res => res.data);
}

export const getYoutubeVideos = async () => {
	console.log('🎬 API: getYoutubeVideos called');
	console.log('🌐 API: Making request to /social-media/youtube/videos');

	try {
		const response = await noMonitor.get('/social-media/youtube/videos');
		console.log('✅ API: YouTube videos response received:', {
			status: response.status,
			statusText: response.statusText,
			data: response.data
		});
		return response.data;
	} catch (error: any) {
		console.error('❌ API: getYoutubeVideos error:', {
			message: error?.message,
			status: error?.response?.status,
			statusText: error?.response?.statusText,
			data: error?.response?.data,
			url: error?.config?.url,
			method: error?.config?.method
		});
		throw error;
	}
}

export const getLinkedInProfile = async () => {
	return await noMonitor.get('/integration/linkedin/profile').then(res => res.data);
}

export const setLinkedInDefaultCompany = async (data: { companyUrn: string | null, companyId: string | null, companyName: string | null }) => {
	return await noMonitor.post('/integration/linkedin/default-company', data).then(res => res.data);
}

export const getLinkedInPosts = async (limit = 25) => {
	return await noMonitor.get(`/integration/linkedin/posts?limit=${limit}`).then(res => res.data);
}

export const scheduleToFeed = async (data) => {
	return await monitoredRequest.post('/feed/post/schedule-test', data).then(res => res.data)
}

export const scheduleMultipleSocialPosts = async (data) => {
	return await monitoredRequest.post('/ai/schedule-multiple-social-posts', data, { timeout: 120000 }).then(res => res.data)
}

export const generateCampaignPosts = async (data) => {
	return await monitoredRequest.post('/ai/campaign-social-posts', data, { timeout: 60000 }).then(res => res.data)
}

export const cancelScheduledPost = async (postId) => {
	return await monitoredRequest.delete(`/ai/schedule-social-post/${postId}`).then(res => res.data)
}

export const getScheduledPosts = async (userId) => {
	return await monitoredRequest.get(`/ai/scheduled-posts/${userId}`).then(res => res.data)
}

/** OpenClaw scheduled cron jobs – proxy to user's OpenClaw instance (for Easy Mode empty state) */
export const getOpenClawScheduledJobs = async () => {
	const res = await monitoredRequest.get('/ai/openclaw/scheduled-jobs');
	return res?.data?.jobs ?? [];
}

export const updateScheduledPost = async (postId, data) => {
	return await monitoredRequest.put(`/ai/scheduled-posts/${postId}`, data).then(res => res.data)
}

export const getAffiliateCode = async () => {
	return await noMonitor.get('/user/affiliate/get-code').then(res => res.data)
}

export const getWordpressProfile = async () => {
	return await noMonitor.get('/integration/wordpress/profile').then(res => res.data)
}

export const getWordPressBlogDetails = async () => {
	return await noMonitor.get('/integration/wordpress/get-posts').then(res => res.data)
}

export const buildProductContent = async (productId: string, previousHtml: string) => {
	return await noMonitor.post(`/ai/build-product-content/${productId}`, { previousHtml }, { timeout: 120000 }).then(res => res.data)
}

export const getSlackProfile = async () => {
	return await noMonitor.get('/integration/slack/profile').then(res => res.data);
}

export const getGitHubProfile = async () => {
	return await noMonitor.get('/integration/github/profile').then(res => res.data);
}

export const listGitHubRepos = async (params?: { page?: number; per_page?: number; sort?: string; type?: string }) => {
	return await noMonitor.get('/integration/github/repos', { params }).then(res => res.data);
}

export const getGitHubRepoContents = async (
	owner: string,
	repo: string,
	options?: { path?: string; ref?: string }
) => {
	const query = new URLSearchParams();
	if (options?.path) query.set('path', options.path);
	if (options?.ref) query.set('ref', options.ref);
	const suffix = query.toString() ? `?${query.toString()}` : '';
	return await noMonitor
		.get(`/integration/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/contents${suffix}`)
		.then(res => res.data);
}

export const upsertGitHubRepoFile = async (
	owner: string,
	repo: string,
	payload: { path: string; content: string; message?: string; sha?: string; branch?: string; production?: boolean }
) => {
	return await monitoredRequest
		.put(`/integration/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/file`, {
			...payload,
			production: payload.production !== false,
		})
		.then(res => res.data);
}

export const createGitHubRepo = async (payload: { name: string; description?: string; isPrivate?: boolean }) => {
	return await monitoredRequest.post('/integration/github/repos', payload).then(res => res.data);
}

export const scaffoldGitHubPerformanceSite = async (
	owner: string,
	repo: string,
	payload?: { siteTitle?: string; siteDescription?: string }
) => {
	return await monitoredRequest
		.post(
			`/integration/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/scaffold-performance-site`,
			payload || {}
		)
		.then(res => res.data);
}

export const getGitHubPagesInfo = async (owner: string, repo: string) => {
	return await noMonitor
		.get(`/integration/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pages`)
		.then(res => res.data);
}

// Hugging Face integration functions
export const getHuggingFaceProfile = async () => {
	return await noMonitor.get('/integration/huggingface/profile').then(res => res.data);
}

export const storeHuggingFaceToken = async (token: string) => {
	return await monitoredRequest.post('/integration/huggingface/token', { token }).then(res => res.data);
}

export const removeHuggingFaceToken = async () => {
	return await monitoredRequest.delete('/integration/huggingface/token').then(res => res.data);
}

export const getHuggingFaceModels = async () => {
	return await noMonitor.get('/integration/huggingface/models').then(res => res.data);
}

export const exportDataModelToHF = async (dataModelId: string, options: { datasetName: string }) => {
	return await monitoredRequest.post('/integration/huggingface/export-data', { dataModelId, ...options }, { timeout: 120000 }).then(res => res.data);
}

export const initiateHuggingFaceTraining = async (datasetId: string, config?: any) => {
	return await monitoredRequest.post('/integration/huggingface/train', { datasetId, config }, { timeout: 120000 }).then(res => res.data);
}

export const getHuggingFaceTrainingHistory = async () => {
	return await noMonitor.get('/integration/huggingface/training-history').then(res => res.data);
}

export const getHuggingFaceTrainingStatus = async (trainingId: string) => {
	return await noMonitor.get(`/integration/huggingface/training/${trainingId}`).then(res => res.data);
}

// PayStack integration functions
export const getPayStackProfile = async () => {
	return await noMonitor.get('/integration/paystack/profile').then(res => res.data);
}

export const storePayStackKeys = async (publicKey: string, secretKey: string) => {
	return await monitoredRequest.post('/integration/paystack/keys', { publicKey, secretKey }).then(res => res.data);
}

export const removePayStackKeys = async () => {
	return await monitoredRequest.delete('/integration/paystack/keys').then(res => res.data);
}

export const generatePayStackPaymentLink = async (params: { amount: number; email: string; currency?: string }) => {
	return await monitoredRequest.post('/integration/paystack/payment-link', params).then(res => res.data);
}

export const connectPlatform = async (platform: string) => {
	return await monitoredRequest.get(`/social-media/integrations/connect/${platform}`).then(res => res.data)
}

export const connectPlatformCallback = async (code: string, identifier: string, codeVerifier: string, verifier?: string) => {
	console.log('[connectPlatformCallback] Sending to backend:', {
		identifier,
		hasCode: !!code,
		codePreview: code ? `${code.substring(0, 10)}...` : 'missing',
		redirectUri: verifier || 'missing',
		redirectUriLength: verifier?.length || 0
	});
	try {
		const res = await monitoredRequest.post(
			`/social-media/integrations/auth-callback`,
			{ code, identifier, codeVerifier, redirectUri: verifier }
		);
		return res.data;
	} catch (e) {
		console.error('[connectPlatformCallback] error occurred:', e);
		throw e;
	}
}

export const getAINotes = async (dataId: string) => {
	return await noMonitor.get(`/ai/analyse-dataset/${dataId}`, { timeout: 120000 }).then((res) => res.data)
}

export const changePublishSettings = async (integration: string) => {
	return await noMonitor.get('/integration/publish-settings/' + integration).then(res => res.data)
}

export const registerLinkedIn = async (code, redirectUri, codeVerifier?: string) => {
	return await monitoredRequest.post('/sso/linkedin/login', { code, redirectUri, codeVerifier })
}

// Report application errors and crashes
export const reportError = async (errorReport) => {
	try {
		return await monitoredRequest.post('/errors/report', errorReport);
	} catch (error) {
		console.error('Failed to report error:', error);
		// Fallback to local storage or console logging
		if (Platform.OS === 'web') {
			localStorage.setItem(`error_report_${Date.now()}`, JSON.stringify(errorReport));
		}
		throw error;
	}
}

// Usage tracking functions
const trackUsage = async (itemId, action = 'viewed', metadata = {}) => {
	try {
		if (!itemId) return null;
		const response = await api.post(`/data/track-usage/${itemId}`, { action, metadata });
		return response.data;
	} catch (error) {
		console.error('Error tracking usage:', error);
		return null;
	}
};

const getUsageStats = async (itemId, detailed = false) => {
	try {
		if (!itemId) return null;
		const response = await api.get(`/data/usage-stats/${itemId}?detailed=${detailed}`);
		return response.data;
	} catch (error) {
		console.error('Error getting usage stats:', error);
		return null;
	}
};

const getMostUsedItems = async (options: any = {}) => {
	try {
		const { dataId, limit = 10, timeframe = 'all' } = options;
		const params = new URLSearchParams();
		if (dataId) params.append('dataId', dataId);
		params.append('limit', limit.toString());
		params.append('timeframe', timeframe);
		const response = await api.get(`/data/most-used?${params.toString()}`);
		return response.data;
	} catch (error) {
		console.error('Error getting most used items:', error);
		return null;
	}
};

const bulkTrackUsage = async (items) => {
	try {
		if (!Array.isArray(items) || items.length === 0) return null;
		const response = await api.post('/data/bulk-track-usage', { items });
		return response.data;
	} catch (error) {
		console.error('Error bulk tracking usage:', error);
		return null;
	}
};

// Timeline CRM API functions
const getTimelineData = async (dataId) => {
	try {
		const response = await api.get(`/timeline/${dataId}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching timeline data:', error);
		return null;
	}
};

const getCustomerSales = async (dataId) => {
	try {
		const response = await api.get(`/timeline/${dataId}/sales`);
		return response.data;
	} catch (error) {
		console.error('Error fetching customer sales:', error);
		throw error;
	}
};

const addContactNote = async (dataId, noteData) => {
	try {
		const response = await api.post(`/timeline/${dataId}/notes`, noteData);
		return response.data;
	} catch (error) {
		console.error('Error adding contact note:', error);
		throw error;
	}
};

const addContactTask = async (dataId, taskData) => {
	try {
		const response = await api.post(`/timeline/${dataId}/tasks`, taskData);
		return response.data;
	} catch (error) {
		console.error('Error adding contact task:', error);
		throw error;
	}
};

const toggleTask = async (dataId, taskId, completed) => {
	try {
		const response = await api.patch(`/timeline/${dataId}/tasks/${taskId}`, { completed });
		return response.data;
	} catch (error) {
		console.error('Error toggling task:', error);
		throw error;
	}
};

const logInteraction = async (dataId, interactionData) => {
	try {
		const response = await api.post(`/timeline/${dataId}/interactions`, interactionData);
		return response.data;
	} catch (error) {
		console.error('Error logging interaction:', error);
		throw error;
	}
};

const updateContactMetrics = async (dataId, metrics) => {
	try {
		const response = await api.patch(`/timeline/${dataId}/metrics`, metrics);
		return response.data;
	} catch (error) {
		console.error('Error updating contact metrics:', error);
		throw error;
	}
};

const analyzeCustomerHealth = async (dataId) => {
	try {
		const response = await api.post(`/ai/analyze-customer-health/${dataId}`, {}, { timeout: 120000 });
		return response.data;
	} catch (error) {
		console.error('Error analyzing customer health:', error);
		throw error;
	}
};

const getCachedCustomerHealthAnalysis = async (dataId) => {
	try {
		const response = await api.get(`/ai/customer-health-analysis/${dataId}`, { timeout: 120000 });
		return response.data;
	} catch (error) {
		console.error('Error getting cached customer health analysis:', error);
		throw error;
	}
};

const getCRMTimelineFolders = async () => {
	try {
		const response = await api.get('/data/crm-folders');
		return response.data;
	} catch (error) {
		console.error('Error fetching CRM folders:', error);
		return null;
	}
};

// Product Management API functions
const getProductDetails = async (productId, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await noMonitor.get(`/products/${productId}?mode=${mode}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching product details:', error);
		return null;
	}
};

const addProductSupplier = async (productId, supplierData, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await api.post(`/products/${productId}/suppliers?mode=${mode}`, supplierData);
		return response.data;
	} catch (error) {
		console.error('Error adding supplier:', error);
		throw error;
	}
};

const getProductSuppliers = async (productId, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await noMonitor.get(`/products/${productId}/suppliers?mode=${mode}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching suppliers:', error);
		return null;
	}
};

const updateProductSupplier = async (productId, supplierId, updateData, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await api.put(`/products/${productId}/suppliers/${supplierId}?mode=${mode}`, updateData);
		return response.data;
	} catch (error) {
		console.error('Error updating supplier:', error);
		throw error;
	}
};

const deleteProductSupplier = async (productId, supplierId, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await api.delete(`/products/${productId}/suppliers/${supplierId}?mode=${mode}`);
		return response.data;
	} catch (error) {
		console.error('Error deleting supplier:', error);
		throw error;
	}
};

const updateProductPricing = async (productId, pricingData, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await api.put(`/products/${productId}/pricing?mode=${mode}`, pricingData);
		return response.data;
	} catch (error) {
		console.error('Error updating pricing:', error);
		throw error;
	}
};

const addQualityCheck = async (productId, qualityData, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await api.post(`/products/${productId}/quality-checks?mode=${mode}`, qualityData);
		return response.data;
	} catch (error) {
		console.error('Error adding quality check:', error);
		throw error;
	}
};

const getProductAnalytics = async (productId, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await noMonitor.get(`/products/${productId}/analytics?mode=${mode}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching analytics:', error);
		return null;
	}
};

const updateProductListingMode = async (productId, mode: 'live' | 'draft') => {
	try {
		const response = await api.put(`/products/${productId}/status`, { mode });
		return response.data;
	} catch (error) {
		console.error('Error updating product listing mode:', error);
		throw error;
	}
};

const getProductInventory = async (productId, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await noMonitor.get(`/products/${productId}/inventory?mode=${mode}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching inventory:', error);
		return null;
	}
};

const updateProductInventory = async (productId, inventoryData, mode: 'live' | 'draft' = 'live') => {
	try {
		const response = await api.put(`/products/${productId}/inventory?mode=${mode}`, inventoryData);
		return response.data;
	} catch (error) {
		console.error('Error updating inventory:', error);
		throw error;
	}
};

export const registerAndPay = async (data) => {
	await api.post('/checkout/register', { ...data })
}

export const completeStoreCheckout = async (data) => {
	return await api.post('/sales/store/checkout-complete', { ...data }).then((res) => res.data);
}

// AI Email Template Generation APIs
export const generateEmailTemplateWithAI = async (description: string, options: {
	referenceTemplateIds?: string[];
	templateType?: string;
	tone?: string;
} = {}) => {
	try {
		const response = await api.post('/ai/email-template/generate', {
			description,
			...options
		}, { timeout: 120000 });
		console.log('✅ Email template generated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to generate email template:', error);
		throw error;
	}
};

export const generateWebpageTemplateWithAI = async (description: string) => {
	try {
		const response = await api.post('/ai/webpage-template/generate', {
			description
		}, { timeout: 120000 });
		console.log('✅ Webpage template generated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to generate webpage template:', error);
		throw error;
	}
};

export const suggestEmailTemplates = async (description: string, limit: number = 5) => {
	try {
		console.log('💡 Suggesting email templates for:', description);
		const response = await api.post('/ai/email-template/suggest', {
			description,
			limit
		}, { timeout: 120000 });
		console.log('✅ Template suggestions received:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to suggest templates:', error);
		throw error;
	}
};

export const generateDocumentTemplateWithAI = async (description: string, options: {
	referenceTemplateIds?: string[];
	documentType?: string;
	tone?: string;
} = {}) => {
	try {
		console.log('📄 Generating document template with AI:', { description, options });
		const response = await monitoredRequest.post('/ai/document-template/generate', {
			description,
			...options
		}, { timeout: 120000 });
		console.log('✅ Document template generated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to generate document template:', error);
		throw error;
	}
};

// Function to get all tasks for a project - placed before main export
export const getProjectTasks = async (projectId: string, globalId: string) => {
	try {
		console.log('📋 Getting tasks for project:', { projectId, globalId });
		const response = await noMonitor.get(`/projects/${globalId}/tasks/${projectId}`);
		console.log('📋 Tasks response:', response.data);
		return response;
	} catch (error) {
		console.error('❌ Failed to fetch project tasks:', error);
		throw error;
	}
};

// API Key Management Methods
export const createAPIKey = async (keyData: any) => {
	try {
		console.log('🔑 Creating API key:', keyData);
		const response = await monitoredRequest.post('/keys/create', keyData);
		console.log('🔑 API key created:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to create API key:', error);
		throw error;
	}
};

export const getAPIKeys = async () => {
	try {
		console.log('🔑 Fetching API keys');
		const response = await monitoredRequest.get('/keys');
		console.log('🔑 API keys fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch API keys:', error);
		throw error;
	}
};

export const deleteAPIKey = async (keyId: string) => {
	try {
		const id = String(keyId ?? '').trim();
		if (!id) throw new Error('Key ID is required');
		console.log('🔑 Deleting API key:', id);
		const response = await monitoredRequest.delete(`/keys/${encodeURIComponent(id)}`);
		console.log('🔑 API key deleted:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to delete API key:', error);
		throw error;
	}
};

export const toggleAPIKey = async (keyId: string) => {
	try {
		const id = String(keyId ?? '').trim();
		if (!id) throw new Error('Key ID is required');
		console.log('🔑 Toggling API key:', id);
		const response = await monitoredRequest.patch(`/keys/${encodeURIComponent(id)}/toggle`);
		console.log('🔑 API key toggled:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to toggle API key:', error);
		throw error;
	}
};

// AI Model API Key Management Functions
export const createAIModelKey = async (keyData: { provider: string; modelName: string; apiKey: string }) => {
	try {
		console.log('🤖 Creating AI model API key:', { provider: keyData.provider, modelName: keyData.modelName });
		const response = await monitoredRequest.post('/ai-model-keys/create', keyData);
		console.log('🤖 AI model API key created:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to create AI model API key:', error);
		throw error;
	}
};

export const getAIModelKeys = async () => {
	try {
		console.log('🤖 Fetching AI model API keys');
		const response = await noMonitor.get('/ai-model-keys');
		console.log('🤖 AI model API keys fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch AI model API keys:', error);
		throw error;
	}
};

/** Effective default model from ML service env (proxied by backend). */
export const getMlDefaultAIModel = async (): Promise<{
	provider?: string;
	modelName?: string;
	modelRef?: string;
} | null> => {
	try {
		const response = await monitoredRequest.get('/ai/ml-default-model');
		const data = response?.data?.data;
		if (data?.provider && data?.modelName) {
			return data;
		}
		return null;
	} catch {
		return null;
	}
};

export const updateAIModelKey = async (keyId: string, updates: { isDefault?: boolean; isActive?: boolean; modelName?: string }) => {
	try {
		console.log('🤖 Updating AI model API key:', keyId, updates);
		const response = await monitoredRequest.patch(`/ai-model-keys/${keyId}`, updates);
		console.log('🤖 AI model API key updated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to update AI model API key:', error);
		throw error;
	}
};

export const deleteAIModelKey = async (keyId: string) => {
	try {
		console.log('🤖 Deleting AI model API key:', keyId);
		const response = await monitoredRequest.delete(`/ai-model-keys/${keyId}`);
		console.log('🤖 AI model API key deleted:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to delete AI model API key:', error);
		throw error;
	}
};

export const toggleAIModelKey = async (keyId: string) => {
	try {
		console.log('🤖 Toggling AI model API key:', keyId);
		const response = await monitoredRequest.patch(`/ai-model-keys/${keyId}/toggle`);
		console.log('🤖 AI model API key toggled:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to toggle AI model API key:', error);
		throw error;
	}
};

export const regenerateAPIKey = async (keyId: string) => {
	try {
		const id = String(keyId ?? '').trim();
		if (!id) throw new Error('Key ID is required');
		console.log('🔑 Regenerating API key:', id);
		const response = await monitoredRequest.post(`/keys/${encodeURIComponent(id)}/regenerate`);
		console.log('🔑 API key regenerated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to regenerate API key:', error);
		throw error;
	}
};

// OAuth Token Management Methods
export const getOAuthTokens = async () => {
	try {
		console.log('🔐 Fetching OAuth tokens');
		const response = await noMonitor.get('/oauth/tokens');
		console.log('🔐 OAuth tokens fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch OAuth tokens:', error);
		throw error;
	}
};

export const revokeOAuthToken = async (tokenIndex: number) => {
	try {
		console.log('🔐 Revoking OAuth token:', tokenIndex);
		const response = await monitoredRequest.delete(`/oauth/tokens/${tokenIndex}`);
		console.log('🔐 OAuth token revoked:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to revoke OAuth token:', error);
		throw error;
	}
};

// Webhook Management Methods
export const createWebhook = async (webhookData: any) => {
	try {
		console.log('🔗 Creating webhook:', webhookData);
		const response = await monitoredRequest.post('/webhooks/create', webhookData);
		console.log('🔗 Webhook created:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to create webhook:', error);
		throw error;
	}
};

export const getWebhooks = async () => {
	try {
		console.log('🔗 Fetching webhooks');
		const response = await noMonitor.get('/webhooks');
		console.log('🔗 Webhooks fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch webhooks:', error);
		throw error;
	}
};

export const getAvailableEvents = async () => {
	try {
		console.log('🔗 Fetching available webhook events');
		const response = await noMonitor.get('/webhooks/events');
		console.log('🔗 Available events fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch available events:', error);
		throw error;
	}
};

export const updateWebhook = async (webhookId: string, updateData: any) => {
	try {
		console.log('🔗 Updating webhook:', webhookId, updateData);
		const response = await monitoredRequest.put(`/webhooks/${webhookId}`, updateData);
		console.log('🔗 Webhook updated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to update webhook:', error);
		throw error;
	}
};

export const deleteWebhook = async (webhookId: string) => {
	try {
		console.log('🔗 Deleting webhook:', webhookId);
		const response = await monitoredRequest.delete(`/webhooks/${webhookId}`);
		console.log('🔗 Webhook deleted:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to delete webhook:', error);
		throw error;
	}
};

export const toggleWebhook = async (webhookId: string) => {
	try {
		console.log('🔗 Toggling webhook:', webhookId);
		const response = await monitoredRequest.patch(`/webhooks/${webhookId}/toggle`);
		console.log('🔗 Webhook toggled:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to toggle webhook:', error);
		throw error;
	}
};

export const regenerateWebhookSecret = async (webhookId: string) => {
	try {
		console.log('🔗 Regenerating webhook secret:', webhookId);
		const response = await monitoredRequest.post(`/webhooks/${webhookId}/regenerate-secret`);
		console.log('🔗 Webhook secret regenerated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to regenerate webhook secret:', error);
		throw error;
	}
};

export const testWebhook = async (webhookId: string) => {
	try {
		console.log('🔗 Testing webhook:', webhookId);
		const response = await monitoredRequest.post(`/webhooks/${webhookId}/test`);
		console.log('🔗 Webhook test result:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to test webhook:', error);
		throw error;
	}
};

// User Stats Trends Methods
export const getUserStatsTrends = async (startDate: string, endDate: string, interval: string = 'day') => {
	try {
		const response = await noMonitor.post('/data/user-stats-trends', {
			startDate,
			endDate,
			interval
		});
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch user stats trends:', error);
		console.error('❌ Error details:', error.response?.data || error.message);
		throw error;
	}
};

// Chat Management API Functions
export const saveChat = async (chatData: {
	userId: string;
	companyId: string;
	messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string; timestamp?: Date }>;
	metadata?: {
		editorConnected?: boolean;
		sessionDuration?: number;
		aiModel?: string;
		[key: string]: any;
	};
}) => {
	return await monitoredRequest.post('/chats/save', chatData).then(res => res.data);
};

export const getUserChats = async (userId: string, options: {
	page?: number;
	limit?: number;
	isArchived?: boolean;
	sortBy?: 'lastMessageAt' | 'createdAt' | 'title';
	sortOrder?: 'asc' | 'desc';
} = {}) => {
	const params = new URLSearchParams();
	if (options.page) params.append('page', options.page.toString());
	if (options.limit) params.append('limit', options.limit.toString());
	if (options.isArchived !== undefined) params.append('isArchived', options.isArchived.toString());
	if (options.sortBy) params.append('sortBy', options.sortBy);
	if (options.sortOrder) params.append('sortOrder', options.sortOrder);

	const queryString = params.toString();
	return await noMonitor.get(`/chats/user/${userId}${queryString ? `?${queryString}` : ''}`).then(res => res.data);
};

export const getChatById = async (chatId: string, userId: string) => {
	return await noMonitor.get(`/chats/${chatId}?userId=${userId}`).then(res => res.data);
};

export const updateChatTitle = async (chatId: string, title: string, userId: string) => {
	return await monitoredRequest.put(`/chats/${chatId}/title`, { title, userId }).then(res => res.data);
};

export const toggleChatArchive = async (chatId: string, isArchived: boolean, userId: string) => {
	return await monitoredRequest.patch(`/chats/${chatId}/archive`, { isArchived, userId }).then(res => res.data);
};

export const deleteChat = async (chatId: string, userId: string) => {
	return await monitoredRequest.delete(`/chats/${chatId}?userId=${userId}`).then(res => res.data);
};

export const shareChat = async (payload: {
	chatId?: string;
	messages?: Array<{ role: 'user' | 'assistant' | 'system'; content: string; timestamp?: string | Date }>;
	title?: string;
	shareWithCompany?: boolean;
	userIds?: string[];
}) => {
	return await monitoredRequest.post('/chats/share', payload).then(res => res.data);
};

export const revokeChatShare = async (chatId: string, options: { userIds?: string[]; company?: boolean } = {}) => {
	return await monitoredRequest.delete(`/chats/${chatId}/share`, { data: options }).then(res => res.data);
};

export const getSharedChats = async (options: { page?: number; limit?: number } = {}) => {
	return await noMonitor.get('/chats/shared', { params: options }).then(res => res.data);
};

export const getSharedChatById = async (chatId: string) => {
	return await noMonitor.get(`/chats/shared/${chatId}`).then(res => res.data);
};

export const appendSharedChatMessages = async (
	chatId: string,
	messages: Array<{
		role: 'user' | 'assistant' | 'system';
		content: string;
		timestamp?: string | Date;
		clientId?: string;
		authorId?: string;
		authorName?: string;
	}>
) => {
	return await monitoredRequest.post(`/chats/shared/${chatId}/messages`, { messages }).then(res => res.data);
};

export const exportChatHistory = async (options: { includeArchived?: boolean } = {}) => {
	return await noMonitor
		.get('/chats/export', { params: options.includeArchived ? { includeArchived: 'true' } : undefined })
		.then(res => res.data);
};

// Dashboard Management API Functions
export const suggestDashboardComponents = async (dataSourceIds: string[]) => {
	return await monitoredRequest.post('/dashboards/suggest-components', { dataSourceIds }).then(res => res.data);
};

export const createDashboard = async (dashboardData: {
	title: string;
	description?: string;
	dataSourceIds: string[];
	layout?: {
		type?: 'grid' | 'freeform';
		columns?: number;
		spacing?: number;
	};
	components: Array<{
		componentId: string;
		componentType: string;
		config: any;
		dataMapping?: any;
		position?: { x: number; y: number };
		size?: { width: number; height: number };
	}>;
	tags?: string[];
	sharing?: DashboardSharing;
}) => {
	return await monitoredRequest.post('/dashboards/create', dashboardData).then(res => res.data);
};

export type DashboardSharing = {
	visibility?: 'private' | 'company';
	companyPermission?: 'view' | 'edit';
};

export type DashboardListScope = 'all' | 'mine' | 'shared';

export const getUserDashboards = async (userId: string, options: {
	page?: number;
	limit?: number;
	isArchived?: boolean;
	scope?: DashboardListScope;
} = {}) => {
	const params = new URLSearchParams();
	if (options.page) params.append('page', options.page.toString());
	if (options.limit) params.append('limit', options.limit.toString());
	if (options.isArchived !== undefined) params.append('isArchived', options.isArchived.toString());
	if (options.scope) params.append('scope', options.scope);

	const queryString = params.toString();
	return await noMonitor.get(`/dashboards/user/${userId}${queryString ? `?${queryString}` : ''}`).then(res => res.data);
};

export const getDashboard = async (dashboardId: string) => {
	return await noMonitor.get(`/dashboards/${dashboardId}`).then(res => res.data);
};

export const updateDashboard = async (dashboardId: string, updates: {
	title?: string;
	description?: string;
	layout?: any;
	components?: any[];
	dataSourceIds?: string[];
	tags?: string[];
	sharing?: DashboardSharing;
}) => {
	return await monitoredRequest.put(`/dashboards/${dashboardId}`, updates).then(res => res.data);
};

export const deleteDashboard = async (dashboardId: string) => {
	return await monitoredRequest.delete(`/dashboards/${dashboardId}`).then(res => res.data);
};

export const getUserChatStats = async (userId: string) => {
	return await noMonitor.get(`/chats/user/${userId}/stats`).then(res => res.data);
};

export const addMessageToChat = async (chatId: string, message: {
	role: 'user' | 'assistant' | 'system';
	content: string;
}, userId: string) => {
	return await monitoredRequest.post(`/chats/${chatId}/message`, { ...message, userId }).then(res => res.data);
};

// AI Chat Title Generation API Functions
export const generateChatTitle = async (firstMessage: string, messages: Array<{ role: string; content: string }> = []) => {
	return await noMonitor.post('/ai/chat/generate-title', { firstMessage, messages }, { timeout: 120000 }).then(res => res.data);
};


// Academy API Functions
// Course Management
export const getAcademyCourses = async (params: {
	page?: number;
	limit?: number;
	category?: string;
	level?: string;
	isPublic?: boolean;
	isPublished?: boolean;
	author?: string;
	search?: string;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
} = {}) => {
	return await noMonitor.get('/academy/courses', { params }).then(res => res.data);
};

export const getAcademyCourse = async (courseId: string, trackView: boolean = false, deviceType: string = 'desktop', source: string = 'direct') => {
	return await noMonitor.get(`/academy/courses/${courseId}`, {
		params: { trackView, deviceType, source }
	}).then(res => res.data);
};

export const createAcademyCourse = async (courseData: any) => {
	return await monitoredRequest.post('/academy/courses', courseData).then(res => res.data);
};

export const updateAcademyCourse = async (courseId: string, updateData: any) => {
	return await monitoredRequest.put(`/academy/courses/${courseId}`, updateData).then(res => res.data);
};

export const deleteAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}`).then(res => res.data);
};

export const publishAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/publish`, { action: 'publish' }).then(res => res.data);
};

export const unpublishAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/publish`, { action: 'unpublish' }).then(res => res.data);
};

export const archiveAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/archive`, { action: 'archive' }).then(res => res.data);
};

export const unarchiveAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/archive`, { action: 'unarchive' }).then(res => res.data);
};

export const getAcademyCourseAnalytics = async (courseId: string) => {
	return await noMonitor.get(`/academy/courses/${courseId}/analytics`).then(res => res.data);
};

export const trackAcademyCourseView = async (courseId: string, trackingData: {
	duration?: number;
	source?: string;
	deviceType?: string;
	location?: { country?: string; region?: string };
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/track-view`, trackingData).then(res => res.data);
};

export const addAcademyCourseReview = async (courseId: string, reviewData: {
	rating: number;
	comment?: string;
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/reviews`, reviewData).then(res => res.data);
};

// Course Voting
export const voteAcademyCourse = async (courseId: string, voteType: 'upvote' | 'downvote') => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/vote`, { voteType }).then(res => res.data);
};

export const removeAcademyCourseVote = async (courseId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/vote`).then(res => res.data);
};

export const getAcademyCourseVoting = async (courseId: string) => {
	return await noMonitor.get(`/academy/courses/${courseId}/voting`).then(res => res.data);
};

// Module Management
export const addAcademyModule = async (courseId: string, moduleData: any) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules`, moduleData).then(res => res.data);
};

export const getAcademyModule = async (courseId: string, moduleId: string, trackView: boolean = false) => {
	return await noMonitor.get(`/academy/courses/${courseId}/modules/${moduleId}`, {
		params: { trackView }
	}).then(res => res.data);
};

export const updateAcademyModule = async (courseId: string, moduleId: string, updateData: any) => {
	return await monitoredRequest.put(`/academy/courses/${courseId}/modules/${moduleId}`, updateData).then(res => res.data);
};

export const deleteAcademyModule = async (courseId: string, moduleId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/modules/${moduleId}`).then(res => res.data);
};

export const reorderAcademyModules = async (courseId: string, moduleOrders: Record<string, number>) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/modules/reorder`, { moduleOrders }).then(res => res.data);
};

export const trackAcademyModuleView = async (courseId: string, moduleId: string, duration: number = 0) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/track-view`, { duration }).then(res => res.data);
};

export const trackAcademyModuleCompletion = async (courseId: string, moduleId: string, completionTime: number = 0) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/complete`, { completionTime }).then(res => res.data);
};

export const updateAcademyModuleContent = async (courseId: string, moduleId: string, content: string, contentType?: string) => {
	return await monitoredRequest.put(`/academy/courses/${courseId}/modules/${moduleId}/content`, { content, contentType }).then(res => res.data);
};

export const addAcademyModuleResource = async (courseId: string, moduleId: string, resourceData: any) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/resources`, resourceData).then(res => res.data);
};

export const removeAcademyModuleResource = async (courseId: string, moduleId: string, resourceId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/modules/${moduleId}/resources/${resourceId}`).then(res => res.data);
};

// Public Academy Routes
export const getPublicAcademyCourses = async (params: {
	page?: number;
	limit?: number;
	category?: string;
	level?: string;
	search?: string;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	isPublic?: boolean;
	isPublished?: boolean;
} = {}) => {
	return await noMonitor.get('/academy/public/courses', { params }).then(res => res.data);
};

export const getPublicAcademyCourse = async (courseId: string, trackView: boolean = false, deviceType: string = 'desktop', source: string = 'direct') => {
	return await noMonitor.get(`/academy/public/courses/${courseId}`, {
		params: { trackView, deviceType, source }
	}).then(res => res.data);
};

export const getPublicAcademyModule = async (courseId: string, moduleId: string, trackView: boolean = false) => {
	return await noMonitor.get(`/academy/public/courses/${courseId}/modules/${moduleId}`, {
		params: { trackView }
	}).then(res => res.data);
};

export const getPublicAcademyCourseStats = async (courseId: string) => {
	return await noMonitor.get(`/academy/public/courses/${courseId}/stats`).then(res => res.data);
};

// Public Learning Route - Access course via /learn/:id URL
export const getAcademyCourseByLearnUrl = async (courseId: string) => {
	return await noMonitor.get(`/academy/learn/${courseId}`).then(res => res.data);
};

// Search and Discovery
export const searchAcademyCourses = async (params: {
	q?: string;
	category?: string;
	level?: string;
	sortBy?: 'relevance' | 'newest' | 'oldest' | 'rating' | 'popular' | 'enrollments';
	page?: number;
	limit?: number;
}) => {
	return await noMonitor.get('/academy/search', { params }).then(res => res.data);
};

export const getAcademyCoursesByCategory = async (category: string, params: {
	page?: number;
	limit?: number;
	sortBy?: 'popular' | 'rating' | 'newest';
} = {}) => {
	return await noMonitor.get(`/academy/categories/${category}`, { params }).then(res => res.data);
};

export const getFeaturedAcademyCourses = async (limit: number = 10) => {
	return await noMonitor.get('/academy/featured', { params: { limit } }).then(res => res.data);
};

export const getTrendingAcademyCourses = async (limit: number = 10, timeRange: '1d' | '7d' | '30d' = '7d') => {
	return await noMonitor.get('/academy/trending', { params: { limit, timeRange } }).then(res => res.data);
};

// Analytics and Tracking
export const trackAcademyEngagement = async (courseId: string, engagementData: {
	action: 'view' | 'enroll' | 'complete' | 'review' | 'share';
	moduleId?: string;
	duration?: number;
	metadata?: any;
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/track-engagement`, engagementData).then(res => res.data);
};

export const getAcademyUserProgress = async (courseId: string) => {
	return await noMonitor.get(`/academy/courses/${courseId}/progress`).then(res => res.data);
};

export const updateAcademyUserProgress = async (courseId: string, progressData: {
	moduleId: string;
	completed: boolean;
	progress: number; // 0-100
	timeSpent: number;
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/progress`, progressData).then(res => res.data);
};

// Course Enrollment
export const enrollInAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/enroll`).then(res => res.data);
};

export const unenrollFromAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/enroll`).then(res => res.data);
};

export const getAcademyEnrollments = async (params: {
	page?: number;
	limit?: number;
	status?: 'active' | 'completed' | 'dropped';
} = {}) => {
	return await noMonitor.get('/academy/enrollments', { params }).then(res => res.data);
};

// Course Content Upload
export const uploadAcademyCourseThumbnail = async (courseId: string, imageData: string, fileName: string, mimeType: string) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/upload-thumbnail`, {
		imageData,
		fileName,
		mimeType
	}).then(res => res.data);
};

export const uploadAcademyCourseBanner = async (courseId: string, imageData: string, fileName: string, mimeType: string) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/upload-banner`, {
		imageData,
		fileName,
		mimeType
	}).then(res => res.data);
};

export const createDocumentTemplate = async (templateData: any) => {
	return await monitoredRequest.post('templates/documents/post-template', templateData).then(res => res.data);
};

export const updateDocumentTemplate = async (templateId: string, templateData: any) => {
	return await monitoredRequest.put(`/templates/documents/${templateId}`, templateData).then(res => res.data);
};

export type SignatureCaptureSession = { token: string; url: string; expiresAt: number };
export type SignatureCapturePoll =
	| { status: 'pending'; expiresAt: number }
	| { status: 'completed'; signature: string }
	| { status: 'expired' };

export const createSignatureCaptureSession = async (payload: { label?: string; locale?: string }): Promise<SignatureCaptureSession> => {
	return await noMonitor.post('/templates/documents/signature-capture', payload).then(res => res.data);
};

export const pollSignatureCaptureSession = async (token: string): Promise<SignatureCapturePoll> => {
	return await noMonitor
		.get(`/templates/documents/signature-capture/${encodeURIComponent(token)}`, { validateStatus: (status) => status === 200 || status === 404 })
		.then(res => (res.status === 404 ? { status: 'expired' as const } : res.data));
};

export const uploadAcademyModuleResource = async (courseId: string, moduleId: string, fileData: FormData) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/upload-resource`, fileData, {
		headers: { 'Content-Type': 'multipart/form-data' }
	}).then(res => res.data);
};

export const generateChatTitleSuggestions = async (firstMessage: string, messages: Array<{ role: string; content: string }> = []) => {
	return await noMonitor.post('/ai/chat/generate-title-suggestions', { firstMessage, messages }, { timeout: 120000 }).then(res => res.data);
};

// AI Image Enhancement
export const enhanceProductImage = async (imageUrl?: string, imageBase64?: string, enhancementType: string = 'upscale', scale: number = 2, prompt?: string, style?: string) => {
	return await monitoredRequest.post('/ai/enhance-product-image', {
		imageUrl,
		imageBase64,
		enhancementType,
		scale,
		prompt,
		style
	}, { timeout: 120000 }).then(res => res.data);
};

// AI Image Analysis using LLaVA 13B
export const analyzeImage = async (options: {
	image?: string; // base64 encoded image
	imageUrl?: string; // URL to image
	imageFile?: File | Blob; // File object for upload
	prompt?: string; // Custom analysis prompt
	stream?: boolean; // Whether to stream the response
}) => {
	const { image, imageUrl, imageFile, prompt, stream } = options;

	// If imageFile is provided, use FormData
	if (imageFile) {
		const formData = new FormData();
		formData.append('image', imageFile);
		if (prompt) formData.append('prompt', prompt);
		if (stream) formData.append('stream', stream.toString());

		return await monitoredRequest.post('/ai/analyze-image', formData, {
			timeout: 120000,
			headers: { 'Content-Type': 'multipart/form-data' }
		}).then(res => res.data);
	}

	// Otherwise, send as JSON
	return await monitoredRequest.post('/ai/analyze-image', {
		image,
		imageUrl,
		prompt,
		stream
	}, { timeout: 120000 }).then(res => res.data);
};

// Check image analysis service health
export const checkImageAnalysisHealth = async () => {
	return await noMonitor.get('/ai/analyze-image/health').then(res => res.data);
};


/**
 * Trim context entries to fit within limits (keeping most recent entries)
 * Limits: 500k tokens, 500k bytes, 10k entries
 */
const trimContextEntries = (entries: any[]): { trimmed: any[], originalCount: number } => {
	const MAX_PAYLOAD_TOKENS = 500000;
	const MAX_PAYLOAD_BYTES = 500000;
	const MAX_ENTRIES = 10000;

	// Calculate total size
	let totalTokens = 0;
	let totalBytes = 0;
	for (const entry of entries) {
		totalTokens += entry.tokenCount || 0;
		totalBytes += entry.byteCount || 0;
	}

	// If already within limits, return as-is
	if (totalTokens <= MAX_PAYLOAD_TOKENS && totalBytes <= MAX_PAYLOAD_BYTES && entries.length <= MAX_ENTRIES) {
		return { trimmed: entries, originalCount: entries.length };
	}

	// Start from the end (most recent entries) and work backwards
	let trimmedTokens = 0;
	let trimmedBytes = 0;
	const trimmedEntries: any[] = [];

	// Process entries in reverse order (most recent first)
	for (let i = entries.length - 1; i >= 0; i--) {
		const entry = entries[i];
		const entryTokens = entry.tokenCount || 0;
		const entryBytes = entry.byteCount || 0;

		// Check if adding this entry would exceed limits
		if (
			trimmedEntries.length >= MAX_ENTRIES ||
			trimmedTokens + entryTokens > MAX_PAYLOAD_TOKENS ||
			trimmedBytes + entryBytes > MAX_PAYLOAD_BYTES
		) {
			break;
		}

		trimmedEntries.unshift(entry); // Add to beginning to maintain chronological order
		trimmedTokens += entryTokens;
		trimmedBytes += entryBytes;
	}

	if (trimmedEntries.length < entries.length) {
		console.log(`[ContextSummary] Client-side trimming: ${entries.length} → ${trimmedEntries.length} entries (${totalTokens} → ${trimmedTokens} tokens, ${totalBytes} → ${trimmedBytes} bytes)`);
	}

	return { trimmed: trimmedEntries, originalCount: entries.length };
};

export const summarizeContext = async (contextEntries: any[]) => {
	// Trim entries on client side to prevent 413 errors
	const { trimmed, originalCount } = trimContextEntries(contextEntries);

	return await api.post('/ai/context/summarize', { entries: trimmed }, { timeout: 120000 })
		.then(res => {
			// Validate response structure
			if (!res.data || !res.data.success) {
				const error = new Error(res.data?.error || 'Invalid response from summarization service');
				(error as any).response = { status: res.status, data: res.data };
				throw error;
			}
			return res.data;
		})
		.catch(error => {
			// Handle specific error cases
			if (error.response?.status === 403) {
				const premiumError = new Error(error.response?.data?.error || 'Premium subscription required');
				(premiumError as any).requiresPremium = true;
				(premiumError as any).response = error.response;
				throw premiumError;
			}
			if (error.response?.status === 413) {
				// If still getting 413, the payload is too large even after trimming
				// This shouldn't happen, but handle it gracefully
				const payloadError = new Error('Payload too large even after trimming. Please reduce context size.');
				(payloadError as any).response = error.response;
				throw payloadError;
			}
			if (error.response?.status === 503) {
				const serviceError = new Error(error.response?.data?.error || 'Service temporarily unavailable');
				(serviceError as any).retryable = true;
				(serviceError as any).response = error.response;
				throw serviceError;
			}
			console.error('Failed to summarize context:', error);
			throw error;
		});
}
// summarizeContext is exported from apisObject below

// Booking API functions
export const getBookings = async (params: Record<string, any> = {}) => {
	const cleaned = Object.fromEntries(
		Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
	);
	const queryString = new URLSearchParams(cleaned as Record<string, string>).toString();
	return await noMonitor.get(`/calendar/get-bookings${queryString ? `?${queryString}` : ''}`).then(res => res.data);
};

export const getBooking = async (id) => {
	return await noMonitor.get(`/calendar/get-booking/${id}`).then(res => res.data);
};

export const updateBooking = async (id, data) => {
	return await noMonitor.put(`/calendar/update-booking/${id}`, data).then(res => res.data);
};

export const cancelBooking = async (id, reason) => {
	return await noMonitor.put(`/calendar/cancel-booking/${id}`, { reason }).then(res => res.data);
};

export const deleteBooking = async (id) => {
	return await noMonitor.delete(`/calendar/delete-booking/${id}`).then(res => res.data);
};


const apisObject = {
	getBounties,
	updateDataItemHistory,
	getBountySchema,
	addDataDescription,
	deleteAccount,
	getAccountDeletionPolicy,
	getCompanyArchivePolicy,
	getCompanyArchiveEligibility,
	requestCompanyArchive,
	addUserToDepartment,
	createAffiliateCode,
	aiCommentAnalysis,
	uploadGlobalAsset,
	updateGlobalAsset,
	deleteGlobalAsset,
	getCustomerPortal,
	getStoreInfo,
	addUserToCompany,
	getCompanyList,
	revokeInvitation,
	acceptInvitation,
	rejectInvitation,
	addCompany,
	listLinkedOrganisations,
	listLinkedOrganisationPackages,
	createLinkedOrganisation,
	convertLinkedOrganisation,
	listLinkedOrganisationGrants,
	grantLinkedOrganisationDatasets,
	revokeLinkedOrganisationGrant,
	getSponsorshipStatus,
	listSharingScopes,
	createSharingScope,
	updateSharingScope,
	deleteSharingScope,
	createSharingScopeDataset,
	migrateDemoSnapshot,
	setDefaultCompany,
	getDataTypes,
	switchCompany,
	getActivityStreamLog,
	getAllUsers,
	getStream,
	logoutUser,
	registerUser,
	sendWhatsAppRegisterOtp,
	verifyWhatsAppRegisterOtp,
	completeWhatsAppRegister,
	setCurrentUser,
	setUserLoading,
	retrieveQueriesByID,
	insertQueryHistory,
	getLocalWeather,
	postAvatar,
	sendUserReg,
	getLocalNews,
	getUserConversations,
	getConversation,
	postMessage,
	unlockUser,
	getCompanyInformation,
	getCompanyInformationName,
	authoriseLinkedIn,
	companyCheck,
	getLargeProfile,
	postLogo,
	quoteToday,
	mailLogin,
	getProject,
	getDefaultWebStats,
	getAllGmail,
	getGmailCount,
	getGmailLabelCounts,
	getGDriveItemsList,
	getGDriveFolderContent,
	postDeskImage,
	createPaymentIntent,
	genKnowledge,
	refreshAchievements,
	addTwofa,
	disableMfa,
	toggleCanBeInvited,
	setRestrictRequestsToNetwork,
	retrieveCompanyBillingInfo,
	passwordReset,
	dispatchAppAction,
	validatePasswordReset,
	getLoginMessages,
	getData,
	getProjects,
	getProjectsPaginated,
	addProjects,
	updateProject,
	getProjectSchema,
	getAddNetworkSchema,
	getTaskSchema,
	getCalendarSchema,
	retrieveUserBillingInfo,
	getProducts,
	getProductById,
	getSession,
	getUserCompanies,
	getSampleSchemas,
	saveData,
	getDataContent,
	createDatabaseConnection,
	getDatabaseConnections,
	getDatabaseConnection,
	updateDatabaseConnection,
	deleteDatabaseConnection,
	testDatabaseConnection,
	getDatabaseTables,
	importDatabaseSchema,
	getSentInvoices,
	searchInvoiceRecipients,
	sendInvoiceEmail,
	handleOAuth,
	getWeChatAuthUrl,
	wechatOAuthLogin,
	paymentSheet,
	generateProductPaymentLink,
	recordPaymentLinkSent,
	getProductSalesStats,
	getProductSalesDashboard,
	getSubscriptions,
	getIntegrationsWithAuth,
	getIntegrations,
	getIntegrationSuggestions,
	createIntegrationRequest,
	getIntegrationRequests,
	getPopularIntegrationRequests,
	getIntegrationRequestStats,
	voteOnIntegrationRequest,
	updateIntegrationRequestStatus,
	authoriseGMail,
	authoriseMicrosoftMail,
	addDataItem,
	confirmAiTool,
	getReminders,
	createReminder,
	cancelReminder,
	getAgentActivity,
	dismissAgentActivity,
	undoAgentActivity,
	getAssistantSuggestions,
	resolveAssistantSuggestion,
	getProfileSchema,
	getUserSocialMedia,
	updateUserSocialMedia,
	addUserTags,
	updateUserProfile,
	updateUserContext,
	updateUserDepartment,
	assignUserToDepartment,
	getUserBranding,
	updateUserBranding,
	uploadBrandingImage,
	updateNotificationToken,
	getAvatarById,
	addTask,
	searchUsers,
	getReport,
	markRead,
	getTags,
	addCalendarEntry,
	updateCalendarEntry,
	removeCalendarEntry,
	getCalendarContent,
	getProductList,
	getWPSchema,
	addWorkPackage,
	getCatalogDeleteBlocks,
	deleteDataItem,
	deleteProject,
	getProfileCompletion,
	getProjectCount,
	getDataCount,
	saveNewsItem,
	getSavedNews,
	addCalendar,
	sendGoogleMail,
	addStickyNote,
	modifyStickyNote,
	removeStickyNote,
	getStickyNotes,
	getCompanyPinnedNotes,
	pinNoteCompanyWide,
	unpinCompanyNote,
	markNoteComplete,
	setNoteColor,
	getInitialModels,
	identifyOnboardingModels,
	createPreviewBlueprint,
	claimPreview,
	inferOnboardingProductCatalog,
	bootstrapOnboardingProductCatalog,
	manipulateHierarchy,
	suggestAutomationFlow,
	postInitialModel,
	getGoogleCalendarList,
	getStandardisedReports,
	validate2FA,
	validateOTP,
	getDataSchema,
	setTutorial,
	getRoomCounts,
	getRoom,
	updateTC,
	getActivityLog,
	getAchievementHistory,
	shareDataItem,
	getUser,
	getInvitations,
	getInvitationCount,
	getRemainingProfile,
	getPublicData,
	getTrendingDataTags,
	getDatasetCategorySuggestions,
	setDatasetCategory,
	acceptCompanyInvitation,
	updateDataItem,
	uploadData,
	setPublicDataSwipeTutorial,
	setInvitationSwipeTutorial,
	addLocation,
	hideFirstTutorial,
	saveEmailTemplate,
	getWebpageTemplates,
	getSingleWebpageTemplate,
	deleteWebpageTemplate,
	saveWebpageTemplate,
	updateWebpageTemplate,
	duplicateWebpageTemplate,
	getSites,
	createSite,
	getSite,
	deleteSite,
	startSitePreview,
	stopSitePreview,
	sitePreviewHeartbeat,
	getSiteFiles,
	saveSiteFiles,
	promptSite,
	getSiteVisualElement,
	visualEditSite,
	getSiteVersions,
	restoreSiteVersion,
	publishSite,
	getSiteDeployments,
	getSiteDeployment,
	activateSiteDeployment,
	addSiteDomain,
	refreshSiteDomain,
	removeSiteDomain,
	getSiteGitHub,
	getSiteAnalytics,
	linkSiteGitHub,
	setSiteGitHubAutoPush,
	unlinkSiteGitHub,
	pushSiteToGitHub,
	getEmailTemplates,
	deleteInvoiceTemplate,
	getInvoiceTemplates,
	getInvoiceById,
	getDocumentTemplates,
	getInitialTemplates,
	deleteDocumentTemplate,
	deletePost,
	getEmailTemplate,
	editEmailTemplate,
	deleteEmailTemplate,
	getServiceList,
	getUserById,
	getPublicProfile,
	trackFormView,
	trackFormSubmission,
	trackDataContribution,
	trackDataInteraction,
	changeWPStatus,
	changeWorkPackageTitle,
	deleteWorkPackage,
	changeTaskTitle,
	changeTaskDescription,
	deleteTask,
	changeTaskStatus,
	moveTask,
	bulkMoveTasks,
	changeProjectTitle,
	changeProjectStatus,
	changeProjectDescription,
	shareProject,
	addFavourite,
	getDataFavorites,
	refreshUser,
	fetchCurrentUser,
	addTagsToData,
	generateBusinessCard,
	addLocalCalendars,
	addDataImage,
	getDataContentById,
	postInvoiceTemplate,
	setDefaultInvoiceTemplate,
	getFeedbackSchema,
	getFeedback,
	postFeedback,
	postOrganisationEnquiry,
	postReportProblem,
	getProductServiceList,
	draftCalendarFromPrompt,
	getMarketplaceData,
	getProductSyncStatus,
	pullProductsFromIntegration,
	pushProductsToIntegration,
	resolveProductConflict,
	getAvailableIntegrations,
	searchRegEx,
	getIntegrationApiAction,
	createStorePaymentIntent,
	createStripeStandardConnectLink,
	getStripeId,
	getStoreSettings,
	updateStoreSettings,
	getFloorPlan,
	updateFloorPlan,
	deleteFloorPlan,
	registerStripeLocation,
	registerStripeReader,
	confirmPaymentIntent,
	getAIForm,
	getFeed,
	postFeedEvents,
	getAgentFeed,
	getAgentTasks,
	cancelAgentTask,
	getGlobalBotRules,
	updateGlobalBotRules,
	getAgentRules,
	getAgentToolCatalog,
	updateAgentRules,
	getAgentNotificationPrefs,
	updateAgentNotificationPrefs,
	patchAgentCoreFiles,
	reinitialiseAgents,
	getAgents,
	dispatchMediaSocialBrief,
	updateAgentDepartment,
	getAgentDeployQuote,
	getAgentUsage,
	deleteAgent,
	getFeedCategories,
	getExternalAds,
	trackAdImpression,
	trackAdClick,
	postToFeed,
	getCompanyBoard,
	postCompanyBoard,
	patchCompanyBoard,
	hideCompanyBoardPost,
	unhideCompanyBoardPost,
	deleteCompanyBoardPost,
	addLikeToPost,
	addShareToPost,
	removeLikeFromPost,
	removeShareFromPost,
	addCommentToPost,
	blockUser,
	unblockUser,
	hidePost,
	unhidePost,
	getBlockedUsers,
	getHiddenPostsCount,
	getAICommentAnalysis,
	getExtendedData,
	getUserHierarchy,
	getCompanySchema,
	storeDeviceToken,
	getDataFolders,
	getFolderStats,
	addPOIsToDataModel,
	scrapeArticleToKnowledge,
	replaceSchema,
	getDataItemById,
	retrieveProduct,
	getCheckoutSession,
	syncCheckoutSession,
	getWinBackOffer,
	claimWinBackOffer,
	createSubscriptionPaymentSheet,
	createCryptoSubscriptionCheckout,
	syncCryptoSubscriptionCheckout,
	getCryptoPaymentStatus,
	changeSubscription,
	syncCalendars,
	syncCalendarEvents,
	getCalendars,
	makeDataPublic,
	unpublishData,
	updateDataSettings,
	updateWorkPackage,
	getKnowledgeBase,
	listKnowledgeDecks,
	getKnowledgeDeck,
	saveKnowledgeDeck,
	getInternalKnowledgePaginated,
	genEmail,
	getContributionChart,
	getAIReportQuery,
	searchMail,
	listGmailMessageIds,
	getGmailMessageHeaders,
	googleAuthCallback,
	buildLink,
	sendInvoice,
	getInventoryData,
	getAddRoomSchema,
	getSalesStats,
	saveForecast,
	getForecasts,
	getForecast,
	saveBusinessPlan,
	getBusinessPlans,
	getBusinessPlan,
	deleteBusinessPlan,
	getBusinessPlanRevisions,
	getBusinessPlanRevision,
	restoreBusinessPlanRevision,
	labelBusinessPlanRevision,
	conductMarketResearch,
	saveDocumentDraft,
	getDocumentDrafts,
	getDocumentDraft,
	deleteDocumentDraft,
	deleteForecast,
	analyzeForecast,
	getSalesAnalytics,
	listAnalyticsConnectors,
	saveAnalyticsConnector,
	deleteAnalyticsConnector,
	getAnalyticsConnectorReport,
	getOpenBankingStatus,
	getOpenBankingInstitutions,
	connectOpenBanking,
	getOpenBankingConnections,
	syncOpenBankingConnection,
	disconnectOpenBanking,
	getOpenBankingCashActuals,
	getOpenBankingTransactions,
	setOpenBankingCategory,
	getSalesKanbanStages,
	updateSalesKanbanStages,
	getSalesKanbanOrders,
	moveSalesOrderStage,
	getCrmCount,
	getActiveCampaigns,
	postNewCampaign,
	getDefaultEmailTemplates,
	postInternalKnowledge,
	postExternalKnowledge,
	getInternalKnowledge,
	deleteKnowledgeArticle,
	saveKnowledgeContent,
	publishKnowledgeArticle,
	getKnowledgePublishSettings,
	saveKnowledgePublishSettings,
	unpublishKnowledgeArticle,
	updateKnowledgeArticleStatus,
	getKnowledgeContent,
	getKnowledgeView,
	addKnowledgeAnnotation,
	deleteKnowledgeAnnotation,
	getKnowledgeHistory,
	getMonitoredUrls,
	addMonitoredUrl,
	validateMonitoredUrl,
	deleteMonitoredUrl,
	getTeam,
	getCRMFolders,
	getTemplates,
	updateWorkPackageDue,
	updateTaskDueDate,
	getCampaign,
	saveDataApprovalSettings,
	saveDataLifecycle,
	saveWelcomeEmail,
	getEditApprovals,
	decideEditApproval,
	saveCampaignAbTest,
	getCampaignEffectiveness,
	shareCampaignUpdate,
	linkCampaignProducts,
	getKnowledgeContributions,
	createZoomMeeting,
	saveMeetingNotes,
	syncInvoiceAccounting,
	setInvoiceAutoReminders,
	listOneDriveFiles,
	downloadOneDriveFile,
	getStoreShareLink,
	pushContactToCrm,
	addCampaignStage,
	deleteCampaignStage,
	moveLeadToStage,
	changeTaskNotes,
	searchDataRegEx,
	handleChangeTaskDescription,
	handleChangeTaskTitle,
	changeTaskPosition,
	modifyTask,
	resolveTaskAgentAttempt,
	assignTaskToAgent,
	googleOAuth,
	getDataTemplateCount,
	updateEmailTemplate,
	setEmailGlobal,
	getGlobalAssets,
	getTemplateById,
	getTargetAudience,
	revokeGoogleScope,
	searchCompanies,
	googleMapSearch,
	googleMapSearchPaginated,
	getUserRecents,
	addRecent,
	getTrending,
	getUserStats,
	parseGetUserStatsPayload,
	getUserStatsData,
	getApiCallHistory,
	getApiCallsOverTime,
	getConnectedIntegrationsCount,
	getAiLogs,
	getKnowledgeByViews,
	cloneData,
	transferData,
	transferDataOwnership,
	transferProjectOwnership,
	cancelUserCreation,
	userLoginHistory,
	getCrmItems,
	getCrmClients,
	uploadDataImage,
	getAllFolderStats,
	getMainCharts,
	requestDataDownload,
	getStartupSchemas,
	startCampaign,
	changeDataTitle,
	requestLastPage,
	getProductsInternal,
	assignExemptProduct,
	requestNextPage,
	searchGmail,
	addDepartment,
	updateDepartment,
	setCompanyName,
	addCompanyLocation,
	resetCompanyPositions,
	updateCalendarSettings,
	postDataNote,
	deleteDataNote,
	updateDataActions,
	removeIntegration,
	retrieveCompanyUserStats,
	getAffiliateHistory,
	getAffiliatePayoutAccount,
	getStatCounts,
	getMeetings,
	getRecentMeetings,
	createMeetingRoom,
	getMeetingSchema,
	transcribeMeeting,
	modifyFolder,
	getCallFlows,
	createCallFlow,
	getUserPosts,
	// Competitor Analysis APIs
	createCompetitor,
	getCompetitors,
	getCompetitor,
	updateCompetitor,
	deleteCompetitor,
	discoverCompetitorSocial,
	confirmCompetitorSocial,
	getCompetitorSocial,
	getSuggestedCompetitors,
	analyzeCompetitorContent,
	getCompetitorInsights,
	getCompetitorStrategy,
	getCompetitorPosts,
	updateBilling,
	reportAppStorePurchaseSheetDismissed,
	syncAffiliateReferralPending,
	bumpViewCount,
	likeComment,
	// Feed Boost functions
	getBoostPricing,
	getPaidOutreachStatus,
	createPaidOutreachBrief,
	createPaidOutreachPaymentIntent,
	startPaidOutreach,
	createPostBoost,
	pausePostBoost,
	resumePostBoost,
	cancelPostBoost,
	getPostBoostAnalytics,
	getAllUserBoosts,
	trackBoostInteraction,
	createBounty,
	flagItem,
	getFlaggedItems,
	getFlaggedProjectTasks,
	setProjectTaskFlagged,
	getTrackedItemHistory,
	getExpiredCampaigns,
	getCompletedProjects,
	addIntegration,
	sendTestEmail,
	testWhatsApp,
	addSSCal,
	getWholeCalendarSchema,
	authManMail,
	getMailBoxes,
	setDefaultMailbox,
	deleteMailbox,
	readMailbox,
	listMailResponseDrafts,
	sendMailResponseDraft,
	discardMailResponseDraft,
	getAffiliateDetails,
	deleteCalendar,
	deleteCalendarEntry,
	analyseDataset,
	getMapList,
	testNotice,
	extractResume,
	getResumeTemplates,
	applyResumeTemplate,
	cancelApplePlan,
	cancelGooglePlan,
	cancelPlayPlan,
	cancelStripePlan,
	getWhatsAppProfile,
	getWhatsappTemplates,
	getWhatsAppCatalogs,
	createWhatsAppCatalog,
	selectWhatsAppCatalog,
	verifyWhatsAppCatalog,
	updateWhatsAppConversationModelSettings,
	sendTemplateMessage,
	moveDataItem,
	verifyCouponCode,
	redeemTrialPromoCode,
	getTrialPromoEligibility,
	getStripeProfile,
	syncShopifyProducts,
	getStripeSalesStats,
	getStripeMonthlySalesOverview,
	getStripeRecentTransactions,
	getStripePaymentMethodStats,
	getPayStackSalesStats,
	getPayStackMonthlySalesOverview,
	getPayStackRecentTransactions,
	getPayStackPaymentMethodStats,
	getXProfile,
	getLinkedInProfile,
	getLinkedInPosts,
	setLinkedInDefaultCompany,
	scheduleToFeed,
	scheduleMultipleSocialPosts,
	generateCampaignPosts,
	cancelScheduledPost,
	getScheduledPosts,
	getOpenClawScheduledJobs,
	updateScheduledPost,
	getAffiliateCode,
	getWordpressProfile,
	getWordPressBlogDetails,
	buildProductContent,
	deleteData,
	getDeletedItems,
	restoreDeletedItem,
	purgeDeletedItem,
	createDocumentTemplate,
	updateDocumentTemplate,
	getSlackProfile,
	getGitHubProfile,
	listGitHubRepos,
	getGitHubRepoContents,
	upsertGitHubRepoFile,
	createGitHubRepo,
	scaffoldGitHubPerformanceSite,
	getGitHubPagesInfo,
	connectPlatform,
	connectPlatformCallback,
	getAINotes,
	changePublishSettings,
	getInstagramProfile,
	getInstagramMedia,
	registerLinkedIn,
	getFacebookProfile,
	getYoutubeProfile,
	getYoutubeVideos,
	updateYoutubeSubscription,
	clearModalTimeout,
	reportError,
	trackUsage,
	getUsageStats,
	getMostUsedItems,
	bulkTrackUsage,
	// Timeline CRM functions
	getTimelineData,
	getCustomerSales,
	addContactNote,
	addContactTask,
	toggleTask,
	logInteraction,
	updateContactMetrics,
	analyzeCustomerHealth,
	getCachedCustomerHealthAnalysis,
	getCRMTimelineFolders,
	// Product Management functions
	getProductDetails,
	addProductSupplier,
	getProductSuppliers,
	updateProductSupplier,
	deleteProductSupplier,
	updateProductPricing,
	addQualityCheck,
	getProductAnalytics,
	updateProductListingMode,
	getProductInventory,
	updateProductInventory,
	trackDataView,
	getDataStats,
	// Utility Functions
	saveUtility,
	getUtilitiesForData,
	getUtility,
	updateUtility,
	deleteUtility,
	linkUtilitiesToData,
	executeUtility,
	buildUtilityPipeline,
	// Advanced Analytics
	getDataInsights,
	keywordLookup,
	lookupDataByEmail,
	getFolderComparison,
	getPredictiveAnalytics,
	getPlanProducts,
	getPlanSubscriptions,
	createNewAppSubscription,
	registerAndPay,
	completeStoreCheckout,
	// Networking APIs
	getNetworkingBusinesses,
	getNetworkingBusinessById,
	searchNetworkingBusinesses,
	verifyOTPCode,
	// Modern Project Management APIs
	moveKanbanTask,
	bulkKanbanActions,
	getKanbanColumns,
	getGanttData,
	updateGanttTimeline,
	manageTaskDependencies,
	getProjectAnalytics,
	getProgressAnalytics,
	getTeamPerformanceAnalytics,
	getTimeTrackingAnalytics,
	addTeamMember,
	inviteTeamMember,
	updateTeamMemberRole,
	removeTeamMember,
	uploadProjectFiles,
	getProjectFiles,
	deleteProjectFile,
	downloadProjectFile,
	updateProjectSettings,
	getProjectTasks,
	bulkCreateTasks,
	addTaskComment,
	getTaskComments,
	logTaskTime,
	sendDocumentEmail,
	sendTemplateToCrm,
	getDocumentShareHistory,
	// Custom Views API
	getCustomViews,
	getCustomView,
	saveCustomView,
	deleteCustomView,
	duplicateCustomView,
	getCustomViewTemplates,
	// AI Project Creation
	createAIProject,
	generateAIProjectFromDescription,
	getAIProjectTemplates,
	// Paginated Data APIs
	getDataContentPaginated,
	previewLiveSource,
	syncLiveDataset,
	updateLiveSource,
	getLiveTimeline,
	getDataFoldersPaginated,
	getAllDataItems,
	getAllMessagingUsers,
	getChatConversationMembers,
	leaveChatConversation,
	setChatConversationMuted,
	createAPIKey,
	getAPIKeys,
	deleteAPIKey,
	toggleAPIKey,
	createAIDataModel,
	regenerateAPIKey,
	createAIModelKey,
	getAIModelKeys,
	getMlDefaultAIModel,
	updateAIModelKey,
	deleteAIModelKey,
	toggleAIModelKey,
	getOAuthTokens,
	revokeOAuthToken,
	createWebhook,
	getWebhooks,
	getAvailableEvents,
	updateWebhook,
	deleteWebhook,
	toggleWebhook,
	regenerateWebhookSecret,
	testWebhook,
	getUserStatsTrends,
	// Chat Management
	saveChat,
	getUserChats,
	getChatById,
	updateChatTitle,
	toggleChatArchive,
	deleteChat,
	shareChat,
	revokeChatShare,
	getSharedChats,
	getSharedChatById,
	exportChatHistory,
	getUserChatStats,
	addMessageToChat,
	// AI Chat Title Generation
	generateChatTitle,
	generateChatTitleSuggestions,
	// AI Image Enhancement
	enhanceProductImage,
	// AI Webpage Template Generation
	generateWebpageTemplateWithAI,
	// AI Email Template Generation
	generateEmailTemplateWithAI,
	trackDataSchemaInteraction,
	suggestEmailTemplates,
	// AI Document Template Generation
	generateDocumentTemplateWithAI,
	// Academy Course Management
	createAcademyCourse,
	updateAcademyCourse,
	getAcademyCourse,
	publishAcademyCourse,
	unpublishAcademyCourse,
	updateAcademyModule,
	updateAcademyModuleContent,
	generateAIImage: async (prompt: string) => {
		return await noMonitor.post('/ai/canvas/generate-image', { prompt }).then(res => res.data);
	},
	getUserDatasForFlow: async (flowId: string) => {
		return await noMonitor.get(`/ai/flow/${flowId}/user-datas`).then(res => res.data);
	},
	getCalendarsForFlow: async (flowId: string) => {
		return await noMonitor.get(`/ai/flow/${flowId}/calendars`).then(res => res.data);
	},
	getCalendarSlots: async (flowId: string, calendarId: string, date: string, duration: number, buffer: number) => {
		return await noMonitor.get(`/ai/flow/${flowId}/calendar/${calendarId}/slots`, {
			params: { date, duration, buffer }
		}).then(res => res.data);
	},
	createFlowAppointment: async (flowId: string, sessionId: string, appointmentData: any) => {
		return await noMonitor.post(`/ai/flow/${flowId}/test/${sessionId}/appointment`, appointmentData).then(res => res.data);
	},
	// Flow CRUD Operations
	createFlow: async (flowData: any) => {
		return await noMonitor.post('/flows/create-flow', { flowData }).then(res => res.data);
	},
	updateFlow: async (flowId: string, flowData: any) => {
		return await noMonitor.put(`/flows/update-flow/${flowId}`, { flowData }).then(res => res.data);
	},
	startFlowOutbound: async (flowId: string) => {
		return await noMonitor.post(`/flows/${flowId}/outbound/start`).then(res => res.data);
	},
	pauseFlowOutbound: async (flowId: string) => {
		return await noMonitor.post(`/flows/${flowId}/outbound/pause`).then(res => res.data);
	},
	resumeFlowOutbound: async (flowId: string) => {
		return await noMonitor.post(`/flows/${flowId}/outbound/resume`).then(res => res.data);
	},
	getFlowOutboundStatus: async (flowId: string) => {
		return await noMonitor.get(`/flows/${flowId}/outbound/status`).then(res => res.data);
	},
	getOutboundCallQueue: async () => {
		return await noMonitor.get('/flows/outbound/queue').then(res => res.data);
	},
	completeOutboundAttempt: async (attemptId: string, payload: { status?: string; note?: string } = {}) => {
		return await noMonitor.post(`/flows/outbound/attempts/${attemptId}/complete`, payload).then(res => res.data);
	},
	dialFlowPstnTest: async (flowId: string, payload: { phone?: string; testRecord?: any } = {}) => {
		return await noMonitor.post(`/flows/${flowId}/outbound/pstn-dial`, payload).then(res => res.data);
	},
	dialOutboundAttemptPstn: async (attemptId: string) => {
		return await noMonitor.post(`/flows/outbound/attempts/${attemptId}/pstn-dial`).then(res => res.data);
	},
	getPstnProxyStatus: async () => {
		return await noMonitor.get('/telephony/pstn/status').then(res => res.data);
	},
	getFlowCallLogs: async (params: { direction?: string; status?: string; page?: string; limit?: string } = {}) => {
		const query = new URLSearchParams();
		if (params.direction) query.append('direction', params.direction);
		if (params.status) query.append('status', params.status);
		if (params.page) query.append('page', params.page);
		if (params.limit) query.append('limit', params.limit);
		const suffix = query.toString();
		return await noMonitor.get(`/flows/call-logs${suffix ? `?${suffix}` : ''}`).then(res => res.data);
	},
	// Workflow Management APIs
	getWorkflows: async (params?: { active?: boolean; category?: string; tags?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.active !== undefined) queryParams.append('active', String(params.active));
		if (params?.category) queryParams.append('category', params.category);
		if (params?.tags) queryParams.append('tags', params.tags);
		const query = queryParams.toString();
		return await api.get(`/workflows${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getWorkflow: async (workflowId: string) => {
		return await api.get(`/workflows/${workflowId}`).then(res => res.data);
	},
	createWorkflow: async (workflowData: any) => {
		return await api.post('/workflows', workflowData).then(res => res.data);
	},
	updateWorkflow: async (workflowId: string, workflowData: any) => {
		return await api.put(`/workflows/${workflowId}`, workflowData).then(res => res.data);
	},
	deleteWorkflow: async (workflowId: string) => {
		return await api.delete(`/workflows/${workflowId}`).then(res => res.data);
	},
	executeWorkflow: async (workflowId: string, triggerData?: any) => {
		return await api.post(`/workflows/${workflowId}/execute`, { triggerData }).then(res => res.data);
	},
	getWorkflowExecutions: async (workflowId: string, params?: { limit?: number; status?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.status) queryParams.append('status', params.status);
		const query = queryParams.toString();
		return await api.get(`/workflows/${workflowId}/executions${query ? `?${query}` : ''}`).then(res => res.data);
	},
	retryWorkflowExecution: async (workflowId: string, executionId: string) => {
		return await api.post(`/workflows/${workflowId}/retry/${executionId}`).then(res => res.data);
	},
	scheduleWorkflow: async (workflowId: string, cronExpression: string, timezone?: string) => {
		return await api.post(`/workflows/${workflowId}/schedule`, { cronExpression, timezone }).then(res => res.data);
	},
	unscheduleWorkflow: async (workflowId: string) => {
		return await api.delete(`/workflows/${workflowId}/schedule`).then(res => res.data);
	},
	getWorkflowWebhooks: async (workflowId: string) => {
		return await api.get(`/workflows/${workflowId}/webhooks`).then(res => res.data);
	},
	// Agentic Flow APIs
	createAgenticFlow: async (dataId: string, flowConfig: any) => {
		return await api.post('/agentic-flow/create', { dataId, flowConfig }).then(res => res.data);
	},
	getAgenticFlows: async (dataId: string) => {
		return await api.get(`/agentic-flow/${dataId}`).then(res => res.data);
	},
	getAgenticFlow: async (dataId: string, flowId: string) => {
		return await api.get(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
	},
	updateAgenticFlow: async (dataId: string, flowId: string, flowConfig: any) => {
		return await api.put(`/agentic-flow/${dataId}/${flowId}`, { flowConfig }).then(res => res.data);
	},
	deleteAgenticFlow: async (dataId: string, flowId: string) => {
		return await api.delete(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
	},
	triggerAgenticFlow: async (dataId: string, flowId: string, inputData?: any, context?: any) => {
		return await api.post(`/agentic-flow/${dataId}/${flowId}/trigger`, { inputData, context }).then(res => res.data);
	},
	testAgenticFlow,
	executeAgenticFlow: async (flowId: string, inputData?: any, context?: any) => {
		const mergedContext = {
			...(context || {}),
			telemetryHints: {
				...((context || {}).telemetryHints || {}),
				...buildAgenticFlowTelemetry()
			}
		};
		return await api.post('/agentic-flow/execute', { flowId, inputData, context: mergedContext }).then(res => res.data);
	},
	validateAgenticFlow: async (flowConfig: any) => {
		return await api.post('/agentic-flow/validate', { flowConfig }).then(res => res.data);
	},
	getAgenticFlowStatus: async (executionId: string) => {
		return await api.get(`/agentic-flow/status/${executionId}`).then(res => res.data);
	},
	// Flow History APIs
	getFlowHistory: async (dataId: string, params?: { limit?: number; offset?: number; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.offset) queryParams.append('offset', String(params.offset));
		if (params?.status) queryParams.append('status', params.status);
		if (params?.actionType) queryParams.append('actionType', params.actionType);
		if (params?.triggerType) queryParams.append('triggerType', params.triggerType);
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getActionHistory: async (actionId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.offset) queryParams.append('offset', String(params.offset));
		if (params?.status) queryParams.append('status', params.status);
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/action/${actionId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getWorkflowHistory: async (workflowId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.offset) queryParams.append('offset', String(params.offset));
		if (params?.status) queryParams.append('status', params.status);
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/workflow/${workflowId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getFlowAnalytics: async (dataId: string, params?: { startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/analytics/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getExecutionDetails: async (executionId: string) => {
		return await api.get(`/flow-history/execution/${executionId}`).then(res => res.data);
	},
	filterExecutions: async (filters: { dataId?: string; actionId?: string; workflowId?: string; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) => {
		return await api.post('/flow-history/filter', filters).then(res => res.data);
	},
	// Workflow AI APIs
	generateWorkflowFromNL: async (description: string, context?: any, availableIntegrations?: string[]) => {
		return await api.post('/workflows/ai/generate', { description, context, availableIntegrations }).then(res => res.data);
	},
	optimizeWorkflow: async (workflow: any) => {
		return await api.post('/workflows/ai/optimize', { workflow }).then(res => res.data);
	},
	blockCalendarDates: async (payload: {
		calendarId: string;
		startDate: string;
		endDate: string;
		reason?: string;
		notes?: string;
	}) => {
		if (!payload?.calendarId || !payload?.startDate || !payload?.endDate) {
			return dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: 'Calendar, start date and end date are required to block dates',
					type: 'error'
				}
			});
		}

		return await monitoredRequest
			.post('/calendar/block-dates', payload)
			.then(res => res.data)
			.catch(error => {
				const errorMessage = error?.response?.data?.error || 'Could not block dates';
				dispatchErrorModal(errorMessage, error?.response?.data);
				throw error;
			});
	},
	createMultiDayEvent: async (payload: {
		calendarId: string;
		title?: string;
		startDate: string;
		endDate: string;
		allDay?: boolean;
		description?: string;
		location?: string;
		attendees?: any[];
		availability?: string;
		notes?: string;
		color?: string;
	}) => {
		if (!payload?.calendarId || !payload?.startDate || !payload?.endDate) {
			return dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: 'Please select a calendar and provide both start and end dates',
					type: 'error'
				}
			});
		}

		return await monitoredRequest
			.post('/calendar/create-multi-day-event', payload)
			.then(res => res.data)
			.catch(error => {
				const errorMessage = error?.response?.data?.error || 'Could not create multi-day event';
				dispatchErrorModal(errorMessage, error?.response?.data);
				throw error;
			});
	},
	discoverNearbyEvents: async (payload?: { location?: string; industry?: string }) => {
		return await monitoredRequest
			.post('/calendar/discover-nearby-events', payload || {}, { timeout: 180000 })
			.then(res => res.data)
			.catch(error => {
				const errorMessage = error?.response?.data?.error || 'Could not discover nearby events';
				dispatchErrorModal(errorMessage, error?.response?.data);
				throw error;
			});
	},
	importDiscoveredEvents: async (payload: {
		calendarId: string;
		events: Array<{
			title: string;
			description?: string | null;
			startDate?: string | null;
			endDate?: string | null;
			location?: string | null;
			registerUrl?: string | null;
			source?: string | null;
			allDay?: boolean;
		}>;
	}) => {
		return await monitoredRequest
			.post('/calendar/import-discovered-events', payload)
			.then(res => res.data)
			.catch(error => {
				const errorMessage = error?.response?.data?.error || 'Could not import discovered events';
				dispatchErrorModal(errorMessage, error?.response?.data);
				throw error;
			});
	},
	// Document Folder APIs
	createDocumentFolder,
	getDocumentFolders,
	deleteDocumentFolder,
	moveDocumentToFolder,
	// User Document APIs
	uploadUserDocument,
	getUserUploadedDocuments,
	getDocumentContent,
	deleteUserDocument,
	// Context Window APIs
	summarizeContext,

	getContextStats: async () => {
		return await api.get('/ai/context/stats', { timeout: 120000 })
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get context stats:', error);
				throw error;
			});
	},
	updateContextSettings: async (settings: any) => {
		return await api.post('/ai/context/settings', settings, { timeout: 120000 })
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to update context settings:', error);
				throw error;
			});
	},
	// Hugging Face Integration APIs
	exportDataModelToHF,
	getHuggingFaceProfile,
	storeHuggingFaceToken,
	removeHuggingFaceToken,
	getHuggingFaceModels,
	updateCalendarQuickSettings,
	initiateHuggingFaceTraining,
	getHuggingFaceTrainingHistory,
	getHuggingFaceTrainingStatus,
	refreshMailToken,
	// PayStack Integration APIs
	getPayStackProfile,
	storePayStackKeys,
	removePayStackKeys,
	generatePayStackPaymentLink,
	getHRDashboard,
	getCompanyMemberAccess,
	updateCompanyMemberAccess,
	getEmployees,
	getLeaveRequests,
	getAttendance,
	getPayroll,
	getPerformanceReviews,
	syncPayrollToAccounting,
	getAccountingIntegrations,
	connectAccountingSoftware,
	disconnectAccountingSoftware,
	// Spec Registry APIs
	getEnrichedSpec: async (integrationId: string, version?: string) => {
		const url = version
			? `/spec-registry/${integrationId}?version=${version}`
			: `/spec-registry/${integrationId}`;

		return await api.get(url)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get enriched spec:', error);
				throw error;
			});
	},
	getSpecDiff: async (integrationId: string, fromVersion: string, toVersion: string) => {
		return await api.get(
			`/spec-registry/${integrationId}/diff?from=${fromVersion}&to=${toVersion}`
		)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get spec diff:', error);
				throw error;
			});
	},
	getIntegrationEndpointData: async (
		integrationId: string,
		endpointPath: string,
		method: string = 'GET',
		body?: any,
		options: { confirmRisk?: string; query?: Record<string, string> } = {}
	) => {
		return await api.request({
			method,
			url: `/integration-runtime/${integrationId}/proxy${endpointPath}`,
			params: {
				...(options.query || {}),
				...(options.confirmRisk ? { _confirmRisk: options.confirmRisk } : {}),
			},
			data: body
		})
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to fetch integration endpoint data:', error);
				throw error;
			});
	},
	setSpecOperationsEnabled: async (integrationId: string, keys: string[], enabled: boolean) => {
		const res = await api.put(`/spec-registry/${integrationId}/operations`, { keys, enabled });
		return res.data;
	},
	getIntegrationSecrets: async (integrationId: string) => {
		const res = await api.get(`/integration-admin/${integrationId}/secrets`);
		return res.data;
	},
	setIntegrationSecret: async (integrationId: string, name: string, value: string) => {
		const res = await api.put(`/integration-admin/${integrationId}/secrets/${encodeURIComponent(name)}`, { value });
		return res.data;
	},
	deleteIntegrationSecret: async (integrationId: string, name: string) => {
		const res = await api.delete(`/integration-admin/${integrationId}/secrets/${encodeURIComponent(name)}`);
		return res.data;
	},
	getIntegrationCredentials: async (integrationId: string) => {
		const res = await api.get(`/integration-runtime/${integrationId}/credentials`);
		return res.data;
	},
	setIntegrationCredentials: async (integrationId: string, values: Record<string, string>) => {
		const res = await api.put(`/integration-runtime/${integrationId}/credentials`, { values });
		return res.data;
	},
	deleteIntegrationCredentials: async (integrationId: string) => {
		const res = await api.delete(`/integration-runtime/${integrationId}/credentials`);
		return res.data;
	},
	getSpecVersions: async (integrationId: string) => {
		return await api.get(`/spec-registry/${integrationId}/versions`)
			.then(res => res.data.versions)
			.catch(error => {
				console.error('Failed to get spec versions:', error);
				throw error;
			});
	},
	saveDismissedSavePrompts,
	getDismissedSavePrompts,
	// Notification Log APIs
	getUserNotificationLogs: async (limit: number = 100) => {
		return await api.get(`/notification-logs/user?limit=${Math.min(limit, 500)}`)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get user notification logs:', error);
				throw error;
			});
	},
	getCompanyNotificationLogs: async (limit: number = 5000) => {
		return await api.get(`/notification-logs/company?limit=${Math.min(limit, 5000)}`)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get company notification logs:', error);
				throw error;
			});
	},
	// Dashboard Management APIs
	suggestDashboardComponents,
	createDashboard,
	getUserDashboards,
	getDashboard,
	updateDashboard,
	deleteDashboard,
	// Document Creation APIs
	createDocumentLayout,
	generateDocumentContent,
	refineDocumentDesign,
	validateDocument,
	getBooking,
	getBookings,
	deleteBooking,
	updateBooking,
	cancelBooking,
	createDocumentBackground,
	// Generic HTTP methods for dynamic API calls
	post: async (url: string, data?: any, config?: any) => {
		return await noMonitor.post(url, data, config).then(res => res.data);
	},
	postMonitored: async (url: string, data?: any, config?: any) => {
		return await postMonitored(url, data, config);
	},
	put: async (url: string, data?: any, config?: any) => {
		return await noMonitor.put(url, data, config).then(res => res.data);
	},
	putMonitored: async (url: string, data?: any, config?: any) => {
		return await putMonitored(url, data, config);
	},
	get: async (url: string, config?: any) => {
		return await noMonitor.get(url, config).then(res => res.data);
	},
	getEntitlements: async (userId: string) => {
		return await noMonitor.get(`/entitlements/${userId}`).then(res => res.data);
	},
}

export { clearModalTimeout };
export default apisObject;
